import { useState, useEffect, useRef, useCallback, ReactNode } from 'react'
import {
  PanelRightClose,
  PanelRightOpen,
  Globe,
  Smartphone,
  ScanEye,
  Rotate3d,
  PackageCheck,
  ChevronLeft,
  StepBack,
  StepForward,
  Sun,
  Moon,
  Monitor,
  MonitorSmartphone,
  Dices,
  RotateCcw,
  // Nesting category icons
  ToyBrick,
  Component,
  Layers,
  AppWindow,
  PanelBottomOpen,
  StickyNote,
  Copy,
  ListTree,
} from 'lucide-react'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../ui/popover'
import { Label } from '../../ui/label'
import Slider from '../../ui/slider'
import type { ShowcaseSettings, ForcedViewportMode, FilteredComponent, Restaurant, Theme } from '../../../pages/ComponentShowcase'
import { ICON_STYLE_LABELS } from '../../../pages/ComponentShowcase'
import { getUnifiedOrderedComponents } from './LandingComponentsSection'
import { loadStatusData, saveStatusData, type ReviewStatusData } from '../utils/reviewStatusStorage'

// Import components from core
import {
  // Atomic components
  ButtonCategory,
  ButtonSearch,
  Toolbar,
  ButtonLarge,
  ButtonMedium,
  ButtonSmall,
  ButtonCheckout,
  Tab,
  // Molecular components
  ButtonLocation,
  ButtonOrder,
  ButtonIconLarge,
  ButtonIconSmall,
  ButtonFavorite,
  ButtonQuickFavorites,
  ButtonQuickReorder,
  QuickButtonsContainerWeb as QuickButtonsContainer,
  ButtonAddProduct,
  ButtonTip,
  ButtonNotificationWeb as ButtonNotification,
  ButtonProduct,
  ButtonProductReorder,
  ToggleOrderType,
  TileProduct,
  TileRewards,
  TilePage,
  TileOffer,
  TileOrderHistory,
  LocationOrderType,
  ProductCount,
  TextField,
  TextFieldSearch,
  AddressSearchResult,
  RadioButton,
  Checkbox,
  TextFieldLabel,
  Badge,
  // Image components
  ImageProductButton,
  ImageProductTile,
  ImageReward,
  ImageProductSheet,
  ImageOffer,
  ImageBanner,
  ImageBrandLogo,
  // Cellular components
  MenuCategory,
  NavigationDesktop,
  NavigationMobile,
  HeaderMenu,
  HeaderCart,
  HeaderRewards,
  HeaderOther,
  TabsContainer,
  FooterDesktop,
  AccountPageNavigation,
  HelpPageNavigation,
  ProductChoices,
  OfferGroup,
  SectionOrderHistory,
  OrderHistorySheet,
  RewardPointsGroup,
  ReorderProductsList,
  SectionFavoritesList,
  SectionReorder,
  LocationList,
  // Misc components
  Divider,
  Toast,
  DiscountBanner,
  DiscountBannerContainer,
  RequiredIndicator,
  GaugeCircular,
  GaugeLinear,
  MapIcon,
  OrderStatusIcon,
  OrderStatusProgressBar,
  TextOrderStatus,
  // Molecular components - Input
  TextBlock,
  TextFieldCreditCard,
  OptionPaymentChoice,
  // Phase 1 components - Input
  OptionRow,
  FieldGroupCardInformation,
  FieldGroupAddressDetails,
  FieldGroupAccountInformation,
  DeliveryPreference,
  // Phase 1 components - Buttons
  CustomTipOption,
  TipPresets,
  NotificationTypes,
  // Phase 1 components - Misc
  Caret,
  OrderLineItem,
  PreviousCartItem,
  SelectedOrder,
  PointsNumberOnly,
  // Overlays
  SheetGrabber,
  SheetCloseButton,
  // Sections
  SectionAddToOrder,
  SectionAddressResults,
  SectionAppIssueForm,
  SectionCheckoutDriverTip,
  SectionCheckoutPaymentMethod,
  SectionFeedbackForm,
  SectionNotificationType,
  SectionOfferDetails,
  SectionOptionGroup,
  SectionOrderIssueForm,
  SectionOrderSummary,
  SectionPreviousOrderSummary,
  SectionProductInformation,
  SectionProductsInCart,
  SectionRequiredProduct,
  SectionSpecialRequests,
  SectionSubtotal,
  ICON_STYLE_IDS,
} from '@restaurant/core'
// `OrderStatus`/`CartItem` are exposed as data TYPES from the top-level
// `@restaurant/core` barrel (they shadow the same-named components). Import the
// component values from their component subpaths instead.
import { OrderStatus } from '@restaurant/core/components/OrderStatus'
import { CartItem } from '@restaurant/core/components/CartItem'
import type { ButtonCheckoutState, NotificationType, IconStyleId } from '@restaurant/core'

// Status types (shared with LandingComponentsSection)
type ReviewStatus = 'default' | 'complete'
type ReviewCategory = 'webTokenCleanup' | 'nativeTokenCleanup' | 'accessibility' | 'animation'

// Status cycle order
const statusCycle: ReviewStatus[] = ['default', 'complete']

// Status colors for category icons (used for single-icon status display)
const statusColors: Record<ReviewStatus, string> = {
  default: '#a1a1a1',   // neutral-400
  complete: '#00bc7d'   // emerald-500
}

// Category icon components (for dynamic coloring based on status)
const categoryIconComponents: Record<ReviewCategory, React.FC<{ className?: string; style?: React.CSSProperties }>> = {
  webTokenCleanup: Globe,
  nativeTokenCleanup: Smartphone,
  accessibility: ScanEye,
  animation: Rotate3d,
}

// Tooltips for each category
const categoryTooltips: Record<ReviewCategory, string> = {
  webTokenCleanup: 'Web Token Cleanup',
  nativeTokenCleanup: 'Native Token Cleanup',
  accessibility: 'Accessibility',
  animation: 'Animation',
}

// Categories in order
const categories: ReviewCategory[] = ['webTokenCleanup', 'nativeTokenCleanup', 'accessibility', 'animation']

// Nesting category types for the Nesting tab
type NestingCategory = 'Blocks' | 'Components' | 'Containers' | 'Chrome' | 'Sheets' | 'Pages' | 'Alerts' | 'Nested'

// Nesting category icons mapping
const nestingCategoryIcons: Record<NestingCategory, React.FC<{ className?: string; style?: React.CSSProperties }>> = {
  'Blocks': ToyBrick,
  'Components': Component,
  'Containers': Layers,
  'Chrome': AppWindow,
  'Sheets': PanelBottomOpen,
  'Pages': StickyNote,
  'Alerts': Copy,
  'Nested': ListTree,
}

// Non-nested categories (all except Nested)
const nonNestedCategories: NestingCategory[] = ['Blocks', 'Components', 'Containers', 'Chrome', 'Sheets', 'Pages', 'Alerts']

// Nesting link interface (a reference to another component)
interface NestingLink {
  componentId: string
  label: string
}

// Nesting data for a component: which categories it appears in
// The key is the category, value is array of component references
interface ComponentNestingData {
  usedIn?: Partial<Record<Exclude<NestingCategory, 'Nested'>, NestingLink[]>>
  // contains is for the "Nested" category - components directly contained within this component
  contains?: NestingLink[]
}

// Manual nesting data: defines which components contain which other components
// Key is the container component ID, value is array of contained component IDs
const componentContainsData: Record<string, string[]> = {
  // Chrome-level parents
  'toolbar': ['button-category', 'button-search'],
  'navigation-desktop': ['tabs-container', 'button-checkout', 'toast', 'image-brand-logo'],
  'navigation-mobile': ['tabs-container', 'button-checkout', 'toast'],
  'header-menu': ['image-banner', 'location-order-type'],
  'header-cart': ['location-order-type'],
  'header-other': ['button-icon-large'],
  'header-rewards': ['points-number-only'],
  'footer-desktop': ['location-order-type'],
  'location-order-type': ['toggle-order-type', 'button-small'],
  'alert': ['button-large'],
  'alert-add-card': ['field-group-card-information', 'option-row', 'button-large'],
  'sheet': ['sheet-grabber', 'sheet-close-button'],

  // Container-level parents (non-Section)
  'tabs-container': ['tab'],
  'menu-category': ['tile-product'],
  'offer-group': ['tile-offer'],
  'order-history': ['tile-order-history'],
  'order-history-sheet': ['button-order'],
  'reward-points-group': ['tile-rewards'],
  'reorder-products-list': ['button-product-reorder'],
  'favorites-list': ['reorder-products-list'],
  'section-reorder': ['reorder-products-list', 'button-large'],
  'location-list': ['button-location'],
  'account-page-navigation': ['tile-page'],
  'help-page-navigation': ['tile-page'],
  'quick-buttons-container': ['button-quick-favorites', 'button-quick-reorder'],
  'product-choices': ['button-product'],
  'tip-presets': ['button-tip'],
  'notification-types': ['button-notification'],
  'field-group-card-information': ['text-field', 'text-field-credit-card'],
  'field-group-address-details': ['text-field', 'delivery-preference', 'text-block'],
  'field-group-account-information': ['text-field', 'button-large'],
  'delivery-preference': ['text-field-label', 'option-row'],

  // Section-level parents
  'section-add-to-order': ['product-count', 'button-favorite', 'button-add-product'],
  'section-address-results': ['address-search-result'],
  'section-app-issue-form': ['text-block', 'button-large'],
  'section-checkout-driver-tip': ['tip-presets', 'custom-tip-option'],
  'section-checkout-payment-method': ['option-payment-choice', 'button-small'],
  'section-feedback-form': ['text-block', 'button-large'],
  'section-notification-type': ['notification-types'],
  'section-offer-details': ['button-large'],
  'section-order-issue-form': ['text-block', 'button-large'],
  'section-order-summary': ['order-line-item', 'button-large'],
  'section-previous-order-summary': ['previous-cart-item', 'order-line-item', 'button-large', 'divider'],
  'section-product-information': ['image-product-sheet'],
  'section-products-in-cart': ['cart-item'],
  'section-required-product': ['required-indicator', 'button-small', 'button-icon-large'],
  'section-option-group': ['option-row'],
  'section-special-requests': ['text-block'],
  'section-subtotal': ['button-large'],

  // Component-level parents
  'sheet-close-button': ['button-small'],
  'custom-tip-option': ['button-small', 'button-icon-small', 'text-field'],
  'tile-rewards': ['button-large', 'image-reward'],
  'tile-product': ['image-product-tile'],
  'tile-offer': ['button-large'],
  'tile-order-history': ['button-medium'],
  'button-product': ['image-product-button'],
  'button-product-reorder': ['image-product-button'],
  'option-payment-choice': ['option-row', 'button-icon-small'],
  'text-field-credit-card': ['text-field'],
  'button-location': ['caret', 'badge'],
  'button-order': ['caret'],
  'address-search-result': ['caret', 'badge'],
  'toast': ['caret'],
  'discount-banner': ['caret'],
  'discount-banner-container': ['discount-banner'],
  'option-row': ['checkbox', 'radio-button', 'text-field-label'],
  'cart-item': ['product-count'],
  'product-count': ['button-icon-small'],
  'order-status': ['order-status-icon', 'order-status-progress-bar', 'text-order-status'],
  'text-field': ['text-field-label'],
  'text-block': ['text-field-label'],
}

// Helper to get component label from componentConfigs (or fall back to ID)
function getComponentLabel(componentId: string): string {
  // Map of component IDs to display labels
  const componentLabels: Record<string, string> = {
    'button-category': 'ButtonCategory',
    'button-search': 'ButtonSearch',
    'toolbar': 'Toolbar',
    'button-small': 'ButtonSmall',
    'button-large': 'ButtonLarge',
    'button-medium': 'ButtonMedium',
    'button-checkout': 'ButtonCheckout',
    'button-location': 'ButtonLocation',
    'button-order': 'ButtonOrder',
    'button-icon-large': 'ButtonIconLarge',
    'button-icon-small': 'ButtonIconSmall',
    'button-favorite': 'ButtonFavorite',
    'button-quick-favorites': 'ButtonQuickFavorites',
    'button-quick-reorder': 'ButtonQuickReorder',
    'button-add-product': 'ButtonAddProduct',
    'button-tip': 'ButtonTip',
    'button-notification': 'ButtonNotification',
    'button-product': 'ButtonProduct',
    'button-product-reorder': 'ButtonProductReorder',
    'toggle-order-type': 'ToggleOrderType',
    'tile-rewards': 'TileRewards',
    'tile-product': 'TileProduct',
    'tile-page': 'TilePage',
    'tile-offer': 'TileOffer',
    'tile-order-history': 'TileOrderHistory',
    'product-count': 'ProductCount',
    'text-field': 'TextField',
    'radio-button': 'RadioButton',
    'checkbox': 'Checkbox',
    'text-field-label': 'TextFieldLabel',
    'text-field-search': 'TextFieldSearch',
    'address-search-result': 'AddressSearchResult',
    'badge': 'Badge',
    'menu-category': 'MenuCategory',
    'navigation-desktop': 'NavigationDesktop',
    'header-menu': 'HeaderMenu',
    'header-rewards': 'HeaderRewards',
    'header-cart': 'HeaderCart',
    'header-other': 'HeaderOther',
    'tab': 'Tab',
    'tabs-container': 'TabsContainer',
    'navigation-mobile': 'NavigationMobile',
    'footer-desktop': 'FooterDesktop',
    'image-product-button': 'ImageProductButton',
    'image-product-tile': 'ImageProductTile',
    'image-reward': 'ImageReward',
    'image-product-sheet': 'ImageProductSheet',
    'image-offer': 'ImageOffer',
    'image-banner': 'ImageBanner',
    'image-brand-logo': 'ImageBrandLogo',
    'divider': 'Divider',
    'toast': 'Toast',
    'discount-banner': 'DiscountBanner',
    'discount-banner-container': 'DiscountBannerContainer',
    'required-indicator': 'RequiredIndicator',
    'gauge-circular': 'GaugeCircular',
    'gauge-linear': 'GaugeLinear',
    'order-status': 'OrderStatus',
    'map-icon': 'MapIcon',
    'order-status-icon': 'OrderStatusIcon',
    'order-status-progress-bar': 'OrderStatusProgressBar',
    'text-order-status': 'TextOrderStatus',
    'location-order-type': 'LocationOrderType',
    'account-page-navigation': 'AccountPageNavigation',
    'help-page-navigation': 'HelpPageNavigation',
    'quick-buttons-container': 'QuickButtonsContainer',
    'product-choices': 'ProductChoices',
    'offer-group': 'OfferGroup',
    'order-history': 'SectionOrderHistory',
    'order-history-sheet': 'OrderHistorySheet',
    'reward-points-group': 'RewardPointsGroup',
    'reorder-products-list': 'ReorderProductsList',
    'favorites-list': 'SectionFavoritesList',
    'section-reorder': 'SectionReorder',
    'location-list': 'LocationList',
    'text-block': 'TextBlock',
    'text-field-credit-card': 'TextFieldCreditCard',
    'option-payment-choice': 'OptionPaymentChoice',
    'alert': 'Alert',
    'alert-add-card': 'AlertAddCard',
    'sheet': 'Sheet',
    'sheet-grabber': 'SheetGrabber',
    'sheet-close-button': 'SheetCloseButton',
    // Phase 1 components - Input
    'option-row': 'OptionRow',
    'field-group-card-information': 'FieldGroupCardInformation',
    'field-group-address-details': 'FieldGroupAddressDetails',
    'field-group-account-information': 'FieldGroupAccountInformation',
    'delivery-preference': 'DeliveryPreference',
    // Phase 1 components - Buttons
    'custom-tip-option': 'CustomTipOption',
    'tip-presets': 'TipPresets',
    'notification-types': 'NotificationTypes',
    // Phase 1 components - Misc
    'caret': 'Caret',
    'order-line-item': 'OrderLineItem',
    'cart-item': 'CartItem',
    'selected-order': 'SelectedOrder',
    'points-number-only': 'PointsNumberOnly',
    // Sections
    'section-add-to-order': 'SectionAddToOrder',
    'section-address-results': 'SectionAddressResults',
    'section-app-issue-form': 'SectionAppIssueForm',
    'section-checkout-driver-tip': 'SectionCheckoutDriverTip',
    'section-checkout-payment-method': 'SectionCheckoutPaymentMethod',
    'section-feedback-form': 'SectionFeedbackForm',
    'section-notification-type': 'SectionNotificationType',
    'section-offer-details': 'SectionOfferDetails',
    'section-option-group': 'SectionOptionGroup',
    'section-order-issue-form': 'SectionOrderIssueForm',
    'section-order-summary': 'SectionOrderSummary',
    'section-previous-order-summary': 'SectionPreviousOrderSummary',
    'section-product-information': 'SectionProductInformation',
    'section-products-in-cart': 'SectionProductsInCart',
    'section-required-product': 'SectionRequiredProduct',
    'section-special-requests': 'SectionSpecialRequests',
    'section-subtotal': 'SectionSubtotal',
  }
  return componentLabels[componentId] || componentId
}

// Manual nesting data: defines the "usedIn" categories for each component
// This is the data you add manually to specify where components are used
const manualNestingData: Record<string, ComponentNestingData['usedIn']> = {
  // --- Children used in Chrome-level parents ---
  'button-category': {
    'Chrome': [
      { componentId: 'toolbar', label: 'Toolbar' },
    ],
  },
  'button-search': {
    'Chrome': [
      { componentId: 'toolbar', label: 'Toolbar' },
    ],
  },
  'tabs-container': {
    'Chrome': [
      { componentId: 'navigation-mobile', label: 'NavigationMobile' },
      { componentId: 'navigation-desktop', label: 'NavigationDesktop' },
    ],
  },
  'button-checkout': {
    'Chrome': [
      { componentId: 'navigation-desktop', label: 'NavigationDesktop' },
      { componentId: 'navigation-mobile', label: 'NavigationMobile' },
    ],
  },
  'toast': {
    'Chrome': [
      { componentId: 'navigation-desktop', label: 'NavigationDesktop' },
      { componentId: 'navigation-mobile', label: 'NavigationMobile' },
    ],
  },
  'discount-banner': {
    'Chrome': [
      { componentId: 'navigation-desktop', label: 'NavigationDesktop' },
      { componentId: 'navigation-mobile', label: 'NavigationMobile' },
    ],
  },
  'image-brand-logo': {
    'Chrome': [
      { componentId: 'navigation-desktop', label: 'NavigationDesktop' },
    ],
  },
  'image-banner': {
    'Chrome': [
      { componentId: 'header-menu', label: 'HeaderMenu' },
    ],
  },
  'location-order-type': {
    'Chrome': [
      { componentId: 'header-cart', label: 'HeaderCart' },
      { componentId: 'header-menu', label: 'HeaderMenu' },
      { componentId: 'footer-desktop', label: 'FooterDesktop' },
    ],
  },
  'toggle-order-type': {
    'Chrome': [
      { componentId: 'location-order-type', label: 'LocationOrderType' },
    ],
  },

  // --- Children used in Alerts ---
  'button-large': {
    'Alerts': [
      { componentId: 'alert', label: 'Alert' },
      { componentId: 'alert-add-card', label: 'AlertAddCard' },
    ],
    'Containers': [
      { componentId: 'field-group-account-information', label: 'FieldGroupAccountInformation' },
      { componentId: 'section-reorder', label: 'SectionReorder' },
      { componentId: 'section-app-issue-form', label: 'SectionAppIssueForm' },
      { componentId: 'section-feedback-form', label: 'SectionFeedbackForm' },
      { componentId: 'section-offer-details', label: 'SectionOfferDetails' },
      { componentId: 'section-order-issue-form', label: 'SectionOrderIssueForm' },
      { componentId: 'section-order-summary', label: 'SectionOrderSummary' },
      { componentId: 'section-previous-order-summary', label: 'SectionPreviousOrderSummary' },
      { componentId: 'section-subtotal', label: 'SectionSubtotal' },
    ],
    'Components': [
      { componentId: 'tile-rewards', label: 'TileRewards' },
      { componentId: 'tile-offer', label: 'TileOffer' },
    ],
  },

  // --- Children used in Sheets ---
  'sheet-grabber': {
    'Sheets': [{ componentId: 'sheet', label: 'Sheet' }],
  },
  'sheet-close-button': {
    'Sheets': [{ componentId: 'sheet', label: 'Sheet' }],
  },

  // --- Children used in Chrome + Containers (multi-category) ---
  'button-small': {
    'Chrome': [
      { componentId: 'location-order-type', label: 'LocationOrderType' },
    ],
    'Containers': [
      { componentId: 'section-checkout-payment-method', label: 'SectionCheckoutPaymentMethod' },
      { componentId: 'section-required-product', label: 'SectionRequiredProduct' },
    ],
    'Components': [
      { componentId: 'custom-tip-option', label: 'CustomTipOption' },
      { componentId: 'sheet-close-button', label: 'SheetCloseButton' },
    ],
  },
  'button-icon-large': {
    'Chrome': [
      { componentId: 'header-other', label: 'HeaderOther' },
    ],
    'Containers': [
      { componentId: 'section-required-product', label: 'SectionRequiredProduct' },
    ],
  },
  'points-number-only': {
    'Chrome': [
      { componentId: 'header-rewards', label: 'HeaderRewards' },
    ],
  },

  // --- Children used in Containers ---
  'tab': {
    'Containers': [{ componentId: 'tabs-container', label: 'TabsContainer' }],
  },
  'tile-product': {
    'Containers': [{ componentId: 'menu-category', label: 'MenuCategory' }],
  },
  'tile-offer': {
    'Containers': [{ componentId: 'offer-group', label: 'OfferGroup' }],
  },
  'tile-order-history': {
    'Containers': [{ componentId: 'order-history', label: 'SectionOrderHistory' }],
  },
  'button-order': {
    'Containers': [{ componentId: 'order-history-sheet', label: 'OrderHistorySheet' }],
  },
  'tile-rewards': {
    'Containers': [{ componentId: 'reward-points-group', label: 'RewardPointsGroup' }],
  },
  'button-location': {
    'Containers': [{ componentId: 'location-list', label: 'LocationList' }],
  },
  'tile-page': {
    'Containers': [
      { componentId: 'account-page-navigation', label: 'AccountPageNavigation' },
      { componentId: 'help-page-navigation', label: 'HelpPageNavigation' },
    ],
  },
  'button-quick-favorites': {
    'Containers': [{ componentId: 'quick-buttons-container', label: 'QuickButtonsContainer' }],
  },
  'button-quick-reorder': {
    'Containers': [{ componentId: 'quick-buttons-container', label: 'QuickButtonsContainer' }],
  },
  'button-product': {
    'Containers': [{ componentId: 'product-choices', label: 'ProductChoices' }],
  },
  'button-tip': {
    'Containers': [{ componentId: 'tip-presets', label: 'TipPresets' }],
  },
  'button-notification': {
    'Containers': [{ componentId: 'notification-types', label: 'NotificationTypes' }],
  },
  'text-field': {
    'Containers': [
      { componentId: 'field-group-card-information', label: 'FieldGroupCardInformation' },
      { componentId: 'field-group-address-details', label: 'FieldGroupAddressDetails' },
      { componentId: 'field-group-account-information', label: 'FieldGroupAccountInformation' },
    ],
    'Components': [
      { componentId: 'custom-tip-option', label: 'CustomTipOption' },
      { componentId: 'text-field-credit-card', label: 'TextFieldCreditCard' },
    ],
  },
  'text-field-credit-card': {
    'Containers': [{ componentId: 'field-group-card-information', label: 'FieldGroupCardInformation' }],
  },
  'delivery-preference': {
    'Containers': [{ componentId: 'field-group-address-details', label: 'FieldGroupAddressDetails' }],
  },
  'option-row': {
    'Alerts': [
      { componentId: 'alert-add-card', label: 'AlertAddCard' },
    ],
    'Containers': [
      { componentId: 'delivery-preference', label: 'DeliveryPreference' },
      { componentId: 'section-option-group', label: 'SectionOptionGroup' },
    ],
    'Blocks': [
      { componentId: 'option-payment-choice', label: 'OptionPaymentChoice' },
    ],
  },
  'field-group-card-information': {
    'Alerts': [
      { componentId: 'alert-add-card', label: 'AlertAddCard' },
    ],
  },
  'text-block': {
    'Containers': [
      { componentId: 'field-group-address-details', label: 'FieldGroupAddressDetails' },
      { componentId: 'section-app-issue-form', label: 'SectionAppIssueForm' },
      { componentId: 'section-feedback-form', label: 'SectionFeedbackForm' },
      { componentId: 'section-order-issue-form', label: 'SectionOrderIssueForm' },
      { componentId: 'section-special-requests', label: 'SectionSpecialRequests' },
    ],
  },
  'product-count': {
    'Containers': [
      { componentId: 'section-add-to-order', label: 'SectionAddToOrder' },
    ],
    'Components': [
      { componentId: 'cart-item', label: 'CartItem' },
    ],
  },
  'button-favorite': {
    'Containers': [
      { componentId: 'section-add-to-order', label: 'SectionAddToOrder' },
    ],
  },
  'button-add-product': {
    'Containers': [
      { componentId: 'section-add-to-order', label: 'SectionAddToOrder' },
    ],
  },
  'address-search-result': {
    'Containers': [
      { componentId: 'section-address-results', label: 'SectionAddressResults' },
    ],
  },
  'button-icon-small': {
    'Blocks': [
      { componentId: 'option-payment-choice', label: 'OptionPaymentChoice' },
    ],
    'Components': [
      { componentId: 'custom-tip-option', label: 'CustomTipOption' },
      { componentId: 'product-count', label: 'ProductCount' },
    ],
  },
  'tip-presets': {
    'Containers': [
      { componentId: 'section-checkout-driver-tip', label: 'SectionCheckoutDriverTip' },
    ],
  },
  'custom-tip-option': {
    'Containers': [
      { componentId: 'section-checkout-driver-tip', label: 'SectionCheckoutDriverTip' },
    ],
  },
  'option-payment-choice': {
    'Containers': [
      { componentId: 'section-checkout-payment-method', label: 'SectionCheckoutPaymentMethod' },
    ],
  },
  'notification-types': {
    'Containers': [
      { componentId: 'section-notification-type', label: 'SectionNotificationType' },
    ],
  },
  'order-line-item': {
    'Containers': [
      { componentId: 'section-order-summary', label: 'SectionOrderSummary' },
      { componentId: 'section-previous-order-summary', label: 'SectionPreviousOrderSummary' },
    ],
  },
  'previous-cart-item': {
    'Containers': [
      { componentId: 'section-previous-order-summary', label: 'SectionPreviousOrderSummary' },
    ],
  },
  'divider': {
    'Containers': [
      { componentId: 'section-previous-order-summary', label: 'SectionPreviousOrderSummary' },
    ],
  },
  'image-product-sheet': {
    'Containers': [
      { componentId: 'section-product-information', label: 'SectionProductInformation' },
    ],
  },
  'cart-item': {
    'Containers': [
      { componentId: 'section-products-in-cart', label: 'SectionProductsInCart' },
    ],
  },
  'required-indicator': {
    'Containers': [
      { componentId: 'section-required-product', label: 'SectionRequiredProduct' },
    ],
  },
  'text-field-label': {
    'Containers': [
      { componentId: 'delivery-preference', label: 'DeliveryPreference' },
    ],
    'Components': [
      { componentId: 'text-field', label: 'TextField' },
      { componentId: 'text-block', label: 'TextBlock' },
      { componentId: 'option-row', label: 'OptionRow' },
    ],
  },

  // --- Children used only in Components ---
  'caret': {
    'Components': [
      { componentId: 'button-location', label: 'ButtonLocation' },
      { componentId: 'button-order', label: 'ButtonOrder' },
      { componentId: 'address-search-result', label: 'AddressSearchResult' },
      { componentId: 'toast', label: 'Toast' },
    ],
  },
  'badge': {
    'Components': [
      { componentId: 'address-search-result', label: 'AddressSearchResult' },
      { componentId: 'button-location', label: 'ButtonLocation' },
    ],
  },
  'checkbox': {
    'Components': [
      { componentId: 'option-row', label: 'OptionRow' },
    ],
  },
  'radio-button': {
    'Components': [
      { componentId: 'option-row', label: 'OptionRow' },
    ],
  },
  'order-status-icon': {
    'Components': [
      { componentId: 'order-status', label: 'OrderStatus' },
    ],
  },
  'order-status-progress-bar': {
    'Components': [
      { componentId: 'order-status', label: 'OrderStatus' },
    ],
  },
  'text-order-status': {
    'Components': [
      { componentId: 'order-status', label: 'OrderStatus' },
    ],
  },
  'image-reward': {
    'Components': [
      { componentId: 'tile-rewards', label: 'TileRewards' },
    ],
  },
  'image-product-tile': {
    'Components': [
      { componentId: 'tile-product', label: 'TileProduct' },
    ],
  },
  'image-product-button': {
    'Components': [
      { componentId: 'button-product', label: 'ButtonProduct' },
      { componentId: 'button-product-reorder', label: 'ButtonProductReorder' },
    ],
  },
  'button-product-reorder': {
    'Containers': [{ componentId: 'reorder-products-list', label: 'ReorderProductsList' }],
  },
  'reorder-products-list': {
    'Containers': [
      { componentId: 'favorites-list', label: 'SectionFavoritesList' },
      { componentId: 'section-reorder', label: 'SectionReorder' },
    ],
  },
  'button-medium': {
    'Components': [
      { componentId: 'tile-order-history', label: 'TileOrderHistory' },
    ],
  },
}

// Compute the full nesting data for a component, including automatic nested resolution
function getComponentNestingData(componentId: string): ComponentNestingData {
  const result: ComponentNestingData = {}

  // Get manual "usedIn" data
  if (manualNestingData[componentId]) {
    result.usedIn = { ...manualNestingData[componentId] }
  }

  // Calculate "contains" (directly nested components) with deep nesting
  const directlyContains = componentContainsData[componentId]

  function getDeepNested(containerId: string, depth: number = 1, visited = new Set<string>()): NestingLink[] {
    if (visited.has(containerId)) return []
    visited.add(containerId)
    const links: NestingLink[] = []
    const containedIds = componentContainsData[containerId]
    if (containedIds) {
      for (const containedId of containedIds) {
        // Add this component with depth indicator
        const depthPrefix = depth > 1 ? '\u00A0\u00A0'.repeat(depth - 1) + '└ ' : ''
        links.push({
          componentId: containedId,
          label: depthPrefix + getComponentLabel(containedId),
        })
        // Recursively get deeper nested components
        const deeperNested = getDeepNested(containedId, depth + 1, visited)
        links.push(...deeperNested)
      }
    }
    return links
  }

  // If this component has direct contains, recalculate with proper depth display
  if (directlyContains && directlyContains.length > 0) {
    const nestedWithDepth: NestingLink[] = []
    for (const containedId of directlyContains) {
      nestedWithDepth.push({
        componentId: containedId,
        label: getComponentLabel(containedId),
      })
      // Add deeper nested components
      const deeperNested = getDeepNested(containedId, 2)
      nestedWithDepth.push(...deeperNested)
    }
    result.contains = nestedWithDepth
  }

  return result
}

// Chrome (mobile) component IDs - these should force mobile viewport
const MOBILE_CHROME_COMPONENTS = [
  'header-menu',
  'header-cart',
  'header-rewards',
  'header-other',
  'navigation-mobile',
  'sheet-grabber',
]

// Chrome (desktop) component IDs - these should force desktop viewport
const DESKTOP_CHROME_COMPONENTS = [
  'navigation-desktop',
  'footer-desktop',
  'sheet-close-button',
]

interface ComponentsPreviewProps {
  settings: ShowcaseSettings
  onSettingsChange: (settings: ShowcaseSettings) => void
  componentId: string
  onNavigate: (componentId: string) => void
  onForcedViewportModeChange?: (mode: ForcedViewportMode) => void
  filteredComponents?: FilteredComponent[]
  onBack?: () => void
}

// Reusable Segmented Toggle Component
interface SegmentedToggleChoice<T> {
  value: T
  label: ReactNode
}

interface SegmentedToggleProps<T> {
  choices: SegmentedToggleChoice<T>[]
  value: T
  onChange: (value: T) => void
  disabled?: boolean
}

function SegmentedToggle<T>({ choices, value, onChange, disabled = false }: SegmentedToggleProps<T>) {
  const selectedIndex = choices.findIndex(c => c.value === value)
  const choiceCount = choices.length

  // Calculate indicator positioning for consistent 2px padding
  const indicatorWidth = `calc(${100 / choiceCount}% - ${4 / choiceCount}px)`
  const getIndicatorLeft = (index: number) => {
    if (index === 0) return '2px'
    if (index === choiceCount - 1) return `calc(${(index * 100) / choiceCount}% - ${2 - (4 / choiceCount)}px)`
    return `calc(${(index * 100) / choiceCount}% + ${2 / choiceCount}px)`
  }

  const handleClick = (clickedIndex: number) => {
    if (disabled) return
    if (clickedIndex === selectedIndex) {
      // Clicking on current selection - cycle to next
      const nextIndex = (selectedIndex + 1) % choiceCount
      const nextChoice = choices[nextIndex]
      if (nextChoice) onChange(nextChoice.value)
    } else {
      // Clicking on different option - select it
      const clickedChoice = choices[clickedIndex]
      if (clickedChoice) onChange(clickedChoice.value)
    }
  }

  return (
    <div
      className="relative flex rounded-md overflow-hidden"
      style={{
        backgroundColor: '#262626',
        padding: '2px',
        opacity: disabled ? 0.6 : 1
      }}
    >
      {/* Sliding indicator */}
      <div
        className="absolute rounded transition-all duration-200 ease-out"
        style={{
          backgroundColor: '#404040',
          width: indicatorWidth,
          top: '2px',
          bottom: '2px',
          left: getIndicatorLeft(selectedIndex),
        }}
      />
      {choices.map((choice, choiceIndex) => (
        <button
          key={choiceIndex}
          onClick={() => handleClick(choiceIndex)}
          disabled={disabled}
          className="relative flex-1 py-2 px-3 text-sm transition-colors z-10 flex items-center justify-center gap-1.5"
          style={{
            color: value === choice.value ? '#ffffff' : '#a1a1a1',
            fontWeight: 500,
            cursor: disabled ? 'not-allowed' : 'pointer',
          }}
        >
          {choice.label}
        </button>
      ))}
    </div>
  )
}

// Style variable interface for individual rows
interface StyleRow {
  label: string      // e.g., "Height", "Width", "Background Fill"
  variable: string   // e.g., "--layout-size-button-large" (empty string if not yet filled in)
}

// Style sections for a component - supports arbitrary section names
type ComponentStyles = Record<string, StyleRow[]>

// Option types for component configuration
// Toggle: segmented control with sliding indicator (2-3 options, full width)
interface ToggleOption {
  type: 'toggle'
  label: string
  choices: { value: string; label: string }[]
  defaultValue: string
}

// Buttons: multi-select buttons in a grid (checkbox-like behavior)
interface ButtonsOption {
  type: 'buttons'
  label: string
  choices: { value: string; label: string }[]
  defaultValues: string[] // multiple can be selected
  columns?: 1 | 2 | 3 // default is 3, but can be 1 for full-width buttons
}

// ButtonGroup: single-select buttons in a grid (radio-like behavior)
interface ButtonGroupOption {
  type: 'buttonGroup'
  label: string
  choices: { value: string; label: string }[]
  defaultValue: string
  columns?: 1 | 2 | 3 // default is 3
}

// Number: numeric up/down control (similar to ProductCount)
interface NumberOption {
  type: 'number'
  label: string
  defaultValue: number
  min?: number  // defaults to 1
  max?: number  // defaults to 25
}

type ComponentOption = ToggleOption | ButtonsOption | ButtonGroupOption | NumberOption

// Component configuration interface
interface ComponentConfig {
  component: (options: Record<string, any>, settings: ShowcaseSettings, setOptionValue?: (key: string, value: unknown) => void) => ReactNode
  styles?: ComponentStyles
  options?: ComponentOption[]
}

// Button variant types for shared options
const BUTTON_VARIANTS = [
  { value: 'Prominent', label: 'Prominent' },
  { value: 'Primary', label: 'Primary' },
  { value: 'Secondary', label: 'Secondary' },
  { value: 'Tertiary', label: 'Tertiary' },
  { value: 'Warning', label: 'Warning' },
  { value: 'Chrome', label: 'Chrome' },
  { value: 'Unavailable', label: 'Unavailable' },
]

// Sample product data for MenuCategory (25 items to support max count)
const sampleProducts = Array.from({ length: 25 }, () => ({
  productName: 'Delicious Item',
  productDescription: 'A wonderful menu item with fresh ingredients and amazing flavor',
  priceCurrent: '$12.99',
  showImageProductTile: true,
  showProductDescription: true,
}))

// Sample tabs for TabsContainer
const sampleTabs = [
  { id: 'menu', label: 'Menu', iconName: 'menu' as const },
  { id: 'offers', label: 'Offers', iconName: 'confetti' as const },
  { id: 'rewards', label: 'Rewards', iconName: 'star' as const },
  { id: 'account', label: 'Account', iconName: 'account' as const },
]

// Sample product choices for ProductChoices
const sampleProductChoices = [
  {
    id: '1',
    productName: 'Classic Burger',
    priceCurrent: '$12.99',
    productDescription: 'Juicy beef patty with fresh lettuce',
  },
  {
    id: '2',
    productName: 'Chicken Sandwich',
    priceCurrent: '$10.99',
    productDescription: 'Crispy chicken with pickles',
  },
]

// Map lowercase restaurant names to capitalized brand names for HeaderMenu
const restaurantToBrand: Record<string, "FastSushi" | "Woodfire" | "Scoop" | "ThaiHouse"> = {
  fastsushi: 'FastSushi',
  woodfire: 'Woodfire',
  scoop: 'Scoop',
  thaihouse: 'ThaiHouse',
}

// Sample offer data for OfferGroup (25 items to support max count)
const sampleOffers = Array.from({ length: 25 }, () => ({
  offerName: 'Buy One Get One',
  buttonText: 'Add to Order',
}))

// Sample order data for OrderHistorySheet (25 items to support max count)
const sampleOrders = Array.from({ length: 25 }, (_, index) => ({
  orderNumber: `${100000000 + index}`,
  orderCost: `$${(12.34 + index * 2.5).toFixed(2)}`,
  orderType: (index % 2 === 0 ? 'Pickup' : 'Delivery') as 'Pickup' | 'Delivery',
  locationName: 'Downtown',
  locationAddress: '123 Main St, Los Angeles',
  orderDate: `January ${index + 1}, 2026 at 12:00 PM`,
}))

// Sample reward data for RewardPointsGroup (25 items to support max count)
const sampleRewards = Array.from({ length: 25 }, () => ({
  rewardName: 'Free Product',
  buttonText: 'Redeem 100 Points',
  isEarned: true,
}))

// Sample reorder product data for ReorderProductsList (25 items to support max count)
const sampleReorderProducts = Array.from({ length: 25 }, () => ({
  productName: 'Delicious Item',
  productOptions: 'Pellentesque id; Arcu id lacus; Condimentum laoreet',
  showProductOptions: true,
  showImageProductButton: true,
}))

// Sample location data for LocationList (25 items to support max count)
const sampleLocations = Array.from({ length: 25 }, (_, index) => ({
  id: String(index + 1),
  locationName: `Location ${index + 1}`,
  locationAddress: `${100 + index} Main Street, City, ST ${10000 + index}`,
  hoursText: 'Open until 10:00 PM',
  distanceText: `${(index * 0.5 + 0.5).toFixed(1)} mi`,
}))

// Component configurations with actual components and style sections
const componentConfigs: Record<string, ComponentConfig> = {
  // ===== ATOMIC COMPONENTS =====
  'button-category': {
    component: (options) => (
      <ButtonCategory
        label="Entrees"
        selected={options.selected === 'true'}
        onPress={() => {}}
      />
    ),
    styles: {
      layout: [
        { label: 'Height', variable: '--layout-size-button-medium' },
        { label: 'Width', variable: 'w-fit' },
        { label: 'Corner Radius', variable: 'rounded-full' },
        { label: 'X Padding', variable: 'px-[var(--layout-space-05)]' },
      ],
      text: [
        { label: 'Text Style', variable: 'text-style-button-medium' },
        { label: 'Font Size', variable: '--layout-font-size-sm' },
        { label: 'Font Weight', variable: '630' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0.01em' },
      ],
      'color (selected)': [
        { label: 'Background Fill', variable: '--theme-fill-object-toolbar' },
        { label: 'Background Fill (Hover)', variable: '--theme-fill-object-toolbar-hover' },
        { label: 'Background Fill (Press)', variable: '--theme-fill-object-toolbar-press' },
        { label: 'Text Color', variable: '--theme-text-object-toolbar' },
      ],
      'color (unselected)': [
        { label: 'Background Fill', variable: '--theme-fill-toolbar' },
        { label: 'Background Fill (Hover)', variable: '--theme-fill-toolbar-hover' },
        { label: 'Background Fill (Press)', variable: '--theme-fill-toolbar-press' },
        { label: 'Text Color', variable: '--theme-text-toolbar' },
      ],
    },
    options: [
      {
        type: 'toggle',
        label: 'Selected',
        choices: [
          { value: 'false', label: 'Unselected' },
          { value: 'true', label: 'Selected' },
        ],
        defaultValue: 'false',
      },
    ],
  },

  'button-search': {
    component: (options) => (
      <ButtonSearch
        variant={options.variant || 'Compact'}
        onPress={() => {}}
      />
    ),
    styles: {
      layout: [
        { label: 'Height', variable: '--layout-size-button-medium' },
        { label: 'Width', variable: 'size-[var(--layout-size-button-medium)] (compact) / w-fit (full, close)' },
        { label: 'Corner Radius', variable: 'rounded-full' },
        { label: 'Left Padding', variable: 'pl-[var(--layout-space-04)] (full, close only)' },
        { label: 'Right Padding', variable: 'pr-[var(--layout-space-05)] (full, close only)' },
        { label: 'Gap', variable: 'gap-[var(--layout-space-03)] (full, close only)' },
        { label: 'Icon Size', variable: '--layout-icon-size-xs' },
      ],
      text: [
        { label: 'Text Style', variable: 'text-style-button-medium' },
        { label: 'Font Size', variable: '--layout-font-size-sm' },
        { label: 'Font Weight', variable: '630' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0.01em' },
      ],
      'color (compact / full)': [
        { label: 'Background Fill', variable: '--theme-fill-toolbar' },
        { label: 'Background Fill (Hover)', variable: '--theme-fill-toolbar-hover' },
        { label: 'Background Fill (Press)', variable: '--theme-fill-toolbar-press' },
        { label: 'Icon Color', variable: '--theme-icon-toolbar' },
        { label: 'Text Color', variable: '--theme-text-toolbar' },
      ],
      'color (close)': [
        { label: 'Background Fill', variable: '--theme-fill-object-toolbar' },
        { label: 'Background Fill (Hover)', variable: '--theme-fill-object-toolbar-hover' },
        { label: 'Background Fill (Press)', variable: '--theme-fill-object-toolbar-press' },
        { label: 'Icon Color', variable: '--theme-icon-object-toolbar' },
        { label: 'Text Color', variable: '--theme-text-object-toolbar' },
      ],
    },
    options: [
      {
        type: 'buttonGroup',
        label: 'Variant',
        choices: [
          { value: 'Compact', label: 'Compact' },
          { value: 'Full', label: 'Full' },
          { value: 'Close', label: 'Close' },
        ],
        defaultValue: 'Compact',
      },
    ],
  },

  'button-small': {
    component: (options) => (
      <ButtonSmall
        variant={options.type || 'Primary'}
        onPress={() => {}}
      >
        BUTTON
      </ButtonSmall>
    ),
    styles: {
      layout: [
        { label: 'Height', variable: '--layout-size-button-small' },
        { label: 'Width', variable: 'w-fit' },
        { label: 'Corner Radius', variable: '--layout-radius-button-small' },
        { label: 'X Padding', variable: '--layout-space-04' },
      ],
      text: [
        { label: 'Text Style', variable: 'text-style-button-small' },
        { label: 'Font Size', variable: '--layout-font-size-xs' },
        { label: 'Font Weight', variable: '700' },
        { label: 'Line Height', variable: '1.0' },
        { label: 'Letter Spacing', variable: '0.02em' },
      ],
      color: [
        { label: 'Background Fill', variable: '--theme-fill-object-{variant}' },
        { label: 'Background Fill (Hover)', variable: '--theme-fill-object-{variant}-hover' },
        { label: 'Background Fill (Press)', variable: '--theme-fill-object-{variant}-press' },
        { label: 'Text Color', variable: '--theme-text-object-{variant}' },
      ],
    },
    options: [
      {
        type: 'buttonGroup',
        label: 'Type',
        choices: BUTTON_VARIANTS,
        defaultValue: 'Primary',
      },
    ],
  },

  'button-large': {
    component: (options) => (
      <ButtonLarge
        variant={options.type || 'Primary'}
        fullWidth={options.width !== 'hug'}
        onPress={() => {}}
      >
        Button Text
      </ButtonLarge>
    ),
    styles: {
      layout: [
        { label: 'Height', variable: '--layout-size-button-large' },
        { label: 'Width', variable: 'w-full / w-fit (prop dependent)' },
        { label: 'Corner Radius', variable: '--layout-radius-md' },
        { label: 'X Padding', variable: '--layout-space-10' },
      ],
      text: [
        { label: 'Text Style', variable: 'text-style-button-large' },
        { label: 'Font Size', variable: '--layout-font-size-lg' },
        { label: 'Font Weight', variable: '630' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0.01em' },
      ],
      color: [
        { label: 'Background Fill', variable: '--theme-fill-object-{variant}' },
        { label: 'Background Fill (Hover)', variable: '--theme-fill-object-{variant}-hover' },
        { label: 'Background Fill (Press)', variable: '--theme-fill-object-{variant}-press' },
        { label: 'Text Color', variable: '--theme-text-object-{variant}' },
      ],
    },
    options: [
      {
        type: 'buttonGroup',
        label: 'Type',
        choices: BUTTON_VARIANTS,
        defaultValue: 'Primary',
      },
      {
        type: 'toggle',
        label: 'Width',
        choices: [
          { value: 'fill', label: 'Fill' },
          { value: 'hug', label: 'Hug' },
        ],
        defaultValue: 'fill',
      },
    ],
  },

  'button-medium': {
    component: (options) => (
      <ButtonMedium
        variant={options.type || 'Primary'}
        fullWidth={options.width !== 'hug'}
        onPress={() => {}}
      >
        Button Text
      </ButtonMedium>
    ),
    styles: {
      layout: [
        { label: 'Height', variable: '--layout-size-button-medium' },
        { label: 'Width', variable: 'w-full / w-fit (prop dependent)' },
        { label: 'Corner Radius', variable: '--layout-radius-sm' },
        { label: 'X Padding', variable: '--layout-space-06' },
      ],
      text: [
        { label: 'Text Style', variable: 'text-style-button-medium' },
        { label: 'Font Size', variable: '--layout-font-size-sm' },
        { label: 'Font Weight', variable: '630' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0.01em' },
      ],
      color: [
        { label: 'Background Fill', variable: '--theme-fill-object-{variant}' },
        { label: 'Background Fill (Hover)', variable: '--theme-fill-object-{variant}-hover' },
        { label: 'Background Fill (Press)', variable: '--theme-fill-object-{variant}-press' },
        { label: 'Text Color', variable: '--theme-text-object-{variant}' },
      ],
    },
    options: [
      {
        type: 'buttonGroup',
        label: 'Type',
        choices: BUTTON_VARIANTS,
        defaultValue: 'Primary',
      },
      {
        type: 'toggle',
        label: 'Width',
        choices: [
          { value: 'fill', label: 'Fill' },
          { value: 'hug', label: 'Hug' },
        ],
        defaultValue: 'fill',
      },
    ],
  },

  'button-checkout': {
    component: (options) => (
      <ButtonCheckout
        orderTotal="$24.99"
        buttonState={options.State as ButtonCheckoutState}
        onPress={() => {}}
      />
    ),
    styles: {
      layout: [
        { label: 'Height', variable: '--layout-height-button-checkout' },
        { label: 'Width', variable: 'w-full' },
        { label: 'Max Width', variable: '--layout-width-max-checkout-button' },
        { label: 'Corner Radius', variable: '--layout-radius-button-checkout' },
        { label: 'X Padding', variable: '--layout-space-09' },
        { label: 'Gap', variable: '--layout-space-07' },
        { label: 'Icon Size', variable: '--layout-icon-size-md' },
      ],
      'text (checkout label)': [
        { label: 'Text Style', variable: 'text-style-button-large-light' },
        { label: 'Font Size', variable: '--layout-font-size-lg' },
        { label: 'Font Weight', variable: '600' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0.01em' },
      ],
      'text (price)': [
        { label: 'Text Style', variable: 'text-style-button-large' },
        { label: 'Font Size', variable: '--layout-font-size-lg' },
        { label: 'Font Weight', variable: '630' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0.01em' },
      ],
      'color (default)': [
        { label: 'Background Fill', variable: '--theme-fill-object-chrome-primary' },
        { label: 'Text Color', variable: '--theme-text-object-chrome-primary' },
      ],
      'color (hover)': [
        { label: 'Background Fill', variable: '--theme-fill-object-chrome-primary-hover' },
        { label: 'Shadow', variable: '--effect-shadow-object-chrome-hover' },
      ],
      'color (press)': [
        { label: 'Background Fill', variable: '--theme-fill-object-chrome-primary-press' },
        { label: 'Shadow', variable: '--effect-shadow-object-chrome-press' },
      ],
    },
    options: [
      {
        type: 'buttonGroup',
        label: 'State',
        choices: [
          { value: 'Default', label: 'Default' },
          { value: 'Hover', label: 'Hover' },
          { value: 'Press', label: 'Press' },
        ],
        defaultValue: 'Default',
      },
    ],
  },

  'button-add-product': {
    component: (options) => (
      <ButtonAddProduct
        price="$12.99"
        missingRequirements={options.disabled === 'disabled' ? 'Missing 2 required options' : undefined}
        buttonText={options.buttonText === 'update' ? 'Update Item' : undefined}
        onPress={() => {}}
      />
    ),
    styles: {
      layout: [
        { label: 'Height', variable: '--layout-height-button-add-product' },
        { label: 'Width', variable: 'w-full' },
        { label: 'Corner Radius', variable: '--layout-radius-md' },
        { label: 'X Padding', variable: '--layout-space-09' },
        { label: 'Gap', variable: '--layout-space-03' },
      ],
      'text (add to order)': [
        { label: 'Text Style', variable: 'text-style-button-large-light' },
        { label: 'Font Size', variable: '--layout-font-size-lg' },
        { label: 'Font Weight', variable: '600' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0.01em' },
      ],
      'text (price)': [
        { label: 'Text Style', variable: 'text-style-button-large' },
        { label: 'Font Size', variable: '--layout-font-size-lg' },
        { label: 'Font Weight', variable: '630' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0.01em' },
      ],
      'color (enabled)': [
        { label: 'Background Fill', variable: '--theme-fill-object-primary' },
        { label: 'Hover Fill', variable: '--theme-fill-object-primary-hover' },
        { label: 'Press Fill', variable: '--theme-fill-object-primary-press' },
        { label: 'Text Color', variable: '--theme-text-object-primary' },
        { label: 'Hover Shadow', variable: '--effect-shadow-object-hover' },
        { label: 'Press Shadow', variable: '--effect-shadow-object-press' },
      ],
      'color (disabled)': [
        { label: 'Background Fill', variable: '--theme-fill-object-disabled' },
        { label: 'Text Color', variable: '--theme-text-tertiary' },
      ],
    },
    options: [
      {
        type: 'toggle',
        label: 'Disabled',
        choices: [
          { value: 'enabled', label: 'Enabled' },
          { value: 'disabled', label: 'Disabled' },
        ],
        defaultValue: 'enabled',
      },
      {
        type: 'toggle',
        label: 'Button Text',
        choices: [
          { value: 'add', label: 'Add to Order' },
          { value: 'update', label: 'Update Item' },
        ],
        defaultValue: 'add',
      },
    ],
  },

  'button-tip': {
    component: (options, _settings, setOptionValue) => (
      <ButtonTip
        tipAmount="$3.00"
        tipPercentage="15%"
        buttonType={options.buttonType || 'Unselected'}
        onPress={() => setOptionValue?.('buttonType', options.buttonType === 'Selected' ? 'Unselected' : 'Selected')}
      />
    ),
    styles: {
      layout: [
        { label: 'Width', variable: 'w-full' },
        { label: 'Padding Top', variable: '--layout-space-06' },
        { label: 'Padding Bottom', variable: '--layout-space-05' },
        { label: 'Content Gap', variable: '--layout-space-03' },
        { label: 'Corner Radius', variable: '--layout-radius-md' },
        { label: 'Stroke (unselected)', variable: '1px' },
        { label: 'Stroke (selected)', variable: '3px' },
      ],
      'text (tip amount)': [
        { label: 'Text Style', variable: 'text-style-button-large-heavy' },
        { label: 'Font Size', variable: '--layout-font-size-lg' },
        { label: 'Font Weight', variable: '700' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0.01em' },
      ],
      'text (tip percentage)': [
        { label: 'Text Style', variable: 'text-style-button-small' },
        { label: 'Font Size', variable: '--layout-font-size-xs' },
        { label: 'Font Weight', variable: '700' },
        { label: 'Line Height', variable: '1' },
        { label: 'Letter Spacing', variable: '0.02em' },
      ],
      'color (unselected, default)': [
        { label: 'Background Fill', variable: '--theme-fill-object-elevated' },
        { label: 'Text Color', variable: '--theme-text-tertiary' },
        { label: 'Stroke Color', variable: '--theme-stroke-tertiary' },
      ],
      'color (unselected, hover)': [
        { label: 'Background Fill', variable: '--theme-fill-object-elevated-hover' },
        { label: 'Stroke Color', variable: '--theme-stroke-secondary' },
        { label: 'Shadow', variable: '--effect-shadow-object-hover' },
      ],
      'color (unselected, press)': [
        { label: 'Background Fill', variable: '--theme-fill-object-elevated-press' },
        { label: 'Stroke Color', variable: '--theme-stroke-primary' },
        { label: 'Shadow', variable: '--effect-shadow-object-press' },
      ],
      'color (selected)': [
        { label: 'Background Fill', variable: '--theme-fill-object-secondary' },
        { label: 'Text Color', variable: '--theme-text-object-secondary' },
        { label: 'Stroke Color', variable: '--theme-stroke-selected' },
      ],
    },
  },

  'button-notification': {
    component: (options, _settings, setOptionValue) => (
      <ButtonNotification
        notificationType={(options.notificationType || 'Push') as NotificationType}
        buttonType={options.buttonType || 'Unselected'}
        onPress={() => setOptionValue?.('buttonType', options.buttonType === 'Selected' ? 'Unselected' : 'Selected')}
      />
    ),
    styles: {
      layout: [
        { label: 'Width', variable: 'w-full' },
        { label: 'Padding Y', variable: '--layout-space-05' },
        { label: 'Content Gap', variable: '--layout-space-03' },
        { label: 'Corner Radius', variable: '--layout-radius-md' },
        { label: 'Icon Size', variable: 'size-6 (24×24)' },
        { label: 'Stroke (unselected)', variable: '1px' },
        { label: 'Stroke (selected)', variable: '3px' },
      ],
      text: [
        { label: 'Text Style', variable: 'text-style-button-small' },
        { label: 'Font Size', variable: '--layout-font-size-xs' },
        { label: 'Font Weight', variable: '700' },
        { label: 'Line Height', variable: '1' },
        { label: 'Letter Spacing', variable: '0.02em' },
      ],
      'color (unselected, default)': [
        { label: 'Background Fill', variable: '--theme-fill-object-elevated' },
        { label: 'Text Color', variable: '--theme-text-tertiary' },
        { label: 'Stroke Color', variable: '--theme-stroke-tertiary' },
      ],
      'color (unselected, hover)': [
        { label: 'Background Fill', variable: '--theme-fill-object-elevated-hover' },
        { label: 'Stroke Color', variable: '--theme-stroke-secondary' },
        { label: 'Shadow', variable: '--effect-shadow-object-hover' },
      ],
      'color (unselected, press)': [
        { label: 'Background Fill', variable: '--theme-fill-object-elevated-press' },
        { label: 'Stroke Color', variable: '--theme-stroke-primary' },
        { label: 'Shadow', variable: '--effect-shadow-object-press' },
      ],
      'color (selected)': [
        { label: 'Background Fill', variable: '--theme-fill-object-secondary' },
        { label: 'Text Color', variable: '--theme-text-object-secondary' },
        { label: 'Stroke Color', variable: '--theme-stroke-selected' },
      ],
    },
    options: [
      {
        type: 'buttonGroup',
        label: 'Notification Type',
        choices: [
          { value: 'Push', label: 'Push' },
          { value: 'Email', label: 'Email' },
          { value: 'Text', label: 'Text' },
        ],
        defaultValue: 'Push',
      },
    ],
  },

  'button-product': {
    component: (options, settings) => (
      <ButtonProduct
        productName="Product Name"
        priceCurrent="$5.50"
        priceOld={options.elements?.includes('oldPrice') ? "$11.00" : undefined}
        showPriceOld={options.elements?.includes('oldPrice') || false}
        showImageProductButton={options.elements?.includes('image') !== false}
        imagePosition={options.imagePosition || 'right'}
        imageUrl={`/showcase/placeholders/${settings.restaurant}/${settings.restaurant}-product-button.png`}
        onPress={() => {}}
      />
    ),
    styles: {
      layout: [
        { label: 'Height', variable: 'Hug' },
        { label: 'Width', variable: 'w-full' },
        { label: 'Corner Radius', variable: '--layout-radius-md' },
      ],
      'layout (text container)': [
        { label: 'Width', variable: 'w-full' },
        { label: 'X Padding', variable: '--layout-space-06' },
        { label: 'Y Padding', variable: '--layout-space-05' },
        { label: 'Gap', variable: '--layout-space-03' },
      ],
      'layout (price container)': [
        { label: 'Gap', variable: '--layout-space-03' },
      ],
      'layout (divider)': [
        { label: 'Width', variable: '1px' },
        { label: 'Color', variable: '--theme-stroke-quaternary' },
      ],
      'layout (image container)': [
        { label: 'Background Fill', variable: '--theme-fill-image' },
      ],
      'text (name)': [
        { label: 'Text Style', variable: 'text-style-h3-light' },
        { label: 'Font Size', variable: '--layout-font-size-lg' },
        { label: 'Font Weight', variable: '630' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0.01em' },
      ],
      'text (price)': [
        { label: 'Text Style', variable: 'text-style-h5' },
        { label: 'Font Size', variable: '--layout-font-size-sm' },
        { label: 'Font Weight', variable: '630' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0.01em' },
      ],
      'color (default)': [
        { label: 'Background Fill', variable: '--theme-fill-object-elevated' },
        { label: 'Border Stroke', variable: '--theme-stroke-tertiary' },
      ],
      'color (hover)': [
        { label: 'Background Fill', variable: '--theme-fill-object-elevated-hover' },
        { label: 'Border Stroke', variable: '--theme-stroke-secondary' },
        { label: 'Shadow', variable: '--effect-shadow-object-hover' },
      ],
      'color (press)': [
        { label: 'Background Fill', variable: '--theme-fill-object-elevated-press' },
        { label: 'Border Stroke', variable: '--theme-stroke-primary' },
        { label: 'Shadow', variable: '--effect-shadow-object-press' },
      ],
      'color (text)': [
        { label: 'Text (Name)', variable: '--theme-text-primary' },
        { label: 'Text (Price, current)', variable: '--theme-text-secondary' },
        { label: 'Text (Price, old)', variable: '--theme-text-tertiary' },
      ],
    },
    options: [
      {
        type: 'buttons',
        label: 'Elements',
        choices: [
          { value: 'image', label: 'Image' },
          { value: 'oldPrice', label: 'Old Price' },
        ],
        defaultValues: ['image'],
        columns: 2,
      },
      {
        type: 'toggle',
        label: 'Image Position',
        choices: [
          { value: 'left', label: 'Left' },
          { value: 'right', label: 'Right' },
        ],
        defaultValue: 'right',
      },
    ],
  },

  'button-product-reorder': {
    component: (options, settings) => (
      <ButtonProductReorder
        productName="Product Name"
        productOptions={options.elements?.includes('options') !== false ? "Pellentesque id; Arcu id lacus; Condimentum laoreet; Phasellus bibendum; Interdum" : undefined}
        showProductOptions={options.elements?.includes('options') !== false}
        showImageProductButton={options.elements?.includes('image') !== false}
        imageUrl={`/showcase/placeholders/${settings.restaurant}/${settings.restaurant}-product-button.png`}
        onPress={() => {}}
      />
    ),
    styles: {
      layout: [
        { label: 'Height', variable: 'Hug' },
        { label: 'Width', variable: 'w-full' },
        { label: 'Corner Radius', variable: '--layout-radius-md' },
      ],
      'layout (text container)': [
        { label: 'Width', variable: 'w-full' },
        { label: 'X Padding', variable: '--layout-space-06' },
        { label: 'Y Padding', variable: '--layout-space-05' },
        { label: 'Gap', variable: '--layout-space-02' },
      ],
      'layout (image container)': [
        { label: 'Background Fill', variable: '--theme-fill-image' },
        { label: 'Border Left', variable: '1px solid --theme-stroke-quaternary' },
      ],
      'text (name)': [
        { label: 'Text Style', variable: 'text-style-h4' },
        { label: 'Font Size', variable: '--layout-font-size-md' },
        { label: 'Font Weight', variable: '630' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0.01em' },
      ],
      'text (options)': [
        { label: 'Text Style', variable: 'text-style-h5-light' },
        { label: 'Font Size', variable: '--layout-font-size-sm' },
        { label: 'Font Weight', variable: '500' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0.01em' },
      ],
      'color (default)': [
        { label: 'Background Fill', variable: '--theme-fill-object-elevated' },
        { label: 'Border Stroke', variable: '--theme-stroke-tertiary' },
      ],
      'color (hover)': [
        { label: 'Background Fill', variable: '--theme-fill-object-elevated-hover' },
        { label: 'Border Stroke', variable: '--theme-stroke-secondary' },
        { label: 'Shadow', variable: '--effect-shadow-object-hover' },
      ],
      'color (press)': [
        { label: 'Background Fill', variable: '--theme-fill-object-elevated-press' },
        { label: 'Border Stroke', variable: '--theme-stroke-primary' },
        { label: 'Shadow', variable: '--effect-shadow-object-press' },
      ],
      'color (text)': [
        { label: 'Text (Name)', variable: '--theme-text-primary' },
        { label: 'Text (Options)', variable: '--theme-text-secondary' },
      ],
    },
    options: [
      {
        type: 'buttons',
        label: 'Elements',
        choices: [
          { value: 'image', label: 'Image' },
          { value: 'options', label: 'Options' },
        ],
        defaultValues: ['image', 'options'],
        columns: 2,
      },
    ],
  },

  'toggle-order-type': {
    component: (options, _settings, setOptionValue) => (
      <ToggleOrderType
        selected={options.selected || 'Pickup'}
        onSelectionChange={() => setOptionValue?.('selected', options.selected === 'Pickup' ? 'Delivery' : 'Pickup')}
      />
    ),
    styles: {
      layout: [
        { label: 'Height', variable: 'Hug' },
        { label: 'Width', variable: '--layout-width-toggle-order-type' },
        { label: 'Corner Radius', variable: '--layout-radius-md' },
        { label: 'Padding', variable: '--layout-space-01' },
      ],
      'layout (selected indicator)': [
        { label: 'Width', variable: 'calc((100% - 2 × space-01) / 2)' },
        { label: 'Height', variable: 'Fill' },
        { label: 'Corner Radius', variable: '--layout-radius-sm' },
      ],
      'layout (option text)': [
        { label: 'Width', variable: 'flex: 1 (fill)' },
        { label: 'Y Padding', variable: '--layout-space-03' },
      ],
      text: [
        { label: 'Text Style', variable: 'text-style-toggle' },
        { label: 'Font Size', variable: '--layout-font-size-xs' },
        { label: 'Font Weight', variable: '630' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0em' },
      ],
      'color (selected)': [
        { label: 'Indicator Fill', variable: '--theme-fill-object-chrome-primary' },
        { label: 'Indicator Fill (hover)', variable: '--theme-fill-object-chrome-primary + 10% overlay' },
        { label: 'Indicator Fill (press)', variable: '--theme-fill-object-chrome-primary + 15% overlay' },
        { label: 'Indicator Shadow (hover)', variable: '--effect-shadow-object-chrome-hover' },
        { label: 'Indicator Shadow (press)', variable: '--effect-shadow-object-chrome-press' },
        { label: 'Text Color', variable: '--theme-text-object-chrome-primary' },
      ],
      'color (unselected)': [
        { label: 'Background Fill', variable: '--theme-fill-input' },
        { label: 'Text Color', variable: '--theme-text-secondary' },
      ],
    },
  },

  'product-count': {
    component: (options, _settings, setOptionValue) => (
      <ProductCount
        value={options.value ?? 1}
        min={1}
        onChange={(newValue) => setOptionValue?.('value', newValue)}
      />
    ),
    styles: {
      layout: [
        { label: 'Height', variable: 'Hug' },
        { label: 'Min Width', variable: '--layout-width-min-product-count' },
      ],
      'layout (text)': [
        { label: 'X Padding', variable: '--layout-space-03' },
      ],
      text: [
        { label: 'Text Style', variable: 'text-style-button-large-light' },
        { label: 'Font Size', variable: '--layout-font-size-lg' },
        { label: 'Font Weight', variable: '600' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0.01em' },
      ],
      color: [
        { label: 'Text Color', variable: '--theme-text-primary' },
      ],
    },
  },

  // ===== MOLECULAR COMPONENTS =====
  'tile-rewards': {
    component: (options, settings) => (
      <TileRewards
        rewardName="Free Dessert"
        buttonText="Redeem 500 Points"
        pointsRequired={500}
        showRewardImage={options.image === 'show'}
        imagePosition="right"
        rewardImageUrl={`/showcase/placeholders/${settings.restaurant}/${settings.restaurant}-reward.png`}
        onClick={() => {}}
      />
    ),
    styles: {
      layout: [
        { label: 'Height', variable: 'Hug' },
        { label: 'Width', variable: 'w-full' },
        { label: 'Corner Radius', variable: '--layout-radius-lg' },
      ],
      'layout (text and button container)': [
        { label: 'Padding', variable: '--layout-space-05' },
        { label: 'Gap', variable: '--layout-space-03' },
      ],
      'layout (name container)': [
        { label: 'X Padding', variable: '--layout-space-03' },
        { label: 'Y Padding', variable: '--layout-space-05' },
      ],
      'layout (divider)': [
        { label: 'Width', variable: '1px' },
        { label: 'Color', variable: '--theme-stroke-quaternary' },
      ],
      'layout (image container)': [
        { label: 'Background Fill', variable: '--theme-fill-image' },
        { label: 'Corner Radius', variable: '--layout-radius-lg' },
      ],
      text: [
        { label: 'Text Style', variable: 'text-style-h2-light' },
        { label: 'Font Size', variable: '--layout-font-size-xl' },
        { label: 'Font Weight', variable: '630' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0.01em' },
      ],
      color: [
        { label: 'Background Fill', variable: '--theme-fill-object-raised' },
        { label: 'Text Color', variable: '--theme-text-primary' },
        { label: 'Stroke', variable: '--theme-stroke-tertiary' },
      ],
    },
    options: [
      {
        type: 'toggle',
        label: 'Image',
        choices: [
          { value: 'show', label: 'Show' },
          { value: 'hide', label: 'Hide' },
        ],
        defaultValue: 'show',
      },
    ],
  },

  'button-location': {
    component: (options) => {
      const badges = options.badges || []
      const isDisabled = options.disabled === 'yes'
      return (
        <ButtonLocation
          locationName="Downtown Location"
          locationAddress="123 Main Street, City, ST 12345"
          hoursText="Open until 10:00 PM"
          distanceText="0.5 mi"
          disabled={isDisabled}
          badges={
            badges.length > 0
              ? [
                  ...(badges.includes('selected') ? [{ variant: 'Default' as const, text: 'Selected Location' }] : []),
                  ...(badges.includes('notAccepting') ? [{ variant: 'Warning' as const, text: 'Not Accepting Orders' }] : []),
                  ...(badges.includes('offerNotAccepted') ? [{ variant: 'Warning' as const, text: 'Offer Not Accepted' }] : []),
                ]
              : undefined
          }
          onPress={() => {}}
        />
      )
    },
    styles: {
      layout: [
        { label: 'Height', variable: 'Hug' },
        { label: 'Width', variable: 'w-full' },
        { label: 'Corner Radius', variable: '--layout-radius-md' },
        { label: 'Padding', variable: '--layout-space-05' },
        { label: 'Gap', variable: '--layout-space-03' },
      ],
      'layout (info container)': [
        { label: 'Gap', variable: '--layout-space-02' },
      ],
      'layout (text container)': [
        { label: 'Gap', variable: '--layout-space-01' },
        { label: 'Padding Left', variable: '--layout-space-02' },
        { label: 'Padding Bottom', variable: '--layout-space-01' },
      ],
      'layout (badges)': [
        { label: 'Gap', variable: '--layout-space-02' },
        { label: 'Padding Top', variable: '--layout-space-02' },
      ],
      'text (name)': [
        { label: 'Text Style', variable: 'text-style-h2-light' },
        { label: 'Font Size', variable: '--layout-font-size-xl' },
        { label: 'Font Weight', variable: '630' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0.01em' },
      ],
      'text (address/hours)': [
        { label: 'Text Style', variable: 'text-style-body-small' },
        { label: 'Font Size', variable: '--layout-font-size-xs' },
        { label: 'Font Weight', variable: '500' },
        { label: 'Line Height', variable: '1.25' },
        { label: 'Letter Spacing', variable: '0em' },
      ],
      'text (distance)': [
        { label: 'Text Style', variable: 'text-style-body-small-light' },
        { label: 'Font Size', variable: '--layout-font-size-xs' },
        { label: 'Font Weight', variable: '400' },
        { label: 'Line Height', variable: '1.25' },
        { label: 'Letter Spacing', variable: '0em' },
      ],
      'color (default)': [
        { label: 'Background Fill', variable: '--theme-fill-object-elevated' },
        { label: 'Stroke', variable: '--theme-stroke-tertiary' },
        { label: 'Text (name/hours)', variable: '--theme-text-primary' },
        { label: 'Text (address/distance)', variable: '--theme-text-secondary' },
        { label: 'Icon/Caret', variable: '--theme-icon' },
      ],
      'color (hover)': [
        { label: 'Background Fill', variable: '--theme-fill-object-elevated-hover' },
        { label: 'Stroke', variable: '--theme-stroke-secondary' },
        { label: 'Shadow', variable: '--effect-shadow-object-hover' },
        { label: 'Icon/Caret', variable: '--theme-icon-hover' },
      ],
      'color (press)': [
        { label: 'Background Fill', variable: '--theme-fill-object-elevated-press' },
        { label: 'Stroke', variable: '--theme-stroke-primary' },
        { label: 'Shadow', variable: '--effect-shadow-object-press' },
        { label: 'Icon/Caret', variable: '--theme-icon-press' },
      ],
      'color (disabled)': [
        { label: 'Background Fill', variable: '--theme-fill-object-elevated' },
        { label: 'Stroke', variable: '--theme-stroke-quaternary' },
        { label: 'Text', variable: '--theme-text-disabled' },
        { label: 'Caret', variable: 'Hidden' },
      ],
    },
    options: [
      {
        type: 'buttons',
        label: 'Badges',
        choices: [
          { value: 'selected', label: 'Selected Location' },
          { value: 'notAccepting', label: 'Not Accepting Orders' },
          { value: 'offerNotAccepted', label: 'Offer Not Accepted' },
        ],
        defaultValues: [],
        columns: 1,
      },
      {
        type: 'buttonGroup',
        label: 'Disabled',
        choices: [
          { value: 'no', label: 'No' },
          { value: 'yes', label: 'Yes' },
        ],
        defaultValue: 'no',
      },
    ],
  },

  'button-order': {
    component: () => (
      <ButtonOrder
        orderNumber="123456789"
        orderCost="$12.34"
        orderType="Pickup"
        locationName="Downtown"
        locationAddress="123 Main St, Los Angeles"
        orderDate="January 1, 2026 at 12:00 PM"
        onPress={() => {}}
      />
    ),
    styles: {
      layout: [
        { label: 'Height', variable: 'Hug' },
        { label: 'Width', variable: 'w-full' },
        { label: 'Corner Radius', variable: '--layout-radius-md' },
        { label: 'Padding Left', variable: '--layout-space-06' },
        { label: 'Padding Right', variable: '--layout-space-05' },
        { label: 'Padding Vertical', variable: '--layout-space-05' },
        { label: 'Gap', variable: '--layout-space-03' },
      ],
      'layout (text container)': [
        { label: 'Gap', variable: '--layout-space-02' },
      ],
      'text (order summary)': [
        { label: 'Text Style', variable: 'text-style-h4' },
        { label: 'Font Size', variable: '--layout-font-size-md' },
        { label: 'Font Weight', variable: '630' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0.01em' },
      ],
      'text (order details)': [
        { label: 'Text Style', variable: 'text-style-body-small' },
        { label: 'Font Size', variable: '--layout-font-size-xs' },
        { label: 'Font Weight', variable: '500' },
        { label: 'Line Height', variable: '1.25' },
        { label: 'Letter Spacing', variable: '0em' },
      ],
      'text (order date)': [
        { label: 'Text Style', variable: 'text-style-body-small-bold' },
        { label: 'Font Size', variable: '--layout-font-size-xs' },
        { label: 'Font Weight', variable: '700' },
        { label: 'Line Height', variable: '1.25' },
        { label: 'Letter Spacing', variable: '0em' },
      ],
      'color (default)': [
        { label: 'Background Fill', variable: '--theme-fill-object-elevated' },
        { label: 'Stroke', variable: '--theme-stroke-tertiary' },
        { label: 'Text Color (summary)', variable: '--theme-text-primary' },
        { label: 'Text Color (details/date)', variable: '--theme-text-secondary' },
        { label: 'Icon/Caret', variable: '--theme-icon' },
      ],
      'color (hover)': [
        { label: 'Background Fill', variable: '--theme-fill-object-elevated-hover' },
        { label: 'Stroke', variable: '--theme-stroke-secondary' },
        { label: 'Shadow', variable: '--effect-shadow-object-hover' },
        { label: 'Icon/Caret', variable: '--theme-icon-hover' },
      ],
      'color (press)': [
        { label: 'Background Fill', variable: '--theme-fill-object-elevated-press' },
        { label: 'Stroke', variable: '--theme-stroke-primary' },
        { label: 'Shadow', variable: '--effect-shadow-object-press' },
        { label: 'Icon/Caret', variable: '--theme-icon-press' },
      ],
    },
  },

  'tile-product': {
    component: (options, settings) => {
      const shortDescription = "A tasty description of this menu item that customers will love."
      const longDescription = "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur."
      const elements = options.elements || ['image', 'description']
      const showImage = elements.includes('image')
      const showDescription = elements.includes('description')
      const showOldPrice = elements.includes('oldPrice')
      const useLongDescription = options.descriptionLength === 'long'
      return (
        <TileProduct
          productName="Delicious Item"
          productDescription={showDescription ? (useLongDescription ? longDescription : shortDescription) : undefined}
          priceCurrent="$12.99"
          priceOld={showOldPrice ? "$18.99" : undefined}
          showPriceOld={showOldPrice}
          showImageProductTile={showImage}
          showProductDescription={showDescription}
          imagePosition={options.imagePosition || 'right'}
          imageUrl={`/showcase/placeholders/${settings.restaurant}/${settings.restaurant}-product-tile.png`}
          onClick={() => {}}
        />
      )
    },
    styles: {
      layout: [
        { label: 'Height', variable: 'Hug' },
        { label: 'Width', variable: 'w-full' },
        { label: 'Min Width', variable: '--layout-width-min-tile-product' },
        { label: 'Max Width', variable: '--layout-width-max-tile-product' },
        { label: 'Corner Radius', variable: '--layout-radius-md' },
      ],
      'layout (text container)': [
        { label: 'X Padding', variable: '--layout-space-07' },
        { label: 'Y Padding', variable: '--layout-space-05' },
        { label: 'Gap', variable: '--layout-space-02' },
      ],
      'layout (price container)': [
        { label: 'Y Padding (top)', variable: '--layout-space-02' },
        { label: 'Y Padding (bottom)', variable: '1px' },
        { label: 'Gap', variable: '--layout-space-03' },
      ],
      'layout (divider)': [
        { label: 'Width', variable: '1px' },
        { label: 'Color', variable: '--theme-stroke-quaternary' },
      ],
      'layout (image container)': [
        { label: 'Background Fill', variable: '--theme-fill-image' },
        { label: 'Corner Radius', variable: '--layout-radius-md' },
      ],
      'text (name)': [
        { label: 'Text Style', variable: 'text-style-h2-light' },
        { label: 'Font Size', variable: '--layout-font-size-xl' },
        { label: 'Font Weight', variable: '630' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0.01em' },
      ],
      'text (description)': [
        { label: 'Text Style', variable: 'text-style-body-small-light' },
        { label: 'Font Size', variable: '--layout-font-size-xs' },
        { label: 'Font Weight', variable: '400' },
        { label: 'Line Height', variable: '1.25' },
        { label: 'Letter Spacing', variable: '0em' },
      ],
      'text (price)': [
        { label: 'Text Style', variable: 'text-style-h5' },
        { label: 'Font Size', variable: '--layout-font-size-sm' },
        { label: 'Font Weight', variable: '630' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0.01em' },
      ],
      'color (default)': [
        { label: 'Background Fill', variable: '--theme-fill-object-raised' },
        { label: 'Text Color (name/description)', variable: '--theme-text-primary' },
        { label: 'Text Color (price)', variable: '--theme-text-secondary' },
        { label: 'Text Color (old price)', variable: '--theme-text-tertiary' },
        { label: 'Stroke', variable: '--theme-stroke-tertiary' },
      ],
      'color (hover)': [
        { label: 'Background Fill', variable: '--theme-fill-object-raised-hover' },
        { label: 'Stroke', variable: '--theme-stroke-secondary' },
      ],
      'color (press)': [
        { label: 'Background Fill', variable: '--theme-fill-object-raised-press' },
        { label: 'Stroke', variable: '--theme-stroke-primary' },
      ],
    },
    options: [
      {
        type: 'buttons',
        label: 'Elements',
        choices: [
          { value: 'image', label: 'Image' },
          { value: 'description', label: 'Description' },
          { value: 'oldPrice', label: 'Old Price' },
        ],
        defaultValues: ['image', 'description'],
      },
      {
        type: 'toggle',
        label: 'Description',
        choices: [
          { value: 'short', label: 'Short' },
          { value: 'long', label: 'Long' },
        ],
        defaultValue: 'short',
      },
      {
        type: 'toggle',
        label: 'Image Position',
        choices: [
          { value: 'left', label: 'Left' },
          { value: 'right', label: 'Right' },
        ],
        defaultValue: 'right',
      },
    ],
  },

  'text-field': {
    component: (options) => {
      const labelState = options.fieldLabel || 'show'
      const showLabel = labelState !== 'hide'
      const showWarning = labelState === 'warning'
      const affixes = options.affixes || []
      return (
        <TextField
          value=""
          placeholder="Enter text..."
          label={showLabel ? (showWarning ? { label: 'Field Label', warningText: 'This field is required', showWarning: true } : { label: 'Field Label' }) : undefined}
          prefix={affixes.includes('prefix') ? '$' : undefined}
          suffix={affixes.includes('suffix') ? '°' : undefined}
          onChange={() => {}}
        />
      )
    },
    styles: {
      layout: [
        { label: 'Width', variable: 'w-full' },
        { label: 'Label Gap', variable: '--layout-space-02' },
      ],
      'layout (field)': [
        { label: 'X Padding', variable: '--layout-space-05' },
        { label: 'Y Padding', variable: '--layout-padding-text-field-inner-vertical' },
        { label: 'Gap', variable: '--layout-space-03' },
        { label: 'Corner Radius', variable: '--layout-radius-sm' },
      ],
      text: [
        { label: 'Text Style', variable: 'text-style-text-field' },
        { label: 'Font Size', variable: '--layout-font-size-md' },
        { label: 'Font Weight', variable: '500' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0.01em' },
      ],
      color: [
        { label: 'Background Fill', variable: '--theme-fill-input' },
        { label: 'Text Color', variable: '--theme-text-primary' },
        { label: 'Placeholder Color', variable: '--theme-text-placeholder' },
        { label: 'Text Color (prefix/suffix)', variable: '--theme-text-tertiary' },
        { label: 'Stroke', variable: '--theme-stroke-secondary' },
        { label: 'Stroke (focused)', variable: '--theme-stroke-selected' },
      ],
    },
    options: [
      {
        type: 'toggle',
        label: 'Field Label',
        choices: [
          { value: 'hide', label: 'Hide' },
          { value: 'show', label: 'Show' },
          { value: 'warning', label: 'Warning' },
        ],
        defaultValue: 'show',
      },
      {
        type: 'buttons',
        label: 'Affixes',
        choices: [
          { value: 'prefix', label: 'Prefix ($)' },
          { value: 'suffix', label: 'Suffix (°)' },
        ],
        defaultValues: [],
        columns: 2,
      },
    ],
  },

  'tile-page': {
    component: () => (
      <TilePage
        pageTitle="Manage Account"
        iconName="accountSettings"
        onClick={() => {}}
      />
    ),
    styles: {
      layout: [
        { label: 'Height', variable: 'Locked aspect ratio (3:2)' },
        { label: 'Width', variable: 'w-full' },
        { label: 'Min Height', variable: '100px' },
        { label: 'Min Width', variable: '--layout-width-min-tile-page' },
        { label: 'Corner Radius', variable: '--layout-radius-lg' },
        { label: 'X Padding', variable: '--layout-space-04' },
        { label: 'Y Padding', variable: '--layout-space-04' },
        { label: 'Gap', variable: '--layout-space-07' },
      ],
      text: [
        { label: 'Text Style', variable: 'text-style-h4' },
        { label: 'Font Size', variable: '--layout-font-size-md' },
        { label: 'Font Weight', variable: '630' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0.01em' },
      ],
      'color (default)': [
        { label: 'Background Fill', variable: '--theme-fill-object-raised' },
        { label: 'Icon Color', variable: '--theme-icon' },
        { label: 'Text Color', variable: '--theme-text-primary' },
        { label: 'Stroke', variable: '--theme-stroke-tertiary' },
      ],
      'color (hover)': [
        { label: 'Background Fill', variable: '--theme-fill-object-raised-hover' },
        { label: 'Icon Color', variable: '--theme-icon-hover' },
        { label: 'Stroke', variable: '--theme-stroke-secondary' },
      ],
      'color (press)': [
        { label: 'Background Fill', variable: '--theme-fill-object-raised-press' },
        { label: 'Icon Color', variable: '--theme-icon-press' },
        { label: 'Stroke', variable: '--theme-stroke-primary' },
      ],
    },
  },

  'tile-offer': {
    component: () => (
      <TileOffer
        offerName="Special Offer"
        buttonText="Add to Order"
        onClick={() => {}}
      />
    ),
    styles: {
      layout: [
        { label: 'Width', variable: 'w-full' },
        { label: 'Corner Radius', variable: '--layout-radius-lg' },
        { label: 'Padding', variable: 'p-[var(--layout-space-05)]' },
        { label: 'Gap', variable: 'gap-[var(--layout-space-03)]' },
      ],
      'layout (text container)': [
        { label: 'X Padding', variable: 'px-[var(--layout-space-03)]' },
        { label: 'Y Padding', variable: 'py-[var(--layout-space-05)]' },
      ],
      text: [
        { label: 'Text Style', variable: 'text-style-h2-light' },
        { label: 'Font Size', variable: '--layout-font-size-xl' },
        { label: 'Font Weight', variable: '630' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0.01em' },
      ],
      color: [
        { label: 'Background Fill', variable: '--theme-fill-object-raised' },
        { label: 'Text Color', variable: '--theme-text-primary' },
        { label: 'Stroke', variable: '--theme-stroke-tertiary' },
      ],
    },
  },

  'tile-order-history': {
    component: () => (
      <TileOrderHistory
        orderNumber="123456789"
        orderCost="$24.99"
        orderType="Pickup"
        locationName="Downtown"
        orderDate="January 15, 2026 at 12:30 PM"
      />
    ),
    styles: {
      layout: [
        { label: 'Height', variable: 'Hug' },
        { label: 'Width', variable: 'w-full' },
        { label: 'Corner Radius', variable: '--layout-radius-lg' },
        { label: 'Padding', variable: '--layout-space-05' },
        { label: 'Gap', variable: '--layout-space-05' },
      ],
      'layout (text container)': [
        { label: 'X Padding', variable: '--layout-space-02' },
        { label: 'Gap', variable: '--layout-space-02' },
      ],
      'layout (button container)': [
        { label: 'Gap', variable: '--layout-space-04' },
      ],
      'text (order summary)': [
        { label: 'Text Style', variable: 'text-style-h4' },
        { label: 'Font Size', variable: '--layout-font-size-md' },
        { label: 'Font Weight', variable: '630' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0.01em' },
      ],
      'text (order details)': [
        { label: 'Text Style', variable: 'text-style-body-small' },
        { label: 'Font Size', variable: '--layout-font-size-xs' },
        { label: 'Font Weight', variable: '500' },
        { label: 'Line Height', variable: '1.25' },
        { label: 'Letter Spacing', variable: '0em' },
      ],
      'text (order date)': [
        { label: 'Text Style', variable: 'text-style-body-small-bold' },
        { label: 'Font Size', variable: '--layout-font-size-xs' },
        { label: 'Font Weight', variable: '700' },
        { label: 'Line Height', variable: '1.25' },
        { label: 'Letter Spacing', variable: '0em' },
      ],
      color: [
        { label: 'Background Fill', variable: '--theme-fill-object-raised' },
        { label: 'Text Color (summary)', variable: '--theme-text-primary' },
        { label: 'Text Color (details/date)', variable: '--theme-text-secondary' },
        { label: 'Stroke', variable: '--theme-stroke-tertiary' },
      ],
    },
  },

  'radio-button': {
    component: (options, _settings, setOptionValue) => (
      <RadioButton
        selected={options.selected || false}
        value="option1"
        onChange={() => setOptionValue?.('selected', !options.selected)}
      />
    ),
    styles: {
      layout: [
        { label: 'Size', variable: 'w-6 h-6 (24×24)' },
      ],
      color: [
        { label: 'Icon Color', variable: '--theme-icon-radio-checkbox' },
        { label: 'Icon Color (hover)', variable: '--theme-icon-radio-checkbox-hover' },
        { label: 'Icon Color (press)', variable: '--theme-icon-radio-checkbox-press' },
      ],
    },
  },

  'checkbox': {
    component: (options, _settings, setOptionValue) => (
      <Checkbox
        checked={options.checked || false}
        value="checkbox1"
        onChange={() => setOptionValue?.('checked', !options.checked)}
      />
    ),
    styles: {
      layout: [
        { label: 'Size', variable: 'w-6 h-6 (24×24)' },
      ],
      color: [
        { label: 'Icon Color', variable: '--theme-icon-radio-checkbox' },
        { label: 'Icon Color (hover)', variable: '--theme-icon-radio-checkbox-hover' },
        { label: 'Icon Color (press)', variable: '--theme-icon-radio-checkbox-press' },
      ],
    },
  },

  'text-field-label': {
    component: (options) => (
      <TextFieldLabel
        label="Field Label"
        warningText="Missing"
        showWarning={options.warning !== 'hide'}
      />
    ),
    styles: {
      layout: [
        { label: 'Height', variable: 'Hug' },
        { label: 'Width', variable: 'Fill' },
        { label: 'X Padding', variable: '--layout-space-03' },
        { label: 'Y Padding', variable: '--layout-space-02' },
        { label: 'Gap', variable: '--layout-space-02' },
      ],
      text: [
        { label: 'Text Style', variable: 'text-style-label-sm' },
        { label: 'Font Size', variable: '--layout-font-size-xxs' },
        { label: 'Font Weight', variable: '700' },
        { label: 'Line Height', variable: '1.2' },
      ],
      color: [
        { label: 'Text Color', variable: '--theme-text-tertiary' },
        { label: 'Text Color (warning)', variable: '--theme-text-warning' },
      ],
    },
    options: [
      {
        type: 'toggle',
        label: 'Warning',
        choices: [
          { value: 'show', label: 'Show' },
          { value: 'hide', label: 'Hide' },
        ],
        defaultValue: 'show',
      },
    ],
  },

  'text-field-search': {
    component: () => (
      <TextFieldSearch
        value=""
        placeholder="Search..."
        onChange={() => {}}
      />
    ),
    styles: {
      layout: [
        { label: 'Width', variable: 'w-full' },
        { label: 'Max Width', variable: '--layout-width-max-lg' },
        { label: 'Padding Left', variable: '--layout-space-04' },
        { label: 'Padding Right', variable: '--layout-space-05' },
        { label: 'Padding Y', variable: '--layout-padding-text-field-inner-vertical' },
        { label: 'Gap', variable: '--layout-space-03' },
        { label: 'Corner Radius', variable: '--layout-radius-sm' },
      ],
      'layout (icon)': [
        { label: 'Size', variable: 'size-5 (20×20)' },
      ],
      text: [
        { label: 'Text Style', variable: 'text-style-text-field' },
        { label: 'Font Size', variable: '--layout-font-size-md' },
        { label: 'Font Weight', variable: '500' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0.01em' },
      ],
      color: [
        { label: 'Background Fill', variable: '--theme-fill-input' },
        { label: 'Icon Color', variable: '--theme-text-tertiary' },
        { label: 'Text Color', variable: '--theme-text-primary' },
        { label: 'Placeholder Color', variable: '--theme-text-placeholder' },
        { label: 'Stroke', variable: '--theme-stroke-secondary' },
        { label: 'Stroke (focused)', variable: '--theme-stroke-selected' },
      ],
    },
  },

  'address-search-result': {
    component: () => (
      <AddressSearchResult
        addressLine1="123 Main Street"
        addressLine2="New York, NY 10001"
        onPress={() => {}}
      />
    ),
    styles: {
      layout: [
        { label: 'Height', variable: 'Hug' },
        { label: 'Width', variable: 'w-full' },
        { label: 'Corner Radius', variable: '--layout-radius-sm' },
        { label: 'X Padding', variable: '--layout-space-05' },
        { label: 'Y Padding', variable: '--layout-space-03' },
        { label: 'Gap', variable: '--layout-space-03' },
      ],
      'layout (text container)': [
        { label: 'Gap', variable: '--layout-space-01' },
      ],
      text: [
        { label: 'Text Style', variable: 'text-style-body-medium' },
        { label: 'Font Size', variable: '--layout-font-size-sm' },
        { label: 'Font Weight', variable: '500' },
        { label: 'Line Height', variable: '1.25' },
        { label: 'Letter Spacing', variable: '0em' },
      ],
      'color (default)': [
        { label: 'Background Fill', variable: 'transparent' },
        { label: 'Text Color (line 1)', variable: '--theme-text-secondary' },
        { label: 'Text Color (line 2)', variable: '--theme-text-tertiary' },
        { label: 'Icon/Caret', variable: '--theme-icon' },
      ],
      'color (hover/press)': [
        { label: 'Background Fill (hover)', variable: '--theme-fill-object-quaternary-hover' },
        { label: 'Background Fill (press)', variable: '--theme-fill-object-quaternary-press' },
        { label: 'Text Color (line 1)', variable: '--theme-text-primary' },
        { label: 'Text Color (line 2)', variable: '--theme-text-secondary' },
        { label: 'Icon/Caret (hover)', variable: '--theme-icon-hover' },
        { label: 'Icon/Caret (press)', variable: '--theme-icon-press' },
      ],
    },
  },

  'text-block': {
    component: (options) => {
      const labelState = options.fieldLabel || 'show'
      const showLabel = labelState !== 'hide'
      const showWarning = labelState === 'warning'
      return (
        <TextBlock
          value=""
          placeholder="Enter multiple lines of text..."
          label={showLabel ? (showWarning ? { label: 'Text Block Label', warningText: 'This field is required', showWarning: true } : { label: 'Text Block Label' }) : undefined}
          rows={3}
          onChange={() => {}}
        />
      )
    },
    styles: {
      layout: [
        { label: 'Width', variable: 'w-full' },
        { label: 'Gap', variable: '--layout-space-02' },
      ],
      'layout (field)': [
        { label: 'X Padding', variable: '--layout-space-05' },
        { label: 'Y Padding', variable: '--layout-padding-text-field-inner-vertical' },
        { label: 'Corner Radius', variable: '--layout-radius-sm' },
      ],
      text: [
        { label: 'Text Style', variable: 'text-style-text-block' },
        { label: 'Font Size', variable: '--layout-font-size-md' },
        { label: 'Font Weight', variable: '400' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0.01em' },
      ],
      color: [
        { label: 'Background Fill', variable: '--theme-fill-input' },
        { label: 'Text Color', variable: '--theme-text-primary' },
        { label: 'Placeholder Color', variable: '--theme-text-placeholder' },
        { label: 'Stroke', variable: '--theme-stroke-secondary' },
        { label: 'Stroke (focused)', variable: '--theme-stroke-selected' },
      ],
    },
    options: [
      {
        type: 'toggle',
        label: 'Field Label',
        choices: [
          { value: 'hide', label: 'Hide' },
          { value: 'show', label: 'Show' },
          { value: 'warning', label: 'Warning' },
        ],
        defaultValue: 'show',
      },
    ],
  },

  'text-field-credit-card': {
    component: (options, _settings, setOptionValue) => (
      <TextFieldCreditCard
        value={options.cardValue || ''}
        onChange={(rawValue: string) => setOptionValue?.('cardValue', rawValue)}
      />
    ),
    styles: {
      layout: [
        { label: 'Height', variable: 'Hug' },
        { label: 'Width', variable: 'w-full' },
        { label: 'Label Gap', variable: '--layout-space-02' },
      ],
      'layout (field container)': [
        { label: 'X Padding (left)', variable: '--layout-space-05' },
        { label: 'X Padding (right)', variable: '--layout-padding-credit-card-field-right' },
        { label: 'Gap', variable: '--layout-space-03' },
        { label: 'Corner Radius', variable: '--layout-radius-sm' },
      ],
      'layout (card icon)': [
        { label: 'Size', variable: '--layout-icon-size-md' },
      ],
      text: [
        { label: 'Text Style', variable: 'text-style-text-field' },
        { label: 'Font Size', variable: '--layout-font-size-md' },
        { label: 'Font Weight', variable: '500' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0.01em' },
      ],
      color: [
        { label: 'Background Fill', variable: '--theme-fill-input' },
        { label: 'Text Color', variable: '--theme-text-primary' },
        { label: 'Text Color (prefix/suffix)', variable: '--theme-text-tertiary' },
        { label: 'Stroke', variable: '--theme-stroke-secondary' },
        { label: 'Stroke (selected)', variable: '--theme-stroke-selected' },
      ],
    },
  },

  'option-payment-choice': {
    component: (options, _settings, setOptionValue) => (
      <OptionPaymentChoice
        text="Visa ending in 4242"
        paymentType="visa"
        selected={options.selected || false}
        onChange={() => setOptionValue?.('selected', !options.selected)}
      />
    ),
    styles: {
      layout: [
        { label: 'Height', variable: 'Hug' },
        { label: 'Width', variable: 'Fill' },
        { label: 'X Padding', variable: '--layout-space-02' },
        { label: 'Gap', variable: '--layout-space-05' },
      ],
    },
  },

  'badge': {
    component: (options) => (
      <Badge
        variant={options.variant || 'Default'}
      >
        Badge Text
      </Badge>
    ),
    styles: {
      layout: [
        { label: 'Height', variable: '--layout-height-badge' },
        { label: 'Width', variable: 'w-fit' },
        { label: 'Corner Radius', variable: '--layout-radius-xs' },
        { label: 'X Padding', variable: '--layout-space-03' },
      ],
      text: [
        { label: 'Text Style', variable: 'text-style-badge' },
        { label: 'Font Size', variable: '--layout-font-size-xs' },
        { label: 'Font Weight', variable: '700' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0.02em' },
      ],
      'color (default)': [
        { label: 'Background Fill', variable: '--theme-fill-object-secondary' },
        { label: 'Text Color', variable: '--theme-text-object-secondary' },
      ],
      'color (warning)': [
        { label: 'Background Fill', variable: '--theme-fill-object-warning' },
        { label: 'Text Color', variable: '--theme-text-object-warning' },
      ],
    },
    options: [
      {
        type: 'toggle',
        label: 'Variant',
        choices: [
          { value: 'Default', label: 'Default' },
          { value: 'Warning', label: 'Warning' },
        ],
        defaultValue: 'Default',
      },
    ],
  },

  'button-icon-large': {
    component: (options) => (
      <ButtonIconLarge
        variant={options.type || 'Primary'}
        onPress={() => {}}
        accessibilityLabel="Icon button"
      />
    ),
    styles: {
      layout: [
        { label: 'Size', variable: 'w-10 h-10 (40×40)' },
        { label: 'Corner Radius', variable: '--layout-radius-md' },
      ],
      color: [
        { label: 'Background Fill', variable: '--theme-fill-object-{variant}' },
        { label: 'Background Hover', variable: '--theme-fill-object-{variant}-hover' },
        { label: 'Background Press', variable: '--theme-fill-object-{variant}-press' },
        { label: 'Icon Color', variable: '--theme-text-object-{variant}' },
      ],
    },
    options: [
      {
        type: 'buttonGroup',
        label: 'Type',
        choices: BUTTON_VARIANTS,
        defaultValue: 'Primary',
      },
    ],
  },

  'button-icon-small': {
    component: (options) => (
      <ButtonIconSmall
        variant={options.type || 'Primary'}
        onPress={() => {}}
        accessibilityLabel="Icon button"
      />
    ),
    styles: {
      layout: [
        { label: 'Size', variable: 'w-[30px] h-[30px] (30×30)' },
        { label: 'Corner Radius', variable: 'rounded-[15px] (15px)' },
      ],
      color: [
        { label: 'Background Fill', variable: '--theme-fill-object-{variant}' },
        { label: 'Background Hover', variable: '--theme-fill-object-{variant}-hover' },
        { label: 'Background Press', variable: '--theme-fill-object-{variant}-press' },
        { label: 'Icon Color', variable: '--theme-text-object-{variant}' },
      ],
    },
    options: [
      {
        type: 'buttonGroup',
        label: 'Type',
        choices: BUTTON_VARIANTS,
        defaultValue: 'Primary',
      },
    ],
  },

  'button-favorite': {
    component: (options, _settings, setOptionValue) => (
      <ButtonFavorite
        active={options.active || false}
        onPress={() => setOptionValue?.('active', !options.active)}
      />
    ),
    styles: {
      layout: [
        { label: 'Size', variable: 'size-[30px] (30×30)' },
        { label: 'Corner Radius', variable: 'rounded-[15px] (15px)' },
        { label: 'Icon Size', variable: 'size-5 (20×20)' },
      ],
      'color (inactive)': [
        { label: 'Background Fill', variable: 'transparent' },
        { label: 'Background Fill (Hover)', variable: '--theme-fill-object-favorite @ 20%' },
        { label: 'Background Fill (Press)', variable: '--theme-fill-object-favorite @ 40%' },
        { label: 'Icon Color', variable: '--theme-icon-favorite' },
      ],
      'color (active)': [
        { label: 'Background Fill', variable: '--theme-fill-object-favorite' },
        { label: 'Background Fill (Hover)', variable: '--theme-fill-object-favorite @ 80%' },
        { label: 'Background Fill (Press)', variable: '--theme-fill-object-favorite @ 60%' },
        { label: 'Icon Color', variable: '--theme-icon-object-favorite' },
      ],
    },
    options: [
      {
        type: 'toggle' as const,
        label: 'active',
        choices: [
          { value: 'false', label: 'Inactive' },
          { value: 'true', label: 'Active' },
        ],
        defaultValue: 'false',
      },
    ],
  },

  'button-quick-favorites': {
    component: () => (
      <ButtonQuickFavorites onPress={() => {}} />
    ),
    styles: {
      layout: [
        { label: 'Width', variable: 'w-full' },
        { label: 'Min Width', variable: '--layout-width-min-quick-order-button' },
        { label: 'Max Width', variable: '--layout-width-max-xs' },
        { label: 'Corner Radius', variable: '--layout-radius-md' },
        { label: 'Gap', variable: '--layout-space-03' },
        { label: 'Vertical Padding', variable: '--layout-space-04' },
        { label: 'Icon Size', variable: '--layout-icon-size-sm' },
      ],
      text: [
        { label: 'Text Style', variable: 'text-style-button-large-heavy' },
        { label: 'Font Size', variable: '--layout-font-size-lg' },
        { label: 'Font Weight', variable: '700' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0.01em' },
      ],
      'color (default)': [
        { label: 'Background Fill', variable: '--theme-fill-object-favorite' },
        { label: 'Border', variable: '--theme-stroke-object-favorite' },
        { label: 'Text/Icon Color', variable: '--theme-icon-object-favorite' },
      ],
      'color (hover)': [
        { label: 'Background Fill', variable: '--theme-fill-object-favorite-hover' },
        { label: 'Border', variable: '--theme-stroke-object-favorite-hover' },
        { label: 'Shadow', variable: '--effect-shadow-object-hover' },
      ],
      'color (press)': [
        { label: 'Background Fill', variable: '--theme-fill-object-favorite-press' },
        { label: 'Border', variable: '--theme-stroke-object-favorite-press' },
        { label: 'Shadow', variable: '--effect-shadow-object-press' },
      ],
    },
  },

  'button-quick-reorder': {
    component: () => (
      <ButtonQuickReorder onPress={() => {}} />
    ),
    styles: {
      layout: [
        { label: 'Width', variable: 'w-full' },
        { label: 'Min Width', variable: '--layout-width-min-quick-order-button' },
        { label: 'Max Width', variable: '--layout-width-max-xs' },
        { label: 'Corner Radius', variable: '--layout-radius-md' },
        { label: 'Gap', variable: '--layout-space-03' },
        { label: 'Vertical Padding', variable: '--layout-space-04' },
        { label: 'Icon Size', variable: '--layout-icon-size-sm' },
      ],
      text: [
        { label: 'Text Style', variable: 'text-style-button-large-heavy' },
        { label: 'Font Size', variable: '--layout-font-size-lg' },
        { label: 'Font Weight', variable: '700' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0.01em' },
      ],
      'color (default)': [
        { label: 'Background Fill', variable: '--theme-fill-object-elevated' },
        { label: 'Border', variable: '--theme-stroke-tertiary' },
        { label: 'Text/Icon Color', variable: '--theme-text-secondary' },
      ],
      'color (hover)': [
        { label: 'Background Fill', variable: '--theme-fill-object-elevated-hover' },
        { label: 'Border', variable: '--theme-stroke-secondary' },
        { label: 'Shadow', variable: '--effect-shadow-object-hover' },
      ],
      'color (press)': [
        { label: 'Background Fill', variable: '--theme-fill-object-elevated-press' },
        { label: 'Border', variable: '--theme-stroke-primary' },
        { label: 'Shadow', variable: '--effect-shadow-object-press' },
      ],
    },
  },

  'quick-buttons-container': {
    component: (options) => (
      <QuickButtonsContainer
        showFavorites={options.favorites !== 'hide'}
        showReorder={options.reorder !== 'hide'}
        onPressFavorites={() => {}}
        onPressReorder={() => {}}
      />
    ),
    styles: {
      layout: [
        { label: 'Width', variable: 'w-full' },
        { label: 'Max Width', variable: '--layout-width-max-tile-product' },
        { label: 'Gap', variable: '--layout-space-05' },
      ],
    },
    options: [
      {
        type: 'toggle',
        label: 'Favorites',
        choices: [
          { value: 'show', label: 'Show' },
          { value: 'hide', label: 'Hide' },
        ],
        defaultValue: 'show',
      },
      {
        type: 'toggle',
        label: 'Reorder',
        choices: [
          { value: 'show', label: 'Show' },
          { value: 'hide', label: 'Hide' },
        ],
        defaultValue: 'show',
      },
    ],
  },

  // ===== CELLULAR COMPONENTS =====
  'menu-category': {
    component: (options, settings) => {
      const productCount = options.productCount ?? 3
      return (
        <MenuCategory
          categoryName="Popular Items"
          products={sampleProducts.slice(0, productCount).map(p => ({
            ...p,
            imageUrl: `/showcase/placeholders/${settings.restaurant}/${settings.restaurant}-product-tile.png`,
          }))}
          borderSide={settings.imageBorderSide}
          onProductClick={() => {}}
          onCategoryClick={() => {}}
          style={{ width: '100%', maxWidth: 'var(--layout-width-max-menu-category)' }}
        />
      )
    },
    styles: {
      layout: [
        { label: 'Height', variable: 'Hug' },
        { label: 'Width', variable: 'w-full' },
        { label: 'Max Width', variable: '--layout-width-max-menu-category' },
        { label: 'Gap', variable: '--layout-space-04' },
      ],
      'layout (heading container)': [
        { label: 'Y Padding', variable: '--layout-space-02' },
      ],
      'layout (tiles container)': [
        { label: 'Grid', variable: 'auto-fill, minmax(--layout-width-min-tile-product, 1fr)' },
        { label: 'Grid Rows', variable: '1fr' },
        { label: 'Gap', variable: '--layout-space-05' },
      ],
      text: [
        { label: 'Text Style', variable: 'text-style-h2' },
        { label: 'Font Size', variable: '--layout-font-size-xl' },
        { label: 'Font Weight', variable: '700' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0.01em' },
      ],
      color: [
        { label: 'Text Color', variable: '--theme-text-primary' },
      ],
    },
    options: [
      {
        type: 'number',
        label: 'Product Count',
        defaultValue: 3,
        min: 1,
        max: 25,
      },
    ],
  },

  'navigation-desktop': {
    component: (options, settings) => {
      const elements = options.elements || []
      return (
        <div style={{ width: '100%', minWidth: '800px' }}>
          <NavigationDesktop
            selectedTabId="menu"
            onTabChange={() => {}}
            brandLogoUrl={`/showcase/placeholders/${settings.restaurant}/${settings.restaurant}-brandlogo.png`}
            brandName={settings.restaurant}
            checkoutButtonState={elements.includes('checkoutButton') ? 'Visible' : 'Hidden'}
            checkoutTotal="$34.21"
            showActiveOrderButton={elements.includes('statusToast')}
          />
        </div>
      )
    },
    styles: {
      layout: [
        { label: 'Width', variable: 'w-full' },
        { label: 'Gap (toast)', variable: '--layout-space-05' },
        { label: 'Safe Area Top', variable: 'env(safe-area-inset-top, 0px)' },
      ],
      'layout (ButtonsContainer)': [
        { label: 'Max Width', variable: '--layout-width-max-chrome' },
        { label: 'X Padding', variable: '--layout-space-05' },
        { label: 'Y Padding', variable: '--layout-space-03' },
      ],
      'layout (TabsContainer)': [
        { label: 'Max Width', variable: '--layout-width-max-tabs-container' },
      ],
      'layout (CheckoutButtonContainer)': [
        { label: 'Max Width', variable: '--layout-width-max-checkout-button' },
      ],
      'layout (ImageBrandLogo)': [
        { label: 'Size', variable: 'size-[80px] (80×80)' },
      ],
      color: [
        { label: 'Background Fill', variable: 'fill-chrome' },
      ],
      effect: [
        { label: 'Shadow', variable: 'effect-shadow-chrome' },
      ],
    },
    options: [
      {
        type: 'buttons',
        label: 'Elements',
        choices: [
          { value: 'checkoutButton', label: 'Checkout Button' },
          { value: 'statusToast', label: 'Status Toast' },
        ],
        defaultValues: [],
        columns: 2,
      },
    ],
  },

  'toolbar': {
    component: (options) => {
      const categories = [
        { id: 'combos', label: 'Combos' },
        { id: 'entrees', label: 'Entrees' },
        { id: 'sides', label: 'Sides' },
        { id: 'drinks', label: 'Drinks' },
        { id: 'dessert', label: 'Dessert' },
        { id: 'specials', label: 'Specials' },
        { id: 'kids', label: 'Kids Menu' },
        { id: 'seasonal', label: 'Seasonal' },
      ]
      return (
        <Toolbar
          categories={categories}
          selectedCategoryId={options.selected || 'combos'}
          searchActive={options.searchActive === 'true'}
          searchButtonPosition={options.position || 'right'}
          onCategoryPress={(id) => options._setOptionValue?.('selected', id)}
          onSearchPress={() => options._setOptionValue?.('searchActive', 'true')}
          onCancelSearch={() => options._setOptionValue?.('searchActive', 'false')}
        />
      )
    },
    styles: {
      layout: [
        { label: 'Y Padding', variable: 'py-[var(--layout-space-02)]' },
        { label: 'X Padding', variable: 'px-[var(--layout-space-06)]' },
        { label: 'Content Max Width', variable: '--layout-width-max-chrome' },
        { label: 'Category Gap', variable: 'gap-[var(--layout-space-03)]' },
        { label: 'Search Gap / Fade Width', variable: '--layout-space-07' },
      ],
      color: [
        { label: 'Background Fill', variable: '--theme-fill-toolbar' },
      ],
    },
    options: [
      {
        type: 'toggle',
        label: 'Search Active',
        choices: [
          { value: 'false', label: 'Categories' },
          { value: 'true', label: 'Searching' },
        ],
        defaultValue: 'false',
      },
      {
        type: 'toggle',
        label: 'Position',
        choices: [
          { value: 'right', label: 'Right' },
          { value: 'left', label: 'Left' },
        ],
        defaultValue: 'right',
      },
    ],
  },

  'header-menu': {
    component: (options, settings, setOptionValue) => {
      const orderType = options.orderType || 'Pickup'
      const locations = options.locations || 'multiple'
      const showBanner = options.banner !== 'hidden'
      return (
        <div style={{ width: '100%', maxWidth: '800px' }}>
          <HeaderMenu
            locationName="Downtown Location"
            addressLine1="123 Main Street"
            addressLine2="Los Angeles, CA 90001"
            brand={restaurantToBrand[settings.restaurant] || 'FastSushi'}
            showBanner={showBanner}
            selectedOrderType={orderType}
            multipleLocations={locations === 'multiple'}
            onOrderTypeChange={(type: string) => setOptionValue?.('orderType', type)}
            onChangeLocationPress={() => {}}
          />
        </div>
      )
    },
    styles: {
      layout: [
        { label: 'Width', variable: 'w-full' },
      ],
      'layout (location container)': [
        { label: 'X Padding', variable: '--layout-space-06' },
        { label: 'Y Padding', variable: '--layout-space-05' },
      ],
      color: [
        { label: 'Background Fill', variable: 'fill-chrome' },
      ],
      effect: [
        { label: 'Shadow', variable: 'effect-shadow-chrome' },
      ],
    },
    options: [
      {
        type: 'toggle',
        label: 'Banner',
        choices: [
          { value: 'visible', label: 'Visible' },
          { value: 'hidden', label: 'Hidden' },
        ],
        defaultValue: 'visible',
      },
      {
        type: 'toggle',
        label: 'Locations',
        choices: [
          { value: 'single', label: 'Single' },
          { value: 'multiple', label: 'Multiple' },
        ],
        defaultValue: 'multiple',
      },
    ],
  },

  'header-cart': {
    component: (options, _settings, setOptionValue) => {
      const orderType = options.orderType || 'Pickup'
      const locations = options.locations || 'multiple'
      return (
        <div style={{ width: '100%', maxWidth: '800px' }}>
          <HeaderCart
            locationName="Downtown Location"
            addressLine1="123 Main Street"
            addressLine2="Los Angeles, CA 90001"
            selectedOrderType={orderType}
            multipleLocations={locations === 'multiple'}
            onOrderTypeChange={(type: string) => setOptionValue?.('orderType', type)}
            onChangeLocationPress={() => {}}
          />
        </div>
      )
    },
    styles: {
      layout: [
        { label: 'Width', variable: 'w-full' },
        { label: 'Safe Area Padding', variable: 'env(safe-area-inset-top, 0px)' },
      ],
      'layout (heading row)': [
        { label: 'Height', variable: 'h-[56px] (56px)' },
        { label: 'Gap', variable: '--layout-space-05' },
      ],
      'layout (location container)': [
        { label: 'X Padding', variable: '--layout-space-06' },
        { label: 'Bottom Padding', variable: '--layout-space-05' },
      ],
      text: [
        { label: 'Text Style', variable: 'text-style-h1' },
        { label: 'Font Size', variable: '--layout-font-size-xxl' },
        { label: 'Font Weight', variable: '700' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0.01em' },
      ],
      color: [
        { label: 'Background Fill', variable: 'fill-chrome' },
        { label: 'Text / Icon Color', variable: 'text-object-chrome-secondary' },
      ],
      effect: [
        { label: 'Shadow', variable: 'effect-shadow-chrome' },
      ],
    },
    options: [
      {
        type: 'toggle',
        label: 'Locations',
        choices: [
          { value: 'single', label: 'Single' },
          { value: 'multiple', label: 'Multiple' },
        ],
        defaultValue: 'multiple',
      },
    ],
  },

  'header-rewards': {
    component: (options) => {
      const preset = options.points || 'low'
      const presets: Record<string, { points: number; pointsUntilNextLevel: number }> = {
        low: { points: 100, pointsUntilNextLevel: 12 },
        mid: { points: 750, pointsUntilNextLevel: 250 },
        high: { points: 12500, pointsUntilNextLevel: 500 },
      }
      const { points, pointsUntilNextLevel } = presets[preset] ?? { points: 100, pointsUntilNextLevel: 12 }
      const showMessage = options.nextLevelMessage !== 'Hide'
      return (
        <div style={{ width: '100%', maxWidth: '800px' }}>
          <HeaderRewards
            points={points}
            pointsUntilNextLevel={pointsUntilNextLevel}
            showNextLevelMessage={showMessage}
          />
        </div>
      )
    },
    styles: {
      layout: [
        { label: 'Width', variable: 'w-full' },
        { label: 'Safe Area Padding', variable: 'env(safe-area-inset-top, 0px)' },
      ],
      color: [
        { label: 'Background Fill', variable: 'fill-chrome' },
        { label: 'Text Color', variable: 'text-object-chrome-secondary' },
      ],
      effect: [
        { label: 'Shadow', variable: 'effect-shadow-chrome' },
      ],
    },
    options: [
      {
        type: 'toggle',
        label: 'Points',
        choices: [
          { value: 'low', label: '100' },
          { value: 'mid', label: '750' },
          { value: 'high', label: '12,500' },
        ],
        defaultValue: 'low',
      },
      {
        type: 'toggle',
        label: 'Next Level Message',
        choices: [
          { value: 'Show', label: 'Show' },
          { value: 'Hide', label: 'Hide' },
        ],
        defaultValue: 'Show',
      },
    ],
  },

  'header-other': {
    component: (options) => {
      const showBack = options.backButton !== 'Hide'
      return (
        <div style={{ width: '100%', maxWidth: '800px' }}>
          <HeaderOther
            pageName="Account Settings"
            showBackButton={showBack}
            onBackPress={showBack ? () => {} : undefined}
          />
        </div>
      )
    },
    styles: {
      layout: [
        { label: 'Width', variable: 'w-full' },
        { label: 'Height', variable: 'h-[56px] (56px)' },
        { label: 'X Padding', variable: '--layout-space-03' },
        { label: 'Safe Area Padding', variable: 'env(safe-area-inset-top, 0px)' },
      ],
      text: [
        { label: 'Text Style', variable: 'text-style-h1' },
        { label: 'Font Size', variable: '--layout-font-size-xxl' },
        { label: 'Font Weight', variable: '700' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0.01em' },
      ],
      color: [
        { label: 'Background Fill', variable: 'fill-chrome' },
        { label: 'Text Color', variable: 'text-object-chrome-secondary' },
        { label: 'Back Button Fill', variable: 'fill-object-chrome-primary' },
        { label: 'Back Button Icon', variable: 'text-object-chrome-primary' },
      ],
      effect: [
        { label: 'Shadow', variable: 'effect-shadow-chrome' },
      ],
    },
    options: [
      {
        type: 'toggle',
        label: 'Back Button',
        choices: [
          { value: 'Hide', label: 'Hide' },
          { value: 'Show', label: 'Show' },
        ],
        defaultValue: 'Show',
      },
    ],
  },

  'location-order-type': {
    component: (options, _settings, setOptionValue) => {
      const orderType = options.orderType || 'Pickup'
      return (
        <LocationOrderType
          location={{
            name: 'Downtown Location',
            addressLine1: '123 Main Street',
            addressLine2: 'New York, NY 10001',
          }}
          selectedOrderType={orderType}
          multipleLocations={options.locations !== 'single'}
          onOrderTypeChange={(type: string) => setOptionValue?.('orderType', type)}
          onChangeLocationPress={() => {}}
        />
      )
    },
    styles: {
      layout: [
        { label: 'Width', variable: 'w-full' },
        { label: 'Gap (root / controls)', variable: '--layout-space-04' },
        { label: 'Gap (text details)', variable: '--layout-space-02' },
        { label: 'Gap (name row)', variable: '--layout-space-03' },
      ],
      'text (location name)': [
        { label: 'Text Style', variable: 'text-style-h2-light' },
        { label: 'Font Size', variable: '--layout-font-size-xl' },
        { label: 'Font Weight', variable: '630' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0.01em' },
      ],
      'text (address)': [
        { label: 'Text Style', variable: 'text-style-body-medium' },
        { label: 'Font Size', variable: '--layout-font-size-sm' },
        { label: 'Font Weight', variable: '500' },
        { label: 'Line Height', variable: '1.25' },
        { label: 'Letter Spacing', variable: '0em' },
      ],
      color: [
        { label: 'Text Color', variable: '--theme-text-object-chrome-secondary' },
      ],
    },
    options: [
      {
        type: 'toggle',
        label: 'Locations',
        choices: [
          { value: 'single', label: 'Single' },
          { value: 'multiple', label: 'Multiple' },
        ],
        defaultValue: 'multiple',
      },
    ],
  },

  'footer-desktop': {
    component: (options, _settings, setOptionValue) => {
      const orderType = options.orderType || 'Pickup'
      return (
        <div style={{ width: '100%', minWidth: '800px' }}>
          <FooterDesktop
            location={{
              name: 'Downtown Location',
              addressLine1: '123 Main Street',
              addressLine2: 'Los Angeles, CA 90001',
            }}
            selectedOrderType={orderType}
            multipleLocations={options.locations !== 'single'}
            onOrderTypeChange={(type: string) => setOptionValue?.('orderType', type)}
            onChangeLocationPress={() => {}}
          />
        </div>
      )
    },
    styles: {
      layout: [
        { label: 'Width', variable: 'w-full' },
        { label: 'Max Width (content)', variable: '--layout-width-max-chrome' },
        { label: 'X Padding', variable: '--layout-space-05' },
        { label: 'Top Padding', variable: '--layout-space-03' },
        { label: 'Bottom Padding', variable: 'max(--layout-space-03, safe-area-inset-bottom)' },
        { label: 'Gap', variable: '--layout-space-04' },
      ],
      color: [
        { label: 'Background Fill', variable: '--theme-fill-chrome' },
        { label: 'Shadow', variable: '--effect-shadow-chrome' },
      ],
    },
    options: [
      {
        type: 'toggle',
        label: 'Locations',
        choices: [
          { value: 'single', label: 'Single' },
          { value: 'multiple', label: 'Multiple' },
        ],
        defaultValue: 'multiple',
      },
    ],
  },

  'tab': {
    component: (options) => {
      const pageState = options.pageState || 'unselected'
      return (
        <Tab
          label="Menu"
          iconName="menu"
          selected={pageState === 'selected'}
          onClick={() => {}}
        />
      )
    },
    styles: {
      layout: [
        { label: 'Width', variable: 'w-full' },
        { label: 'Max Width', variable: 'max-w-[110px] (110px)' },
        { label: 'Height', variable: '--layout-height-tab' },
        { label: 'Corner Radius', variable: '--layout-radius-tab' },
        { label: 'X Padding', variable: '--layout-space-05' },
        { label: 'Gap', variable: '--layout-gap-tab' },
        { label: 'Icon Size', variable: 'size-8 (32×32)' },
      ],
      text: [
        { label: 'Text Style', variable: 'text-style-tab' },
        { label: 'Font Size', variable: '--layout-font-size-xxs' },
        { label: 'Font Weight', variable: '700' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0em' },
      ],
      'color (selected)': [
        { label: 'Background Fill', variable: '--theme-fill-object-chrome-primary' },
        { label: 'Hover Fill', variable: '--theme-fill-object-chrome-primary-hover' },
        { label: 'Press Fill', variable: '--theme-fill-object-chrome-primary-press' },
        { label: 'Text Color', variable: '--theme-text-object-chrome-primary' },
        { label: 'Icon Color', variable: '--theme-icon-object-chrome-primary' },
        { label: 'Hover Shadow', variable: '--effect-shadow-object-chrome-hover' },
        { label: 'Press Shadow', variable: '--effect-shadow-object-chrome-press' },
      ],
      'color (unselected)': [
        { label: 'Background Fill', variable: '--theme-fill-object-chrome-secondary' },
        { label: 'Hover Fill', variable: '--theme-fill-object-chrome-secondary-hover' },
        { label: 'Press Fill', variable: '--theme-fill-object-chrome-secondary-press' },
        { label: 'Text Color', variable: '--theme-text-object-chrome-secondary' },
        { label: 'Icon Color', variable: '--theme-icon-object-chrome-secondary' },
      ],
    },
    options: [
      {
        type: 'toggle',
        label: 'Page State',
        choices: [
          { value: 'unselected', label: 'Unselected' },
          { value: 'selected', label: 'Selected' },
        ],
        defaultValue: 'unselected',
      },
    ],
  },

  'tabs-container': {
    component: () => (
      <div style={{ width: '100%', maxWidth: '400px' }}>
        <TabsContainer
          tabs={sampleTabs}
          selectedTabId="menu"
          onTabChange={() => {}}
        />
      </div>
    ),
    styles: {
      layout: [
        { label: 'Height', variable: 'Hug' },
        { label: 'Width', variable: 'w-full' },
        { label: 'Max Width', variable: '--layout-width-max-tabs-container' },
        { label: 'X Padding', variable: '--layout-padding-tab-container-horizontal' },
        { label: 'Gap', variable: '--layout-gap-tab-container' },
      ],
    },
  },

  'navigation-mobile': {
    component: (options) => {
      const elements = options.elements || []
      return (
        <div style={{ width: '100%', maxWidth: '800px', height: '200px' }}>
          <NavigationMobile
            selectedTabId="menu"
            onTabChange={() => {}}
            checkoutButtonState={elements.includes('checkoutButton') ? 'Visible' : 'Hidden'}
            showToastOrderStatus={elements.includes('statusToast')}
            orderTotal="$34.21"
            orderStatus={{
              title: 'Out For Delivery',
              description: 'Estimated arrival 12:34-12:39 PM',
            }}
            onCheckoutPress={() => {}}
            onOrderStatusPress={() => {}}
          />
        </div>
      )
    },
    styles: {
      'layout (root)': [
        { label: 'Width', variable: 'w-full' },
        { label: 'Gap', variable: '--layout-space-06' },
      ],
      'layout (toast container)': [
        { label: 'Horizontal Padding', variable: '--layout-space-06' },
      ],
      'layout (navigation container)': [
        { label: 'Top Padding', variable: '--layout-space-06' },
        { label: 'Horizontal Padding', variable: '--layout-space-06' },
        { label: 'Bottom Padding', variable: 'max(--layout-space-06, env(safe-area-inset-bottom, 0px))' },
        { label: 'Gap', variable: '--layout-space-06' },
        { label: 'Corner Radius (checkout visible)', variable: 'rounded-tl-[34px] rounded-tr-[34px] (34px)' },
      ],
      'layout (checkout wrapper)': [
        { label: 'Max Width', variable: '--layout-width-max-checkout-button' },
        { label: 'Height', variable: '--layout-height-button-checkout' },
      ],
      'layout (tabs wrapper)': [
        { label: 'Max Width', variable: '--layout-width-max-tabs-container' },
      ],
      color: [
        { label: 'Background Fill', variable: '--theme-fill-chrome' },
      ],
      effect: [
        { label: 'Shadow', variable: '--effect-shadow-chrome' },
      ],
    },
    options: [
      {
        type: 'buttons',
        label: 'Elements',
        choices: [
          { value: 'checkoutButton', label: 'Checkout Button' },
          { value: 'statusToast', label: 'Status Toast' },
        ],
        defaultValues: [],
        columns: 2,
      },
    ],
  },

  // ===== IMAGE COMPONENTS =====
  'image-product-button': {
    component: (_options, settings) => (
      <div className="w-full h-full" style={{ aspectRatio: '1 / 1' }}>
        <ImageProductButton
          imageUrl={`/showcase/placeholders/${settings.restaurant}/${settings.restaurant}-product-button.png`}
          accessibilityLabel="Product button image"
        />
      </div>
    ),
    styles: {
      layout: [
        { label: 'Height', variable: 'h-full' },
        { label: 'Width', variable: 'w-full' },
      ],
      color: [
        { label: 'Background Fill', variable: '--theme-fill-image' },
      ],
    },
  },

  'image-product-tile': {
    component: (_options, settings) => (
      <div className="w-full h-full" style={{ aspectRatio: '1 / 1' }}>
        <ImageProductTile
          imageUrl={`/showcase/placeholders/${settings.restaurant}/${settings.restaurant}-product-tile.png`}
          accessibilityLabel="Product tile image"
        />
      </div>
    ),
    styles: {
      layout: [
        { label: 'Height', variable: 'h-full' },
        { label: 'Width', variable: 'w-full' },
      ],
      color: [
        { label: 'Background Fill', variable: '--theme-fill-image' },
      ],
    },
  },

  'image-reward': {
    component: (_options, settings) => (
      <div className="w-full h-full" style={{ aspectRatio: '1 / 1' }}>
        <ImageReward
          imageUrl={`/showcase/placeholders/${settings.restaurant}/${settings.restaurant}-reward.png`}
          accessibilityLabel="Reward image"
          rewardType="star"
          isEarned={true}
        />
      </div>
    ),
    styles: {
      layout: [
        { label: 'Height', variable: 'h-full' },
        { label: 'Width', variable: 'w-full' },
      ],
      color: [
        { label: 'Background Fill', variable: '--theme-fill-image' },
      ],
    },
  },

  'image-product-sheet': {
    component: (_options, settings) => (
      <div className="w-full h-full" style={{ aspectRatio: '1 / 1' }}>
        <ImageProductSheet
          imageUrl={`/showcase/placeholders/${settings.restaurant}/${settings.restaurant}-product-sheet.png`}
          accessibilityLabel="Product sheet image"
        />
      </div>
    ),
    styles: {
      layout: [
        { label: 'Height', variable: 'h-full' },
        { label: 'Width', variable: 'w-full' },
        { label: 'Corner Radius', variable: '--layout-radius-md' },
      ],
      color: [
        { label: 'Background Fill', variable: '--theme-fill-image' },
        { label: 'Border Color', variable: '--theme-stroke-quaternary' },
        { label: 'Border Style', variable: 'border' },
      ],
    },
  },

  'image-offer': {
    component: (_options, settings) => (
      <div style={{ width: '100%', maxWidth: '100%', maxHeight: '100%', aspectRatio: '1 / 1' }}>
        <ImageOffer
          imageUrl={`/showcase/placeholders/${settings.restaurant}/${settings.restaurant}-offer.png`}
          accessibilityLabel="Offer banner image"
        />
      </div>
    ),
    styles: {
      layout: [
        { label: 'Height', variable: 'h-full' },
        { label: 'Width', variable: 'w-full' },
      ],
      color: [
        { label: 'Background Fill', variable: '--theme-fill-image' },
      ],
    },
  },

  'image-banner': {
    component: (_options, settings) => (
      <div style={{ width: '100%' }}>
        <ImageBanner
          key={settings.restaurant}
          imageUrl={`/showcase/placeholders/${settings.restaurant}/${settings.restaurant}-banner.png`}
          accessibilityLabel="Banner image"
          bannerType="hero"
        />
      </div>
    ),
    styles: {
      layout: [
        { label: 'Height', variable: 'h-auto' },
        { label: 'Width', variable: 'w-full' },
      ],
    },
  },

  'image-brand-logo': {
    component: (options, settings) => {
      const useAltImage = options.imageType === 'Alternate'
      return (
        <div style={{ width: '100%', maxWidth: '100%', maxHeight: '100%', aspectRatio: '1 / 1' }}>
          <ImageBrandLogo
            imageUrl={useAltImage
              ? `/showcase/placeholders/${settings.restaurant}/${settings.restaurant}-brandlogo-alt.png`
              : `/showcase/placeholders/${settings.restaurant}/${settings.restaurant}-brandlogo.png`
            }
            accessibilityLabel="Brand logo"
            brand={restaurantToBrand[settings.restaurant] || 'FastSushi'}
          />
        </div>
      )
    },
    styles: {
      layout: [
        { label: 'Height', variable: 'h-full' },
        { label: 'Width', variable: 'w-full' },
      ],
    },
    options: [
      {
        type: 'toggle',
        label: 'Image Type',
        choices: [
          { value: 'Default', label: 'Default' },
          { value: 'Alternate', label: 'Alternate' },
        ],
        defaultValue: 'Default',
      },
    ],
  },

  // ===== MISC COMPONENTS =====
  'toast': {
    component: (options) => (
      <Toast
        title={options.type === 'orderStatus' ? 'Order Status' : 'Notification'}
        description={options.type === 'orderStatus' ? 'Your order is being prepared' : 'You have a new message'}
        onPress={() => {}}
      />
    ),
    styles: {
      layout: [
        { label: 'Height', variable: 'Hug' },
        { label: 'Width', variable: 'w-full' },
        { label: 'Max Width', variable: '--layout-width-max-sm' },
        { label: 'Corner Radius', variable: '--layout-radius-md' },
        { label: 'X Padding (left)', variable: '--layout-space-06' },
        { label: 'X Padding (right)', variable: '--layout-space-05' },
        { label: 'Y Padding', variable: '--layout-space-05' },
        { label: 'Gap', variable: '--layout-space-06' },
        { label: 'Gap (text container)', variable: '--layout-space-02' },
      ],
      text: [
        { label: 'Text Style (title)', variable: 'text-style-h5' },
        { label: 'Text Style (description)', variable: 'text-style-body-small' },
      ],
      color: [
        { label: 'Background Fill', variable: '--theme-fill-object-elevated' },
        { label: 'Background Fill (hover)', variable: '--theme-fill-object-elevated-hover' },
        { label: 'Background Fill (press)', variable: '--theme-fill-object-elevated-press' },
        { label: 'Stroke', variable: '--theme-stroke-secondary' },
        { label: 'Stroke (hover/press)', variable: '--theme-stroke-primary' },
        { label: 'Icon/Caret', variable: '--theme-icon' },
        { label: 'Icon/Caret (hover)', variable: '--theme-icon-hover' },
        { label: 'Icon/Caret (press)', variable: '--theme-icon-press' },
        { label: 'Text Color (title)', variable: '--theme-text-primary' },
        { label: 'Text Color (description)', variable: '--theme-text-tertiary' },
        { label: 'Shadow', variable: '--effect-shadow-chrome' },
      ],
    },
    options: [
      {
        type: 'toggle',
        label: 'Type',
        choices: [
          { value: 'normal', label: 'Normal' },
          { value: 'orderStatus', label: 'Order Status' },
        ],
        defaultValue: 'normal',
      },
    ],
  },

  'discount-banner': {
    component: (options) => (
      <DiscountBanner
        mode={options.mode === 'offer' ? 'offer' : 'reward'}
        title={options.mode === 'offer' ? 'Buy One Get One Free' : '$5 Discount'}
        description={options.mode === 'offer' ? 'Offer applied to this order' : 'Reward applied to this order'}
        onPress={() => {}}
      />
    ),
    styles: {
      layout: [
        { label: 'Height', variable: 'Hug' },
        { label: 'Width', variable: 'Fill' },
        { label: 'Corner Radius', variable: '--layout-radius-md' },
        { label: 'X Padding (left)', variable: '--layout-space-06' },
        { label: 'X Padding (right)', variable: '--layout-space-05' },
        { label: 'Y Padding', variable: '--layout-space-05' },
        { label: 'Gap', variable: '--layout-space-06' },
        { label: 'Gap (text container)', variable: '--layout-space-02' },
      ],
      text: [
        { label: 'Text Style (title)', variable: 'text-style-h5' },
        { label: 'Text Style (description)', variable: 'text-style-body-small' },
      ],
      color: [
        { label: 'Background Fill', variable: '--theme-fill-object-elevated' },
        { label: 'Background Fill (hover)', variable: '--theme-fill-object-elevated-hover' },
        { label: 'Background Fill (press)', variable: '--theme-fill-object-elevated-press' },
        { label: 'Stroke', variable: '--theme-stroke-secondary' },
        { label: 'Stroke (hover/press)', variable: '--theme-stroke-primary' },
        { label: 'Icon/Caret', variable: '--theme-icon' },
        { label: 'Icon/Caret (hover)', variable: '--theme-icon-hover' },
        { label: 'Icon/Caret (press)', variable: '--theme-icon-press' },
        { label: 'Text Color (title)', variable: '--theme-text-primary' },
        { label: 'Text Color (description)', variable: '--theme-text-tertiary' },
        { label: 'Shadow (hover)', variable: '--effect-shadow-object-hover' },
        { label: 'Shadow (press)', variable: '--effect-shadow-object-press' },
      ],
    },
    options: [
      {
        type: 'toggle',
        label: 'Mode',
        choices: [
          { value: 'reward', label: 'Reward' },
          { value: 'offer', label: 'Offer' },
        ],
        defaultValue: 'reward',
      },
    ],
  },

  'discount-banner-container': {
    component: (options) => (
      <DiscountBannerContainer
        showOffer={options.offer !== 'hide'}
        showReward={options.reward !== 'hide'}
        onPressOffer={() => {}}
        onPressReward={() => {}}
      />
    ),
    styles: {
      layout: [
        { label: 'Height', variable: 'Hug' },
        { label: 'Width', variable: 'w-full' },
        { label: 'Max Width', variable: '--layout-width-max-sm' },
        { label: 'Gap', variable: '--layout-space-03' },
      ],
    },
    options: [
      {
        type: 'toggle',
        label: 'Offer',
        choices: [
          { value: 'show', label: 'Show' },
          { value: 'hide', label: 'Hide' },
        ],
        defaultValue: 'show',
      },
      {
        type: 'toggle',
        label: 'Reward',
        choices: [
          { value: 'show', label: 'Show' },
          { value: 'hide', label: 'Hide' },
        ],
        defaultValue: 'show',
      },
    ],
  },

  'gauge-circular': {
    component: (options) => (
      <div
        style={{
          background: 'var(--theme-fill-chrome-background, #F9FAFB)',
          padding: 24,
          display: 'flex',
          justifyContent: 'center',
          width: '100%',
          height: '100%',
        }}
      >
        <GaugeCircular
          value={Number(options.fillPercent) || 0}
          size={320}
        />
      </div>
    ),
    styles: {
      layout: [
        { label: 'Width', variable: '320px (default)' },
        { label: 'Stroke Width', variable: '24px (default)' },
        { label: 'Arc Angle', variable: '235°' },
      ],
      color: [
        { label: 'Background', variable: '--theme-fill-chrome-background' },
        { label: 'Fill Arc', variable: '--theme-fill-chrome-focused' },
        { label: 'Track Arc', variable: '--theme-fill-toggle' },
      ],
    },
    options: [
      {
        type: 'buttonGroup',
        label: 'Fill Percent',
        choices: [
          { value: '0', label: '0%' },
          { value: '25', label: '25%' },
          { value: '50', label: '50%' },
          { value: '75', label: '75%' },
          { value: '100', label: '100%' },
        ],
        defaultValue: '50',
      },
    ],
  },

  'gauge-linear': {
    component: (options) => (
      <div
        style={{
          background: 'var(--theme-fill-chrome-background, #F9FAFB)',
          padding: 24,
          display: 'flex',
          justifyContent: 'center',
          width: '100%',
          height: '100%',
        }}
      >
        <GaugeLinear
          value={Number(options.fillPercent) || 0}
        />
      </div>
    ),
    styles: {
      layout: [
        { label: 'Max Width', variable: '--layout-width-max-03' },
        { label: 'Stroke Width', variable: '20px (default)' },
      ],
      color: [
        { label: 'Background', variable: '--theme-fill-chrome-background' },
        { label: 'Fill Bar', variable: '--theme-fill-chrome-focused' },
        { label: 'Track Bar', variable: '--theme-fill-toggle' },
      ],
    },
    options: [
      {
        type: 'buttonGroup',
        label: 'Fill Percent',
        choices: [
          { value: '0', label: '0%' },
          { value: '25', label: '25%' },
          { value: '50', label: '50%' },
          { value: '75', label: '75%' },
          { value: '100', label: '100%' },
        ],
        defaultValue: '50',
      },
    ],
  },

  'divider': {
    component: () => (
      <Divider />
    ),
    styles: {
      layout: [
        { label: 'Height (line)', variable: '--layout-height-divider' },
        { label: 'Width', variable: 'w-full' },
        { label: 'Y Padding', variable: 'py-[var(--layout-space-02)]' },
      ],
      color: [
        { label: 'Line Color', variable: '--theme-stroke-tertiary' },
      ],
    },
  },

  'required-indicator': {
    component: () => (
      <RequiredIndicator
        requiredItemDescription="Required Item - 2 of 3"
      />
    ),
    styles: {
      layout: [
        { label: 'Height', variable: 'h-10 (40px)' },
        { label: 'Width', variable: 'w-fit' },
        { label: 'Corner Radius', variable: '--layout-radius-md' },
        { label: 'X Padding', variable: '--layout-space-09' },
      ],
      text: [
        { label: 'Text Style', variable: 'text-style-label-lg' },
        { label: 'Font Size', variable: '--layout-font-size-sm' },
        { label: 'Font Weight', variable: '700' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0.01em' },
      ],
      color: [
        { label: 'Background Fill', variable: '--theme-fill-object-raised' },
        { label: 'Text Color', variable: '--theme-text-secondary' },
      ],
    },
  },

  'order-status': {
    component: (options) => (
      <OrderStatus
        orderStatus={options.orderStatus || 'Order Placed'}
      />
    ),
    styles: {
      layout: [
        { label: 'Layout', variable: 'flex col, gap: --layout-space-02' },
        { label: 'Icons Row', variable: '310px × 48px (relative)' },
        { label: 'Text Row', variable: '310px, justify-between, 3 × 110px slots' },
      ],
    },
    options: [
      {
        type: 'buttonGroup',
        label: 'Order Status',
        choices: [
          { value: 'Order Placed', label: 'Order Placed' },
          { value: 'InProgress', label: 'In Progress' },
          { value: 'OnTheWay', label: 'On the Way' },
          { value: 'Delivered', label: 'Delivered' },
          { value: 'Ready for Pickup', label: 'Ready for Pickup' },
          { value: 'Cancelled', label: 'Cancelled' },
          { value: 'Refunded Delivery', label: 'Delivery Refund' },
          { value: 'Refunded Pickup', label: 'Pickup Refund' },
        ],
        defaultValue: 'Order Placed',
        columns: 2,
      },
    ],
  },

  'map-icon': {
    component: (options) => (
      <MapIcon
        object={options.object || 'pin'}
        state={options.state || 'active'}
      />
    ),
    styles: {
      layout: [
        { label: 'Container', variable: 'size-[48px] (48×48)' },
        { label: 'Circle', variable: 'size-[40px] (40×40)' },
        { label: 'Border Radius', variable: 'rounded-full (50%)' },
        { label: 'Icon', variable: 'size-8 (32×32)' },
      ],
      'color (active)': [
        { label: 'Background Fill', variable: '--theme-fill-object-secondary' },
        { label: 'Icon Color', variable: '--theme-text-object-secondary' },
      ],
      'color (inactive)': [
        { label: 'Background Fill', variable: '--theme-fill-object-disabled' },
        { label: 'Icon Color', variable: '--theme-text-object-disabled' },
      ],
    },
    options: [
      {
        type: 'buttonGroup',
        label: 'Object',
        choices: [
          { value: 'pin', label: 'Pin' },
          { value: 'delivery-driver', label: 'Driver' },
        ],
        defaultValue: 'pin',
        columns: 2,
      },
      {
        type: 'toggle',
        label: 'State',
        choices: [
          { value: 'active', label: 'Active' },
          { value: 'inactive', label: 'Inactive' },
        ],
        defaultValue: 'active',
      },
    ],
  },

  'order-status-icon': {
    component: (options) => (
      <OrderStatusIcon
        status={options.status || 'in-progress'}
        state={options.state || 'complete'}
      />
    ),
    styles: {
      layout: [
        { label: 'Container', variable: 'size-[48px] (48×48)' },
        { label: 'Circle', variable: 'size-[40px] (40×40)' },
        { label: 'Border Radius', variable: 'rounded-full (50%)' },
        { label: 'Icon', variable: 'size-8 (32×32)' },
      ],
      'color (complete)': [
        { label: 'Background Fill', variable: '--theme-fill-object-secondary' },
        { label: 'Icon Color', variable: '--theme-text-object-secondary' },
      ],
      'color (pending)': [
        { label: 'Background Fill', variable: '--theme-fill-object-disabled' },
        { label: 'Icon Color', variable: '--theme-text-object-disabled' },
      ],
    },
    options: [
      {
        type: 'buttonGroup',
        label: 'Status',
        choices: [
          { value: 'order-placed', label: 'Placed' },
          { value: 'in-progress', label: 'In Progress' },
          { value: 'out-for-delivery', label: 'Delivery' },
          { value: 'order-complete', label: 'Complete' },
          { value: 'order-cancelled', label: 'Cancelled' },
          { value: 'refunded', label: 'Refunded' },
        ],
        defaultValue: 'in-progress',
        columns: 3,
      },
      {
        type: 'toggle',
        label: 'State',
        choices: [
          { value: 'complete', label: 'Complete' },
          { value: 'pending', label: 'Pending' },
        ],
        defaultValue: 'complete',
      },
    ],
  },

  'order-status-progress-bar': {
    component: (options) => (
      <div style={{ display: 'flex', justifyContent: 'center', width: '100%', maxWidth: '400px' }}>
        <OrderStatusProgressBar status={options.state || 'complete'} />
      </div>
    ),
    styles: {
      layout: [
        { label: 'Width', variable: 'w-[70px] (70px)' },
        { label: 'Height', variable: 'h-[12px] (12px)' },
      ],
      'color (complete)': [
        { label: 'Fill', variable: '--theme-fill-object-tertiary' },
      ],
      'color (pending)': [
        { label: 'Fill', variable: '--theme-fill-object-disabled' },
      ],
    },
    options: [
      {
        type: 'toggle',
        label: 'State',
        choices: [
          { value: 'complete', label: 'Complete' },
          { value: 'pending', label: 'Pending' },
        ],
        defaultValue: 'complete',
      },
    ],
  },

  'text-order-status': {
    component: () => (
      <TextOrderStatus>Preparing</TextOrderStatus>
    ),
    styles: {
      text: [
        { label: 'Text Style', variable: 'text-style-order-status' },
        { label: 'Font Size', variable: '--layout-font-size-xs' },
        { label: 'Font Weight', variable: '700' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0em' },
      ],
      color: [
        { label: 'Text Color', variable: '--theme-text-primary' },
      ],
    },
  },

  // ===== CONTAINERS =====
  'account-page-navigation': {
    component: () => (
      <AccountPageNavigation
        navItems={[
          { id: 'manage-account', pageTitle: 'Manage Account', iconName: 'accountSettings' },
          { id: 'notifications', pageTitle: 'Notifications', iconName: 'bell' },
          { id: 'favorites-list', pageTitle: 'Favorites List', iconName: 'favoritesList' },
          { id: 'order-history', pageTitle: 'Order History', iconName: 'history' },
          { id: 'my-qr-code', pageTitle: 'My QR Code', iconName: 'qr' },
          { id: 'help-feedback', pageTitle: 'Help and Feedback', iconName: 'help' },
        ]}
        style={{ width: '100%' }}
      />
    ),
    styles: {
      layout: [
        { label: 'Width', variable: 'w-full' },
        { label: 'Max Width', variable: '--layout-width-max-xl' },
        { label: 'Grid Min Column', variable: '--layout-width-min-tile-page' },
        { label: 'Gap', variable: '--layout-space-07' },
      ],
    },
  },

  'help-page-navigation': {
    component: () => (
      <HelpPageNavigation
        navItems={[
          { id: 'order-issue', pageTitle: 'Order Issue', iconName: 'orderIssue' },
          { id: 'app-issue', pageTitle: 'App Issue', iconName: 'questionApp' },
          { id: 'feedback', pageTitle: 'Feedback', iconName: 'lightbulb' },
          { id: 'call-restaurant', pageTitle: 'Call Restaurant', iconName: 'callOut' },
        ]}
        style={{ width: '100%' }}
      />
    ),
    styles: {
      layout: [
        { label: 'Width', variable: 'w-full' },
        { label: 'Max Width', variable: '--layout-width-max-xl' },
        { label: 'Grid Min Column', variable: '--layout-width-min-tile-page' },
        { label: 'Gap', variable: '--layout-space-07' },
      ],
    },
  },

  'product-choices': {
    component: (_options, settings) => (
      <ProductChoices
        products={sampleProductChoices.map(p => ({
          ...p,
          imageUrl: `/showcase/placeholders/${settings.restaurant}/${settings.restaurant}-product-button.png`,
        }))}
        onProductPress={() => {}}
        style={{ width: '100%' }}
      />
    ),
    styles: {
      layout: [
        { label: 'Height', variable: 'Hug' },
        { label: 'Width', variable: 'w-full' },
        { label: 'Max Width', variable: '--layout-width-max-lg' },
        { label: 'Grid', variable: '1fr / auto-rows: 1fr' },
        { label: 'Border Radius', variable: '--layout-radius-md' },
        { label: 'Gap', variable: '--layout-space-04' },
      ],
    },
  },

  'offer-group': {
    component: (options, settings) => (
      <OfferGroup
        groupName="Active Offers"
        offers={sampleOffers.slice(0, options.offersCount || 3).map(o => ({
          ...o,
          imageUrl: `/showcase/placeholders/${settings.restaurant}/${settings.restaurant}-offer.png`,
        }))}
        onOfferClick={() => {}}
        style={{ width: '100%', maxWidth: 'var(--layout-width-max-lg)' }}
      />
    ),
    styles: {
      layout: [
        { label: 'Height', variable: 'Hug' },
        { label: 'Width', variable: 'w-full' },
        { label: 'Max Width', variable: '--layout-width-max-lg' },
        { label: 'Gap', variable: '--layout-space-07' },
      ],
      'layout (heading container)': [
        { label: 'Y Padding', variable: '--layout-space-02' },
      ],
      'layout (tiles container)': [
        { label: 'Gap', variable: '--layout-space-05' },
      ],
      text: [
        { label: 'Text Style', variable: 'text-style-h2' },
        { label: 'Font Size', variable: '--layout-font-size-xl' },
        { label: 'Font Weight', variable: '700' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0.01em' },
      ],
      color: [
        { label: 'Text Color', variable: '--theme-text-primary' },
      ],
    },
    options: [
      {
        type: 'number',
        label: 'Offers Count',
        defaultValue: 3,
        min: 1,
        max: 25,
      },
    ],
  },

  'order-history': {
    component: (options) => (
      <SectionOrderHistory
        groupName="Order History"
        orders={sampleOrders.slice(0, options.orderCount ?? 3)}
        onOrderClick={() => {}}
      />
    ),
    styles: {
      layout: [
        { label: 'Width', variable: 'w-full' },
        { label: 'Max Width', variable: '--layout-width-max-lg' },
        { label: 'Centering', variable: 'mx-auto' },
        { label: 'Gap', variable: '--layout-space-07' },
      ],
      'layout (title container)': [
        { label: 'Padding', variable: 'py-[var(--layout-space-02)]' },
      ],
      'layout (orders container)': [
        { label: 'Gap', variable: '--layout-space-05' },
      ],
      'layout (empty state)': [
        { label: 'Padding', variable: 'py-[var(--layout-space-09)]' },
      ],
      text: [
        { label: 'Text Style', variable: 'text-style-h2' },
        { label: 'Font Size', variable: '--layout-font-size-xl' },
        { label: 'Font Weight', variable: '700' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0.01em' },
      ],
      color: [
        { label: 'Heading Text', variable: '--theme-text-primary' },
        { label: 'Empty State Text', variable: '--theme-text-secondary' },
        { label: 'Skeleton Fill', variable: '--theme-fill-object-quaternary' },
      ],
    },
    options: [
      {
        type: 'number',
        label: 'Order Count',
        defaultValue: 3,
        min: 0,
        max: 25,
      },
    ],
  },

  'order-history-sheet': {
    component: (options) => (
      <OrderHistorySheet
        groupName="Order History"
        orders={sampleOrders.slice(0, options.ordersCount || 3)}
        onOrderClick={() => {}}
        style={{ width: '100%', maxWidth: 'var(--layout-width-max-lg)' }}
      />
    ),
    styles: {
      layout: [
        { label: 'Width', variable: 'w-full' },
        { label: 'Max Width', variable: '--layout-width-max-lg' },
        { label: 'Gap', variable: '--layout-space-07' },
      ],
      'layout (title container)': [
        { label: 'Y Padding', variable: '--layout-space-02' },
      ],
      'layout (orders container)': [
        { label: 'Gap', variable: '--layout-space-05' },
      ],
      text: [
        { label: 'Text Style', variable: 'text-style-h2' },
        { label: 'Font Size', variable: '--layout-font-size-xl' },
        { label: 'Font Weight', variable: '700' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0.01em' },
      ],
      color: [
        { label: 'Text Color', variable: '--theme-text-primary' },
      ],
    },
    options: [
      {
        type: 'number',
        label: 'Orders Count',
        defaultValue: 3,
        min: 1,
        max: 25,
      },
    ],
  },

  'reward-points-group': {
    component: (options, settings) => (
      <RewardPointsGroup
        groupName="100 Points"
        rewards={sampleRewards.slice(0, options.rewardsCount || 3).map(r => ({
          ...r,
          rewardImageUrl: `/showcase/placeholders/${settings.restaurant}/${settings.restaurant}-reward.png`,
        }))}
        onRewardClick={() => {}}
        style={{ width: '100%', maxWidth: 'var(--layout-width-max-lg)' }}
      />
    ),
    styles: {
      layout: [
        { label: 'Width', variable: 'w-full' },
        { label: 'Max Width', variable: '--layout-width-max-lg' },
        { label: 'Gap', variable: '--layout-space-07' },
      ],
      'layout (title container)': [
        { label: 'Y Padding', variable: '--layout-space-02' },
      ],
      'layout (tiles container)': [
        { label: 'Gap', variable: '--layout-space-05' },
      ],
      text: [
        { label: 'Text Style', variable: 'text-style-h2' },
        { label: 'Font Size', variable: '--layout-font-size-xl' },
        { label: 'Font Weight', variable: '700' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0.01em' },
      ],
      color: [
        { label: 'Text Color', variable: '--theme-text-primary' },
      ],
    },
    options: [
      {
        type: 'number',
        label: 'Rewards Count',
        defaultValue: 3,
        min: 1,
        max: 25,
      },
    ],
  },

  'reorder-products-list': {
    component: (options, settings) => (
      <ReorderProductsList
        products={sampleReorderProducts.slice(0, options.productsCount || 3).map(p => ({
          ...p,
          imageUrl: `/showcase/placeholders/${settings.restaurant}/${settings.restaurant}-product-button.png`,
        }))}
        onProductPress={() => {}}
        style={{ width: '100%' }}
      />
    ),
    styles: {
      layout: [
        { label: 'Height', variable: 'Hug' },
        { label: 'Width', variable: 'w-full' },
        { label: 'Max Width', variable: '--layout-width-max-lg' },
        { label: 'Grid', variable: '1fr / auto-rows: 1fr' },
        { label: 'Gap', variable: '--layout-space-03' },
      ],
    },
    options: [
      {
        type: 'number',
        label: 'Products Count',
        defaultValue: 3,
        min: 1,
        max: 25,
      },
    ],
  },

  'favorites-list': {
    component: (options, settings) => (
      <SectionFavoritesList
        heading="Favorites"
        products={sampleReorderProducts.slice(0, options.productsCount || 3).map(p => ({
          ...p,
          imageUrl: `/showcase/placeholders/${settings.restaurant}/${settings.restaurant}-product-button.png`,
        }))}
        onProductPress={() => {}}
        className="w-full"
      />
    ),
    styles: {
      layout: [
        { label: 'Width', variable: 'w-full' },
        { label: 'Max Width', variable: 'max-w-[var(--layout-width-max-lg)]' },
        { label: 'Gap', variable: 'gap-[var(--layout-space-04)]' },
        { label: 'Heading Padding', variable: 'py-[var(--layout-space-02)]' },
        { label: 'Skeleton Items Gap', variable: 'gap-[var(--layout-space-03)]' },
      ],
      text: [
        { label: 'Text Style', variable: 'text-style-h3' },
        { label: 'Font Size', variable: '--layout-font-size-lg' },
        { label: 'Font Weight', variable: '700' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0.01em' },
      ],
      color: [
        { label: 'Heading Text', variable: 'text-primary' },
        { label: 'Empty State Text', variable: 'text-secondary' },
        { label: 'Skeleton Fill', variable: 'fill-object-quaternary' },
      ],
    },
    options: [
      {
        type: 'number',
        label: 'Products Count',
        defaultValue: 3,
        min: 0,
        max: 25,
      },
    ],
  },

  'section-reorder': {
    component: (options, settings) => (
      <SectionReorder
        orderNumber="123456789"
        title="Pickup from Location Name"
        locationAddress="100 Main Street, Los Angeles"
        products={sampleReorderProducts.slice(0, options.productsCount || 3).map(p => ({
          ...p,
          imageUrl: `/showcase/placeholders/${settings.restaurant}/${settings.restaurant}-product-button.png`,
        }))}
        onProductPress={() => {}}
        onAddAllPress={() => {}}
        onDetailsPress={() => {}}
        style={{ width: '100%' }}
      />
    ),
    styles: {
      layout: [
        { label: 'Width', variable: 'w-full' },
        { label: 'Max Width', variable: 'max-w-[var(--layout-width-max-lg)]' },
        { label: 'Gap (root)', variable: 'gap-[var(--layout-space-04)]' },
        { label: 'Gap (heading)', variable: 'gap-[var(--layout-space-02)]' },
        { label: 'Gap (buttons)', variable: 'gap-[var(--layout-space-04)]' },
      ],
      'text (order heading)': [
        { label: 'Text Style', variable: 'text-style-h3-light' },
        { label: 'Font Size', variable: '--layout-font-size-lg' },
        { label: 'Font Weight', variable: '630' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0.01em' },
      ],
      'text (title)': [
        { label: 'Text Style', variable: 'text-style-h3' },
        { label: 'Font Size', variable: '--layout-font-size-lg' },
        { label: 'Font Weight', variable: '700' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0.01em' },
      ],
      'text (address)': [
        { label: 'Text Style', variable: 'text-style-body-medium' },
        { label: 'Font Size', variable: '--layout-font-size-sm' },
        { label: 'Font Weight', variable: '500' },
        { label: 'Line Height', variable: '1.25' },
        { label: 'Letter Spacing', variable: '0em' },
      ],
      color: [
        { label: 'Heading Text', variable: 'text-primary' },
        { label: 'Title Text', variable: 'text-primary' },
        { label: 'Address Text', variable: 'text-secondary' },
      ],
      'color (loading)': [
        { label: 'Skeleton Fill', variable: 'fill-object-quaternary' },
      ],
      'color (empty state)': [
        { label: 'Empty State Text', variable: 'text-secondary' },
      ],
    },
    options: [
      {
        type: 'number',
        label: 'Products Count',
        defaultValue: 3,
        min: 0,
        max: 25,
      },
    ],
  },

  'location-list': {
    component: (options) => {
      const badges = options.badges || []
      const locationsCount = options.locationsAmount || 3

      // Apply badges to locations based on pattern:
      // - "Selected Location" only on first location
      // - "Not Accepting Orders" on every other location starting from first: indices 0, 2, 4, 6...
      // - "Offer Not Accepted" on every third location starting from first: indices 0, 3, 6, 9...
      const locationsWithBadges = sampleLocations.slice(0, locationsCount).map((location, index) => {
        const locationBadges: { variant: 'Default' | 'Warning'; text: string }[] = []

        // Selected Location badge on first only
        if (badges.includes('selected') && index === 0) {
          locationBadges.push({ variant: 'Default', text: 'Selected Location' })
        }

        // Not Accepting Orders badge on every other starting from first (indices 0, 2, 4, 6...)
        if (badges.includes('notAccepting') && index % 2 === 0) {
          locationBadges.push({ variant: 'Warning', text: 'Not Accepting Orders' })
        }

        // Offer Not Accepted badge on every third starting from first (indices 0, 3, 6, 9...)
        if (badges.includes('offerNotAccepted') && index % 3 === 0) {
          locationBadges.push({ variant: 'Warning', text: 'Offer Not Accepted' })
        }

        return {
          ...location,
          badges: locationBadges.length > 0 ? locationBadges : undefined,
        }
      })

      return (
        <LocationList
          locations={locationsWithBadges}
          onLocationSelect={() => {}}
          style={{ width: '100%' }}
        />
      )
    },
    styles: {
      layout: [
        { label: 'Height', variable: 'Hug' },
        { label: 'Width', variable: 'w-full' },
        { label: 'Max Width', variable: '--layout-width-max-lg' },
        { label: 'Gap', variable: '--layout-space-04' },
      ],
    },
    options: [
      {
        type: 'buttons',
        label: 'Badges',
        choices: [
          { value: 'selected', label: 'Selected Location' },
          { value: 'notAccepting', label: 'Not Accepting Orders' },
          { value: 'offerNotAccepted', label: 'Offer Not Accepted' },
        ],
        defaultValues: [],
        columns: 1,
      },
      {
        type: 'number',
        label: 'Locations Amount',
        defaultValue: 3,
        min: 1,
        max: 25,
      },
    ],
  },

  'alert': {
    component: () => {
      return (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px',
          }}
        >
          <div
            className="fill-modal [box-shadow:var(--effect-shadow-modal)]"
            style={{
              position: 'relative',
              borderRadius: 'var(--layout-radius-xl)',
              width: '100%',
              maxWidth: 'var(--layout-width-max-alerts)',
              minWidth: 'var(--layout-width-min-alerts)',
              padding: 'var(--layout-space-08)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 'var(--layout-space-08)',
              overflow: 'clip',
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 'var(--layout-space-07)',
                padding: 'var(--layout-space-05) var(--layout-space-03)',
                width: '100%',
                textAlign: 'center',
              }}
            >
              <h2
                className="text-style-h2 text-primary"
                style={{
                  width: '100%',
                  textAlign: 'center',
                  margin: 0,
                }}
              >
                Alert Title
              </h2>
              <p
                className="text-style-body-medium text-secondary"
                style={{
                  width: '100%',
                  textAlign: 'center',
                  margin: 0,
                }}
              >
                This is the alert description text that explains what action the user needs to take.
              </p>
            </div>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--layout-space-05)',
                width: '100%',
              }}
            >
              <ButtonLarge
                variant="Secondary"
                onPress={() => {}}
                className="w-full"
              >
                Cancel
              </ButtonLarge>
              <ButtonLarge
                variant="Prominent"
                onPress={() => {}}
                className="w-full"
              >
                Confirm
              </ButtonLarge>
            </div>
          </div>
        </div>
      )
    },
    styles: {
      layout: [
        { label: 'Corner Radius', variable: '--layout-radius-xl' },
        { label: 'Padding', variable: 'p-[var(--layout-space-08)]' },
        { label: 'Content Gap', variable: 'gap-[var(--layout-space-08)]' },
        { label: 'Text Gap', variable: 'gap-[var(--layout-space-07)]' },
        { label: 'Text Padding', variable: 'px-[var(--layout-space-03)] py-[var(--layout-space-05)]' },
        { label: 'Button Gap', variable: 'gap-[var(--layout-space-05)]' },
        { label: 'Min Width', variable: '--layout-width-min-alerts' },
        { label: 'Max Width', variable: '--layout-width-max-alerts' },
      ],
      'text (title)': [
        { label: 'Text Style', variable: 'text-style-h2' },
        { label: 'Font Size', variable: '--layout-font-size-xl' },
        { label: 'Font Weight', variable: '700' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0.01em' },
      ],
      'text (description)': [
        { label: 'Text Style', variable: 'text-style-body-medium' },
        { label: 'Font Size', variable: '--layout-font-size-sm' },
        { label: 'Font Weight', variable: '500' },
        { label: 'Line Height', variable: '1.25' },
        { label: 'Letter Spacing', variable: '0em' },
      ],
      color: [
        { label: 'Background Fill', variable: 'fill-modal' },
        { label: 'Shadow', variable: '--effect-shadow-modal' },
        { label: 'Backdrop', variable: 'rgba(0, 0, 0, 0.5)' },
        { label: 'Title Color', variable: 'text-primary' },
        { label: 'Description Color', variable: 'text-secondary' },
      ],
    },
  },

  'alert-add-card': {
    component: () => {
      return (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px',
          }}
        >
          <div
            className="fill-modal [box-shadow:var(--effect-shadow-modal)]"
            style={{
              position: 'relative',
              borderRadius: 'var(--layout-radius-xl)',
              width: '100%',
              maxWidth: 'var(--layout-width-max-alerts)',
              minWidth: 'var(--layout-width-min-alerts)',
              padding: 'var(--layout-space-08)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 'var(--layout-space-08)',
              overflow: 'clip',
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: 'var(--layout-space-05) var(--layout-space-03)',
                width: '100%',
              }}
            >
              <p
                className="text-style-h2 text-primary"
                style={{
                  width: '100%',
                  textAlign: 'center',
                  margin: 0,
                }}
              >
                Add Card
              </p>
            </div>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                maxWidth: 'var(--layout-width-max-xs)',
                width: '100%',
              }}
            >
              <FieldGroupCardInformation
                value={{
                  nameOnCard: '',
                  cardNumber: '',
                  cvc: '',
                  expiry: '',
                  billingZip: '',
                }}
              />
            </div>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                justifyContent: 'center',
                overflow: 'clip',
              }}
            >
              <OptionRow
                optionType="checkbox"
                optionAlignment="hug"
                text="Save Card"
                selected={false}
                value="save-card"
              />
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'clip',
                width: '100%',
              }}
            >
              <ButtonLarge
                variant="Primary"
                onPress={() => {}}
                className="w-full"
              >
                Confirm
              </ButtonLarge>
            </div>
          </div>
        </div>
      )
    },
    styles: {
      layout: [
        { label: 'Corner Radius', variable: '--layout-radius-xl' },
        { label: 'Padding', variable: 'p-[var(--layout-space-08)]' },
        { label: 'Content Gap', variable: 'gap-[var(--layout-space-08)]' },
        { label: 'Title Padding', variable: 'px-[var(--layout-space-03)] py-[var(--layout-space-05)]' },
        { label: 'Card Form Max Width', variable: '--layout-width-max-xs' },
        { label: 'Min Width', variable: '--layout-width-min-alerts' },
        { label: 'Max Width', variable: '--layout-width-max-alerts' },
      ],
      'text (title)': [
        { label: 'Text Style', variable: 'text-style-h2' },
        { label: 'Font Size', variable: '--layout-font-size-xl' },
        { label: 'Font Weight', variable: '700' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0.01em' },
      ],
      color: [
        { label: 'Background Fill', variable: 'fill-modal' },
        { label: 'Shadow', variable: '--effect-shadow-modal' },
        { label: 'Backdrop', variable: 'rgba(0, 0, 0, 0.5)' },
        { label: 'Title Color', variable: 'text-primary' },
      ],
    },
  },

  'sheet': {
    component: (_options, settings) => {
      const isMobile = settings.viewportMode === 'mobile'

      return (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: isMobile ? 'flex-end' : 'center',
            justifyContent: 'center',
          }}
        >
          {!isMobile ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
              <SheetCloseButton onPress={() => {}}>CLOSE</SheetCloseButton>

              <div
                style={{
                  position: 'relative',
                  backgroundColor: 'var(--theme-fill-modal)',
                  borderRadius: 'var(--layout-radius-xl) var(--layout-radius-xl) var(--layout-radius-sheet-bottom) var(--layout-radius-sheet-bottom)',
                  minWidth: 'var(--layout-width-min-sheet)',
                  maxWidth: 'var(--layout-width-max-sheets)',
                  width: '100%',
                  maxHeight: '80%',
                  boxShadow: 'var(--effect-shadow-modal)',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                  paddingBottom: 'var(--layout-padding-sheet-bottom)',
                }}
              >
                <div style={{ textAlign: 'center', padding: '32px' }}>
                  <p style={{ marginBottom: '16px', color: '#1f2937' }}>Sheet content goes here.</p>
                  <p style={{ color: '#6b7280', fontSize: '14px' }}>
                    Desktop: Centered modal with close button
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div
              style={{
                position: 'relative',
                backgroundColor: 'var(--theme-fill-modal)',
                borderRadius: 'var(--layout-radius-xl) var(--layout-radius-xl) 0 0',
                width: '100%',
                maxHeight: '90%',
                boxShadow: 'var(--effect-shadow-modal)',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                paddingBottom: 'var(--layout-padding-sheet-bottom)',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  zIndex: 2,
                }}
              >
                <SheetGrabber />
              </div>

              <div style={{ textAlign: 'center', padding: '30px 15px 34px' }}>
                <p style={{ marginBottom: '16px', color: '#1f2937' }}>Sheet content goes here.</p>
                <p style={{ color: '#6b7280', fontSize: '14px' }}>
                  Mobile: Bottom sheet with grabber handle
                </p>
              </div>
            </div>
          )}
        </div>
      )
    },
    styles: {
      layout: [
        { label: 'Corner Radius (top)', variable: '--layout-radius-xl' },
        { label: 'Corner Radius (bottom, desktop)', variable: '--layout-radius-sheet-bottom' },
        { label: 'Bottom Padding', variable: 'pb-[var(--layout-padding-sheet-bottom)]' },
        { label: 'Min Width', variable: '--layout-width-min-sheet' },
        { label: 'Max Width', variable: '--layout-width-max-sheets' },
      ],
      color: [
        { label: 'Background Fill', variable: 'fill-modal' },
        { label: 'Backdrop', variable: 'bg-black/50' },
        { label: 'Drop Shadow', variable: '--effect-shadow-modal' },
      ],
    },
  },

  // ===== PHASE 1 COMPONENTS - INPUT =====
  'option-row': {
    component: (options, _settings, setOptionValue) => {
      const optionType = (options.type || 'radio') as 'radio' | 'checkbox'
      const optionAlignment = (options.width === 'hug' ? 'hug' : 'fill') as 'fill' | 'hug'
      const showLabel = options.label === 'show'
      return (
        <OptionRow
          text="Option Name"
          optionType={optionType}
          optionAlignment={optionAlignment}
          showLabel={showLabel}
          label="Label"
          selected={options.selected || false}
          onChange={() => setOptionValue?.('selected', !options.selected)}
        />
      )
    },
    styles: {
      layout: [
        { label: 'Container Gap', variable: '--layout-space-02' },
        { label: 'Option Row Gap', variable: '--layout-space-07' },
        { label: 'Option Row Padding', variable: 'px-[var(--layout-space-02)]' },
        { label: 'Text Option Gap', variable: '--layout-space-05' },
        { label: 'Text Option Padding', variable: 'py-[var(--layout-space-02)]' },
        { label: 'Control Size', variable: 'size-6 (24×24)' },
        { label: 'Width', variable: 'w-full / w-fit (prop dependent)' },
      ],
      text: [
        { label: 'Text Style', variable: 'text-style-h4' },
        { label: 'Font Size', variable: '--layout-font-size-md' },
        { label: 'Font Weight', variable: '630' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0.01em' },
      ],
      color: [
        { label: 'Text Color', variable: '--theme-text-primary' },
        { label: 'Detail Text Color', variable: '--theme-text-tertiary' },
      ],
    },
    options: [
      {
        type: 'toggle' as const,
        label: 'Type',
        choices: [
          { value: 'radio', label: 'Radio' },
          { value: 'checkbox', label: 'Checkbox' },
        ],
        defaultValue: 'radio',
      },
      {
        type: 'toggle' as const,
        label: 'Width',
        choices: [
          { value: 'fill', label: 'Fill' },
          { value: 'hug', label: 'Hug' },
        ],
        defaultValue: 'fill',
      },
      {
        type: 'toggle' as const,
        label: 'Label',
        choices: [
          { value: 'show', label: 'Show' },
          { value: 'hide', label: 'Hide' },
        ],
        defaultValue: 'hide',
      },
    ],
  },

  'field-group-card-information': {
    component: () => (
      <FieldGroupCardInformation
        value={{
          nameOnCard: '',
          cardNumber: '',
          cvc: '',
          expiry: '',
          billingZip: '',
        }}
      />
    ),
    styles: {
      layout: [
        { label: 'Gap (fields)', variable: '--layout-space-04' },
        { label: 'Gap (bottom row)', variable: '--layout-space-06' },
        { label: 'Max Width', variable: '--layout-width-max-xs' },
        { label: 'Width', variable: 'w-full' },
      ],
    },
  },

  'field-group-address-details': {
    component: (options, _settings, setOptionValue) => (
      <FieldGroupAddressDetails
        value={{
          unitNumber: options.unitNumber || '',
          entryCode: options.entryCode || '',
          buildingName: options.buildingName || '',
          deliveryPreference: options.deliveryPreference || 'door',
          deliveryNotes: options.deliveryNotes || '',
        }}
        onChange={(fieldName, fieldValue) => setOptionValue?.(fieldName, fieldValue)}
      />
    ),
    styles: {
      layout: [
        { label: 'Width', variable: 'w-full' },
        { label: 'Max Width', variable: '--layout-width-max-sm' },
        { label: 'Container Gap', variable: '--layout-space-04' },
        { label: 'Top Row Gap', variable: '--layout-space-06' },
      ],
    },
  },

  'field-group-account-information': {
    component: (options, _settings, setOptionValue) => (
      <FieldGroupAccountInformation
        value={{
          fullName: options.fullName || '',
          emailAddress: options.emailAddress || '',
          phoneNumber: options.phoneNumber || '',
          birthday: options.birthday || '',
        }}
        onChange={(fieldName, fieldValue) => setOptionValue?.(fieldName, fieldValue)}
      />
    ),
    styles: {
      layout: [
        { label: 'Outer Gap', variable: '--layout-space-09' },
        { label: 'Inner Field Gap', variable: '--layout-space-04' },
        { label: 'Bottom Row Gap', variable: '--layout-space-06' },
        { label: 'Max Width', variable: '--layout-width-max-sm' },
        { label: 'Width', variable: 'w-full' },
      ],
      'layout (calendar)': [
        { label: 'Padding', variable: 'p-3 (12px)' },
        { label: 'Width', variable: 'w-[248px] (248px)' },
        { label: 'Border Radius', variable: 'rounded-lg (8px)' },
        { label: 'Cell Size', variable: 'w-8 h-8 (32×32)' },
      ],
      'color (calendar)': [
        { label: 'Background', variable: '--theme-fill-object-elevated' },
        { label: 'Border', variable: '--theme-stroke-tertiary' },
        { label: 'Text', variable: '--theme-text-primary' },
        { label: 'Nav Arrows', variable: '--theme-text-secondary' },
        { label: 'Shadow', variable: '--effect-shadow-modal' },
      ],
      'color (calendar selected)': [
        { label: 'Fill', variable: '--theme-fill-object-primary' },
        { label: 'Text', variable: '--theme-text-object-primary' },
      ],
      'color (calendar hover)': [
        { label: 'Fill', variable: '--theme-fill-object-quaternary-hover' },
      ],
    },
  },

  'delivery-preference': {
    component: (options, _settings, setOptionValue) => (
      <DeliveryPreference
        value={options.deliveryPreference || 'door'}
        onChange={(value) => setOptionValue?.('deliveryPreference', value)}
      />
    ),
    styles: {
      layout: [
        { label: 'Outer Gap', variable: '--layout-space-03' },
        { label: 'Inner Gap', variable: '--layout-space-02' },
      ],
    },
  },

  // ===== PHASE 1 COMPONENTS - BUTTONS =====
  'custom-tip-option': {
    component: (options, _settings, setOptionValue) => (
      <CustomTipOption
        customTipState={options.customTipState || 'Default'}
        value={options.customTipValue || ''}
        onActivate={() => setOptionValue?.('customTipState', 'Active')}
        onChange={(value: string) => {
          setOptionValue?.('customTipValue', value)
          setOptionValue?.('customTipState', value.trim() ? 'Filled' : 'Active')
        }}
        onClear={() => {
          setOptionValue?.('customTipValue', '')
          setOptionValue?.('customTipState', 'Default')
        }}
        onSubmit={(value: string) => {
          if (value.trim()) {
            setOptionValue?.('customTipState', 'Filled')
          } else {
            setOptionValue?.('customTipState', 'Default')
          }
        }}
      />
    ),
    styles: {
      layout: [
        { label: 'Height', variable: '--layout-height-custom-tip-option' },
        { label: 'Width', variable: '--layout-width-max-xxs' },
        { label: 'TextField Width', variable: 'w-[110px] (110px)' },
      ],
      'color (default state)': [
        { label: 'Button Fill', variable: '--theme-fill-object-tertiary' },
        { label: 'Button Text', variable: '--theme-text-object-tertiary' },
      ],
      'color (filled state)': [
        { label: 'Clear Button Fill', variable: '--theme-fill-object-quaternary' },
        { label: 'Clear Button Icon', variable: '--theme-text-object-quaternary' },
      ],
    },
  },

  'tip-presets': {
    component: () => (
      <TipPresets
        subtotalCents={2499}
      />
    ),
    styles: {
      layout: [
        { label: 'Width', variable: 'w-full' },
        { label: 'Max Width', variable: '--layout-width-max-sm' },
        { label: 'Gap', variable: '--layout-space-05' },
      ],
    },
  },

  'notification-types': {
    component: () => (
      <NotificationTypes />
    ),
    styles: {
      layout: [
        { label: 'Width', variable: 'w-full' },
        { label: 'Max Width', variable: '--layout-width-max-sm' },
        { label: 'Gap', variable: '--layout-space-05' },
      ],
    },
  },

  // ===== PHASE 1 COMPONENTS - MISC =====
  'caret': {
    component: (options) => (
      <Caret direction={(options['Direction'] || 'right') as 'up' | 'down' | 'left' | 'right'} />
    ),
    styles: {
      layout: [
        { label: 'Size', variable: 'size-5 (20×20)' },
      ],
      color: [
        { label: 'Icon Color', variable: '--theme-icon' },
      ],
    },
    options: [
      {
        type: 'buttonGroup',
        label: 'Direction',
        choices: [
          { value: 'up', label: 'Up' },
          { value: 'down', label: 'Down' },
          { value: 'left', label: 'Left' },
          { value: 'right', label: 'Right' },
        ],
        defaultValue: 'right',
        columns: 2,
      },
    ],
  },

  'order-line-item': {
    component: () => (
      <OrderLineItem
        lineItem="Subtotal"
        charge="$24.99"
      />
    ),
    styles: {
      layout: [
        { label: 'Width', variable: 'w-full' },
        { label: 'Gap', variable: '--layout-space-02' },
        { label: 'Padding Horizontal', variable: '--layout-space-03' },
        { label: 'Padding Vertical', variable: '--layout-space-02' },
      ],
      'text (line item)': [
        { label: 'Text Style', variable: 'text-style-h4-light' },
        { label: 'Font Size', variable: '--layout-font-size-md' },
        { label: 'Font Weight', variable: '600' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0.01em' },
      ],
      'text (charge)': [
        { label: 'Text Style', variable: 'text-style-h4' },
        { label: 'Font Size', variable: '--layout-font-size-md' },
        { label: 'Font Weight', variable: '630' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0.01em' },
      ],
      color: [
        { label: 'Line Item Text', variable: '--theme-text-primary' },
        { label: 'Charge Text', variable: '--theme-text-secondary' },
      ],
    },
  },

  'cart-item': {
    component: (options) => (
      <CartItem
        productName="Sample Product"
        priceCurrent="$12.99"
        priceOld="$15.99"
        showPriceOld={options['Show Old Price'] !== 'hide'}
        showProductOptions={options['Show Options'] !== 'hide'}
        quantity={options.Quantity ?? 2}
      />
    ),
    styles: {
      layout: [
        { label: 'Width', variable: 'w-full' },
        { label: 'Gap', variable: '--layout-space-03' },
        { label: 'Padding Vertical', variable: '--layout-space-03' },
      ],
      'layout (text container)': [
        { label: 'Gap', variable: '--layout-space-02' },
      ],
      'layout (pricing)': [
        { label: 'Gap', variable: '--layout-space-03' },
      ],
      'text (product name)': [
        { label: 'Text Style', variable: 'text-style-h4' },
        { label: 'Font Size', variable: '--layout-font-size-md' },
        { label: 'Font Weight', variable: '630' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0.01em' },
      ],
      'text (options)': [
        { label: 'Text Style', variable: 'text-style-h5-light' },
        { label: 'Font Size', variable: '--layout-font-size-sm' },
        { label: 'Font Weight', variable: '500' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0.01em' },
      ],
      'text (price)': [
        { label: 'Text Style', variable: 'text-style-h5' },
        { label: 'Font Size', variable: '--layout-font-size-sm' },
        { label: 'Font Weight', variable: '630' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0.01em' },
      ],
      color: [
        { label: 'Product Name', variable: '--theme-text-primary' },
        { label: 'Options Text', variable: '--theme-text-secondary' },
        { label: 'Current Price', variable: '--theme-text-secondary' },
        { label: 'Old Price', variable: '--theme-text-tertiary' },
        { label: 'Edit Button Hover', variable: '--theme-fill-object-quaternary-hover' },
        { label: 'Edit Button Press', variable: '--theme-fill-object-quaternary-press' },
      ],
    },
    options: [
      {
        type: 'buttons',
        label: 'Show Old Price',
        choices: [
          { value: 'show', label: 'Show' },
          { value: 'hide', label: 'Hide' },
        ],
        defaultValues: ['show'],
      },
      {
        type: 'buttons',
        label: 'Show Options',
        choices: [
          { value: 'show', label: 'Show' },
          { value: 'hide', label: 'Hide' },
        ],
        defaultValues: ['show'],
      },
      {
        type: 'number',
        label: 'Quantity',
        defaultValue: 2,
        min: 0,
        max: 10,
      },
    ],
  },

  'previous-cart-item': {
    component: (options) => (
      <PreviousCartItem
        productName="Sample Product"
        priceCurrent="$12.99"
        priceOld="$15.99"
        showPriceOld={options['Show Old Price'] !== 'hide'}
        showProductOptions={options['Show Options'] !== 'hide'}
        quantity={options.Quantity ?? 2}
      />
    ),
    styles: {
      layout: [
        { label: 'Width', variable: 'w-full' },
        { label: 'Gap', variable: '--layout-space-03' },
        { label: 'Padding', variable: '--layout-space-03' },
      ],
      'layout (text container)': [
        { label: 'Gap', variable: '--layout-space-02' },
      ],
      'layout (pricing)': [
        { label: 'Gap', variable: '--layout-space-03' },
      ],
      'layout (quantity)': [
        { label: 'Width', variable: 'w-[40px] (40px)' },
      ],
      'text (product name)': [
        { label: 'Text Style', variable: 'text-style-h4' },
        { label: 'Font Size', variable: '--layout-font-size-md' },
        { label: 'Font Weight', variable: '630' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0.01em' },
      ],
      'text (options)': [
        { label: 'Text Style', variable: 'text-style-h5-light' },
        { label: 'Font Size', variable: '--layout-font-size-sm' },
        { label: 'Font Weight', variable: '500' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0.01em' },
      ],
      'text (price)': [
        { label: 'Text Style', variable: 'text-style-h5' },
        { label: 'Font Size', variable: '--layout-font-size-sm' },
        { label: 'Font Weight', variable: '630' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0.01em' },
      ],
      'text (quantity)': [
        { label: 'Text Style', variable: 'text-style-button-large-light' },
        { label: 'Font Size', variable: '--layout-font-size-lg' },
        { label: 'Font Weight', variable: '600' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0.01em' },
      ],
      color: [
        { label: 'Product Name', variable: '--theme-text-primary' },
        { label: 'Options Text', variable: '--theme-text-secondary' },
        { label: 'Current Price', variable: '--theme-text-secondary' },
        { label: 'Old Price', variable: '--theme-text-tertiary' },
        { label: 'Quantity', variable: '--theme-text-primary' },
      ],
    },
    options: [
      {
        type: 'buttons',
        label: 'Show Old Price',
        choices: [
          { value: 'show', label: 'Show' },
          { value: 'hide', label: 'Hide' },
        ],
        defaultValues: ['show'],
      },
      {
        type: 'buttons',
        label: 'Show Options',
        choices: [
          { value: 'show', label: 'Show' },
          { value: 'hide', label: 'Hide' },
        ],
        defaultValues: ['show'],
      },
      {
        type: 'number',
        label: 'Quantity',
        defaultValue: 2,
        min: 1,
        max: 10,
      },
    ],
  },

  'selected-order': {
    component: (options) => (
      <SelectedOrder
        orderNumber="123456789"
        orderCost="$45.99"
        orderType={options['Order Type'] === 'delivery' ? 'Delivery' : 'Pickup'}
        locationName="Downtown"
        orderDate="January 15, 2026 at 2:30 PM"
      />
    ),
    styles: {
      layout: [
        { label: 'Width', variable: 'w-full' },
        { label: 'Max Width', variable: '--layout-width-max-md' },
        { label: 'Gap', variable: '--layout-space-03' },
        { label: 'Padding Vertical', variable: '--layout-space-03' },
      ],
      'layout (text container)': [
        { label: 'Gap', variable: '--layout-space-02' },
      ],
      'text (order summary)': [
        { label: 'Text Style', variable: 'text-style-h4' },
        { label: 'Font Size', variable: '--layout-font-size-md' },
        { label: 'Font Weight', variable: '630' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0.01em' },
      ],
      'text (order details)': [
        { label: 'Text Style', variable: 'text-style-body-small' },
        { label: 'Font Size', variable: '--layout-font-size-xs' },
        { label: 'Font Weight', variable: '500' },
        { label: 'Line Height', variable: '1.25' },
        { label: 'Letter Spacing', variable: '0em' },
      ],
      'text (order date)': [
        { label: 'Text Style', variable: 'text-style-body-small' },
        { label: 'Font Size', variable: '--layout-font-size-xs' },
        { label: 'Font Weight', variable: '500' },
        { label: 'Line Height', variable: '1.25' },
        { label: 'Letter Spacing', variable: '0em' },
      ],
      color: [
        { label: 'Order Summary', variable: '--theme-text-primary' },
        { label: 'Order Details', variable: '--theme-text-secondary' },
        { label: 'Order Date', variable: '--theme-text-secondary' },
        { label: 'Return Icon', variable: '--theme-icon' },
      ],
    },
    options: [
      {
        type: 'toggle',
        label: 'Order Type',
        choices: [
          { value: 'pickup', label: 'Pickup' },
          { value: 'delivery', label: 'Delivery' },
        ],
        defaultValue: 'pickup',
      },
    ],
  },

  'points-number-only': {
    component: (options) => {
      const preset = options.pointsPreset || '0'
      const presets: Record<string, { points: number; pointsUntilNextLevel: number; showNextLevelMessage: boolean }> = {
        '0': { points: 0, pointsUntilNextLevel: 500, showNextLevelMessage: true },
        '750': { points: 750, pointsUntilNextLevel: 250, showNextLevelMessage: true },
        '1500': { points: 1500, pointsUntilNextLevel: 0, showNextLevelMessage: false },
        '999999': { points: 999999, pointsUntilNextLevel: 0, showNextLevelMessage: false },
      }
      const { points, pointsUntilNextLevel, showNextLevelMessage } = presets[preset] ?? { points: 0, pointsUntilNextLevel: 500, showNextLevelMessage: true }
      return (
        <PointsNumberOnly
          points={points}
          pointsUntilNextLevel={pointsUntilNextLevel}
          showNextLevelMessage={showNextLevelMessage}
          style={{ width: '100%', maxWidth: 'var(--layout-width-max-lg)' }}
        />
      )
    },
    styles: {
      layout: [
        { label: 'Height', variable: 'h-fit' },
        { label: 'Width', variable: 'w-full' },
        { label: 'Max Width', variable: '--layout-width-max-lg' },
        { label: 'Corner Radius', variable: '--layout-radius-lg' },
        { label: 'Padding Top', variable: '--layout-space-04' },
        { label: 'Padding Bottom', variable: '--layout-space-09' },
        { label: 'Padding Horizontal', variable: '--layout-space-09' },
        { label: 'Gap', variable: '--layout-space-06' },
      ],
      'text (number)': [
        { label: 'Text Style', variable: 'text-style-points-number-only' },
        { label: 'Font Size', variable: '--layout-font-size-points-number-only' },
        { label: 'Font Weight', variable: '700' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0em' },
      ],
      'text (label)': [
        { label: 'Text Style', variable: 'text-style-points-label' },
        { label: 'Font Size', variable: '--layout-font-size-points-label' },
        { label: 'Font Weight', variable: '700' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0em' },
      ],
      'text (subtitle)': [
        { label: 'Text Style', variable: 'text-style-h4' },
        { label: 'Font Size', variable: '--layout-font-size-md' },
        { label: 'Font Weight', variable: '630' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0.01em' },
      ],
      color: [
        { label: 'Background', variable: '--theme-fill-chrome' },
        { label: 'Text Color', variable: '--theme-text-object-chrome-secondary' },
      ],
    },
    options: [
      {
        type: 'buttonGroup',
        label: 'Points Preset',
        choices: [
          { value: '0', label: '0 Points' },
          { value: '750', label: '750 Points' },
          { value: '1500', label: '1500 Points' },
          { value: '999999', label: '999999 Points' },
        ],
        defaultValue: '0',
        columns: 2,
      },
    ],
  },

  'sheet-grabber': {
    component: () => (
      <SheetGrabber />
    ),
    styles: {
      layout: [
        { label: 'Container', variable: 'w-full h-[10px] (full-width × 10)' },
        { label: 'Grabber bar', variable: 'w-[50px] h-[4px] (50×4)' },
        { label: 'Top padding', variable: 'pt-[6px] (6px)' },
        { label: 'Corner radius', variable: 'rounded-[2px] (2px)' },
      ],
      color: [
        { label: 'Grabber fill', variable: '--theme-icon-sheet-grabber' },
        { label: 'Opacity', variable: '0.5' },
      ],
    },
  },

  'sheet-close-button': {
    component: () => (
      <SheetCloseButton onPress={() => {}}>
        CLOSE
      </SheetCloseButton>
    ),
    styles: {
      layout: [
        { label: 'Width', variable: 'w-full' },
        { label: 'Padding (container bottom)', variable: '--layout-space-05' },
      ],
    },
  },

  // ===== SECTIONS =====
  'section-add-to-order': {
    component: (options, _settings, setOptionValue) => (
      <SectionAddToOrder
        quantity={options.quantity ?? 1}
        onQuantityChange={(newValue) => setOptionValue?.('quantity', newValue)}
        price="$12.99"
        isFavorited={options.isFavorited ?? false}
        onFavoriteToggle={() => setOptionValue?.('isFavorited', !options.isFavorited)}
        onAddToOrder={() => {}}
      />
    ),
    styles: {
      layout: [
        { label: 'Height', variable: 'Hug contents' },
        { label: 'Width', variable: 'w-full' },
        { label: 'Max Width', variable: '--layout-width-max-lg' },
        { label: 'Gap', variable: '--layout-space-04' },
      ],
      'layout (product actions)': [
        { label: 'Height', variable: 'Hug contents' },
        { label: 'Width', variable: 'w-full' },
        { label: 'Spacer', variable: 'w-[30px] h-[30px] (30×30)' },
      ],
    },
  },
  'section-address-results': {
    component: (options) => (
      <SectionAddressResults
        addresses={[
          { id: '1', addressLine1: '123 Main Street', addressLine2: 'New York, NY 10001' },
          { id: '2', addressLine1: '456 Oak Avenue', addressLine2: 'Los Angeles, CA 90001' },
          { id: '3', addressLine1: '789 Pine Boulevard', addressLine2: 'Chicago, IL 60601' },
        ]}
        title={options.title === 'visible' ? 'Saved Addresses' : undefined}
        onAddressSelect={() => {}}
      />
    ),
    options: [
      {
        type: 'toggle',
        label: 'Title',
        choices: [
          { value: 'hidden', label: 'Hidden' },
          { value: 'visible', label: 'Visible' },
        ],
        defaultValue: 'hidden',
      },
    ],
    styles: {
      layout: [
        { label: 'Width', variable: 'w-full' },
        { label: 'Max Width', variable: '--layout-width-max-lg' },
        { label: 'Gap', variable: 'gap-[var(--layout-space-02)]' },
        { label: 'Title Padding', variable: 'py-[var(--layout-space-02)]' },
      ],
      'text (title)': [
        { label: 'Text Style', variable: 'text-style-h4' },
        { label: 'Font Size', variable: '--layout-font-size-md' },
        { label: 'Font Weight', variable: '630' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0.01em' },
      ],
      color: [
        { label: 'Title Text', variable: '--theme-text-secondary' },
      ],
    },
  },
  'section-app-issue-form': {
    component: (options, _settings, setOptionValue) => (
      <SectionAppIssueForm
        specialRequests={options.specialRequests ?? ''}
        onSpecialRequestsChange={(text) => setOptionValue?.('specialRequests', text)}
        onSubmit={() => {}}
      />
    ),
    styles: {
      layout: [
        { label: 'Width', variable: 'w-full' },
        { label: 'Max Width', variable: '--layout-width-max-md' },
        { label: 'Gap', variable: '--layout-space-06' },
      ],
    },
  },
  'section-checkout-driver-tip': {
    component: (options, _settings, setOptionValue) => (
      <SectionCheckoutDriverTip
        subtotalCents={2499}
        selectedPercentage={options.selectedPercentage}
        onTipSelect={(tip) => setOptionValue?.('selectedPercentage', tip?.percentage)}
      />
    ),
    styles: {
      layout: [
        { label: 'Width', variable: 'w-full' },
        { label: 'Max Width', variable: '--layout-width-max-sm' },
        { label: 'Gap', variable: '--layout-space-07' },
        { label: 'Heading Padding', variable: 'py: --layout-space-02' },
      ],
      text: [
        { label: 'Text Style', variable: 'text-style-h3' },
        { label: 'Font Size', variable: '--layout-font-size-lg' },
        { label: 'Font Weight', variable: '700' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0.01em' },
      ],
      color: [
        { label: 'Text Color', variable: '--theme-text-primary' },
      ],
    },
  },
  'section-checkout-payment-method': {
    component: (options, _settings, setOptionValue) => {
      const methods: { id: string; type: 'visa' | 'mastercard' | 'applepay' | 'googlepay' | 'paypal' | 'venmo'; text: string; selected: boolean; showDeleteButton: boolean }[] = []
      if (options.applePay !== 'hide') methods.push({ id: 'applepay', type: 'applepay', text: 'Apple Pay', selected: options.selectedPayment === 'applepay', showDeleteButton: false })
      if (options.googlePay !== 'hide') methods.push({ id: 'googlepay', type: 'googlepay', text: 'Google Pay', selected: options.selectedPayment === 'googlepay', showDeleteButton: false })
      if (options.paypal !== 'hide') methods.push({ id: 'paypal', type: 'paypal', text: 'PayPal', selected: options.selectedPayment === 'paypal', showDeleteButton: false })
      if (options.venmo !== 'hide') methods.push({ id: 'venmo', type: 'venmo', text: 'Venmo', selected: options.selectedPayment === 'venmo', showDeleteButton: false })
      methods.push({ id: 'visa-1', type: 'visa', text: 'Visa ending in 4242', selected: options.selectedPayment === 'visa-1', showDeleteButton: true })
      methods.push({ id: 'mc-1', type: 'mastercard', text: 'Mastercard ending in 8888', selected: options.selectedPayment === 'mc-1', showDeleteButton: true })
      return (
        <SectionCheckoutPaymentMethod
          paymentMethods={methods}
          selectedPaymentMethodId={options.selectedPayment}
          onPaymentMethodSelect={(pm) => setOptionValue?.('selectedPayment', pm.id)}
          onUpdateCardPress={() => {}}
        />
      )
    },
    options: [
      {
        type: 'toggle' as const,
        label: 'Apple Pay',
        choices: [
          { value: 'show', label: 'Show' },
          { value: 'hide', label: 'Hide' },
        ],
        defaultValue: 'show',
      },
      {
        type: 'toggle' as const,
        label: 'Google Pay',
        choices: [
          { value: 'show', label: 'Show' },
          { value: 'hide', label: 'Hide' },
        ],
        defaultValue: 'show',
      },
      {
        type: 'toggle' as const,
        label: 'PayPal',
        choices: [
          { value: 'show', label: 'Show' },
          { value: 'hide', label: 'Hide' },
        ],
        defaultValue: 'show',
      },
      {
        type: 'toggle' as const,
        label: 'Venmo',
        choices: [
          { value: 'show', label: 'Show' },
          { value: 'hide', label: 'Hide' },
        ],
        defaultValue: 'show',
      },
    ],
    styles: {
      layout: [
        { label: 'Width', variable: 'w-full' },
        { label: 'Max Width', variable: '--layout-width-max-sm' },
        { label: 'Gap (root)', variable: '--layout-space-07' },
        { label: 'Gap (methods)', variable: '--layout-space-03' },
        { label: 'Gap (choice row)', variable: '--layout-space-05' },
        { label: 'Padding (heading)', variable: '--layout-space-02' },
        { label: 'Padding (choice row)', variable: '--layout-space-02' },
      ],
      text: [
        { label: 'Text Style', variable: 'text-style-h3' },
        { label: 'Font Size', variable: '--layout-font-size-lg' },
        { label: 'Font Weight', variable: '700' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0.01em' },
      ],
      color: [
        { label: 'Heading Text', variable: '--theme-text-primary' },
      ],
    },
  },
  'section-feedback-form': {
    component: (options, _settings, setOptionValue) => (
      <SectionFeedbackForm
        specialRequests={options.specialRequests ?? ''}
        onSpecialRequestsChange={(text) => setOptionValue?.('specialRequests', text)}
        onSubmit={() => {}}
      />
    ),
    styles: {
      layout: [
        { label: 'Width', variable: 'w-full' },
        { label: 'Max Width', variable: '--layout-width-max-md' },
        { label: 'Gap', variable: '--layout-space-06' },
      ],
    },
  },
  'section-notification-type': {
    component: () => (
      <SectionNotificationType />
    ),
    styles: {
      layout: [
        { label: 'Width', variable: 'w-full' },
        { label: 'Max Width', variable: '--layout-width-max-md' },
        { label: 'Gap', variable: 'gap-[var(--layout-space-07)]' },
      ],
      'layout (heading)': [
        { label: 'Padding', variable: 'py-[var(--layout-space-02)]' },
      ],
      'text (heading)': [
        { label: 'Text Style', variable: 'text-style-h2' },
        { label: 'Font Size', variable: '--layout-font-size-xl' },
        { label: 'Font Weight', variable: '700' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0.01em' },
      ],
      'text (description)': [
        { label: 'Text Style', variable: 'text-style-body-medium' },
        { label: 'Font Size', variable: '--layout-font-size-sm' },
        { label: 'Font Weight', variable: '500' },
        { label: 'Line Height', variable: '1.25' },
        { label: 'Letter Spacing', variable: '0em' },
      ],
      color: [
        { label: 'Heading Text', variable: 'text-primary' },
        { label: 'Description Text', variable: 'text-primary' },
      ],
    },
  },
  'section-offer-details': {
    component: () => (
      <SectionOfferDetails
        offerTitle="Buy One Get One Free"
        offerDescription="Enjoy a complimentary item of equal or lesser value with any purchase."
        buttonText="Add to Order"
        finePrintText="Valid for dine-in and takeout orders only. Cannot be combined with other offers."
        onOfferPress={() => {}}
      />
    ),
    styles: {
      layout: [
        { label: 'Width', variable: 'w-full' },
        { label: 'Max Width', variable: '--layout-width-max-lg' },
        { label: 'Gap', variable: '--layout-space-07' },
        { label: 'Gap (heading)', variable: '--layout-space-05' },
        { label: 'Max Width (fine print)', variable: '--layout-width-max-md' },
        { label: 'Padding (fine print)', variable: 'p-[var(--layout-space-03)]' },
      ],
      'text (title)': [
        { label: 'Text Style', variable: 'text-style-h2' },
        { label: 'Font Size', variable: '--layout-font-size-xl' },
        { label: 'Font Weight', variable: '700' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0.01em' },
      ],
      'text (description)': [
        { label: 'Text Style', variable: 'text-style-h4' },
        { label: 'Font Size', variable: '--layout-font-size-md' },
        { label: 'Font Weight', variable: '630' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0.01em' },
      ],
      'text (fine print)': [
        { label: 'Text Style', variable: 'text-style-detail' },
        { label: 'Font Size', variable: '--layout-font-size-xxs' },
        { label: 'Font Weight', variable: '500' },
        { label: 'Line Height', variable: '1.25' },
        { label: 'Letter Spacing', variable: '0em' },
      ],
      color: [
        { label: 'Title Text', variable: '--theme-text-primary' },
        { label: 'Description Text', variable: '--theme-text-secondary' },
        { label: 'Fine Print Text', variable: '--theme-text-tertiary' },
      ],
    },
  },
  'section-option-group': {
    component: (options, _settings, setOptionValue) => (
      <SectionOptionGroup
        title="Size"
        required={true}
        maxSelections={1}
      >
        <OptionRow
          text="Small"
          optionDetail="$0.00"
          showOptionDetail
          selected={options.selectedOption === 'small'}
          onPress={() => { setOptionValue?.('selectedOption', 'small') }}
        />
        <OptionRow
          text="Medium"
          optionDetail="+$1.50"
          showOptionDetail
          selected={options.selectedOption === 'medium'}
          onPress={() => { setOptionValue?.('selectedOption', 'medium') }}
        />
        <OptionRow
          text="Large"
          optionDetail="+$2.50"
          showOptionDetail
          selected={options.selectedOption === 'large'}
          onPress={() => { setOptionValue?.('selectedOption', 'large') }}
        />
      </SectionOptionGroup>
    ),
    styles: {
      layout: [
        { label: 'Width', variable: 'w-full' },
        { label: 'Max Width', variable: '--layout-width-max-lg' },
        { label: 'Gap (outer)', variable: '--layout-space-05' },
        { label: 'Gap (header)', variable: '--layout-space-03' },
        { label: 'Gap (details)', variable: '--layout-space-03' },
        { label: 'Gap (options)', variable: '--layout-space-03' },
      ],
      'text (group title)': [
        { label: 'Text Style', variable: 'text-style-h3-light' },
        { label: 'Font Size', variable: '--layout-font-size-lg' },
        { label: 'Font Weight', variable: '630' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0.01em' },
      ],
      'text (details)': [
        { label: 'Text Style', variable: 'text-style-h5' },
        { label: 'Font Size', variable: '--layout-font-size-sm' },
        { label: 'Font Weight', variable: '630' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0.01em' },
      ],
      color: [
        { label: 'Title Text', variable: '--theme-text-primary' },
        { label: 'Details Text', variable: '--theme-text-secondary' },
      ],
    },
  },
  'section-order-issue-form': {
    component: (options, _settings, setOptionValue) => (
      <SectionOrderIssueForm
        specialRequests={options.specialRequests ?? ''}
        onSpecialRequestsChange={(text) => setOptionValue?.('specialRequests', text)}
        onSubmit={() => {}}
      />
    ),
    styles: {
      layout: [
        { label: 'Width', variable: 'w-full' },
        { label: 'Max Width', variable: '--layout-width-max-md' },
        { label: 'Gap', variable: '--layout-space-06' },
      ],
    },
  },
  'section-order-summary': {
    component: () => (
      <SectionOrderSummary
        title="Pickup from Downtown"
        locationAddress="123 Main Street, Los Angeles"
        lineItems={[
          { label: 'Subtotal', amount: '$24.99' },
          { label: 'Tax', amount: '$2.12' },
          { label: 'Tip', amount: '$3.75' },
        ]}
        total="$30.86"
        buttonText="Place Order"
        onPlaceOrder={() => {}}
      />
    ),
    styles: {
      layout: [
        { label: 'Width', variable: 'w-full' },
        { label: 'Max Width', variable: '--layout-width-max-md' },
        { label: 'Gap', variable: '--layout-space-03' },
      ],
      'layout (title container)': [
        { label: 'Gap', variable: '--layout-space-02' },
      ],
      'layout (line items)': [
        { label: 'Max Width', variable: '--layout-width-max-sm' },
        { label: 'Gap', variable: '--layout-space-03' },
      ],
      'layout (total container)': [
        { label: 'Gap', variable: '--layout-space-02' },
        { label: 'Padding', variable: 'py-[var(--layout-space-02)]' },
      ],
      'text (title)': [
        { label: 'Text Style', variable: 'text-style-h3' },
        { label: 'Font Size', variable: '--layout-font-size-lg' },
        { label: 'Font Weight', variable: '700' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0.01em' },
      ],
      'text (address)': [
        { label: 'Text Style', variable: 'text-style-body-medium' },
        { label: 'Font Size', variable: '--layout-font-size-sm' },
        { label: 'Font Weight', variable: '500' },
        { label: 'Line Height', variable: '1.25' },
        { label: 'Letter Spacing', variable: '0em' },
      ],
      'text (total label)': [
        { label: 'Text Style', variable: 'text-style-h3-light' },
        { label: 'Font Size', variable: '--layout-font-size-lg' },
        { label: 'Font Weight', variable: '630' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0.01em' },
      ],
      'text (total value)': [
        { label: 'Text Style', variable: 'text-style-h3' },
        { label: 'Font Size', variable: '--layout-font-size-lg' },
        { label: 'Font Weight', variable: '700' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0.01em' },
      ],
      color: [
        { label: 'Title Color', variable: '--theme-text-primary' },
        { label: 'Address Color', variable: '--theme-text-secondary' },
        { label: 'Total Color', variable: '--theme-text-primary' },
      ],
    },
  },
  'section-previous-order-summary': {
    component: (options) => {
      const itemCount = options['Items Count'] || 3
      const sampleItems = Array.from({ length: itemCount }, (_, i) => ({
        productName: ['Margherita Pizza', 'Caesar Salad', 'Fresh Lemonade', 'Chocolate Cake', 'BBQ Ribs'][i % 5] ?? '',
        priceCurrent: ['$18.99', '$12.50', '$4.99', '$6.99', '$24.99'][i % 5] ?? '',
        priceOld: ['$22.99', '$15.00', '', '', '$29.99'][i % 5],
        showPriceOld: [true, true, false, false, true][i % 5],
        productOptions: ['Large; Extra cheese; Thin crust', 'Grilled chicken; Extra parmesan', 'Large; Extra ice', '', 'Full rack; BBQ sauce; Coleslaw'][i % 5],
        showProductOptions: [true, true, true, false, true][i % 5],
        quantity: [2, 1, 3, 1, 1][i % 5] ?? 0,
      }))
      return (
        <SectionPreviousOrderSummary
          orderNumber="123456790"
          title="Pickup from Location Name"
          locationAddress="100 Main Street, Los Angeles"
          items={sampleItems}
          lineItems={[
            { label: 'Subtotal', amount: '$24.99' },
            { label: 'Driver Tip', amount: '$4.50' },
            { label: 'Taxes and Fees', amount: '$5.12' },
          ]}
          total="$34.61"
          onReorder={() => {}}
        />
      )
    },
    styles: {
      layout: [
        { label: 'Width', variable: 'w-full' },
        { label: 'Max Width', variable: '--layout-width-max-lg' },
        { label: 'Gap', variable: 'gap-[var(--layout-space-03)]' },
      ],
      'layout (heading)': [
        { label: 'Gap', variable: 'gap-[var(--layout-space-02)]' },
      ],
      'layout (previous items)': [
        { label: 'Gap', variable: 'gap-[var(--layout-space-02)]' },
      ],
      'layout (line items)': [
        { label: 'Max Width', variable: '--layout-width-max-sm' },
        { label: 'Gap', variable: 'gap-[var(--layout-space-03)]' },
      ],
      'layout (total row)': [
        { label: 'Gap', variable: 'gap-[var(--layout-space-02)]' },
        { label: 'Padding', variable: 'py-[var(--layout-space-02)]' },
      ],
      'text (order number / total label)': [
        { label: 'Text Style', variable: 'text-style-h3-light' },
        { label: 'Font Size', variable: '--layout-font-size-lg' },
        { label: 'Font Weight', variable: '630' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0.01em' },
      ],
      'text (title / total amount)': [
        { label: 'Text Style', variable: 'text-style-h3' },
        { label: 'Font Size', variable: '--layout-font-size-lg' },
        { label: 'Font Weight', variable: '700' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0.01em' },
      ],
      'text (address)': [
        { label: 'Text Style', variable: 'text-style-body-medium' },
        { label: 'Font Size', variable: '--layout-font-size-sm' },
        { label: 'Font Weight', variable: '500' },
        { label: 'Line Height', variable: '1.25' },
        { label: 'Letter Spacing', variable: '0em' },
      ],
      color: [
        { label: 'Heading / Total Text', variable: 'text-primary' },
        { label: 'Address Text', variable: 'text-secondary' },
      ],
    },
    options: [
      {
        type: 'number',
        label: 'Items Count',
        defaultValue: 3,
        min: 0,
        max: 25,
      },
    ],
  },
  'section-product-information': {
    component: (_options, settings) => (
      <SectionProductInformation
        productName="Delicious Burger"
        description="A juicy beef patty topped with fresh lettuce, tomato, pickles, and our signature sauce on a toasted brioche bun."
        currentPrice="$12.99"
        originalPrice="$15.99"
        imageUrl={`/showcase/placeholders/${settings.restaurant}/${settings.restaurant}-product-sheet.png`}
      />
    ),
    styles: {
      layout: [
        { label: 'Width', variable: 'w-full' },
        { label: 'Max Width', variable: '--layout-width-max-lg' },
        { label: 'Gap', variable: '--layout-space-07' },
      ],
      'layout (text container)': [
        { label: 'Gap', variable: '--layout-space-02' },
      ],
      'layout (pricing container)': [
        { label: 'Gap', variable: '--layout-space-03' },
        { label: 'Padding Top', variable: '--layout-space-02' },
      ],
      'layout (image)': [
        { label: 'Size', variable: 'size-[110px] (110×110)' },
      ],
      'text (product name)': [
        { label: 'Text Style', variable: 'text-style-h2-light' },
        { label: 'Font Size', variable: '--layout-font-size-xl' },
        { label: 'Font Weight', variable: '630' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0.01em' },
      ],
      'text (description)': [
        { label: 'Text Style', variable: 'text-style-body-small-light' },
        { label: 'Font Size', variable: '--layout-font-size-xs' },
        { label: 'Font Weight', variable: '400' },
        { label: 'Line Height', variable: '1.25' },
        { label: 'Letter Spacing', variable: '0em' },
      ],
      'text (original price)': [
        { label: 'Text Style', variable: 'text-style-h5 + line-through' },
        { label: 'Font Size', variable: '--layout-font-size-sm' },
        { label: 'Font Weight', variable: '630' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0.01em' },
      ],
      'text (current price)': [
        { label: 'Text Style', variable: 'text-style-h5' },
        { label: 'Font Size', variable: '--layout-font-size-sm' },
        { label: 'Font Weight', variable: '630' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0.01em' },
      ],
      color: [
        { label: 'Product Name', variable: '--theme-text-primary' },
        { label: 'Description', variable: '--theme-text-primary' },
        { label: 'Original Price', variable: '--theme-text-tertiary' },
        { label: 'Current Price', variable: '--theme-text-secondary' },
      ],
    },
  },
  'section-products-in-cart': {
    component: () => (
      <SectionProductsInCart
        items={[
          { id: '1', productName: 'Classic Burger', quantity: 2, priceCurrent: '$12.99' },
          { id: '2', productName: 'Chicken Sandwich', quantity: 1, priceCurrent: '$10.99' },
          { id: '3', productName: 'Large Fries', quantity: 1, priceCurrent: '$4.99' },
        ]}
        onItemEdit={() => {}}
        onItemIncrease={() => {}}
        onItemDecrease={() => {}}
      />
    ),
    styles: {
      layout: [
        { label: 'Width', variable: 'w-full' },
        { label: 'Max Width', variable: '--layout-width-max-products-in-cart' },
        { label: 'Gap', variable: '--layout-space-02' },
      ],
      'layout (empty state)': [
        { label: 'Padding', variable: '--layout-space-06' },
      ],
      'color (loading)': [
        { label: 'Background Fill', variable: 'fill-object-disabled' },
      ],
      'color (empty)': [
        { label: 'Text Color', variable: 'text-secondary' },
      ],
      'text (empty state)': [
        { label: 'Text Style', variable: 'text-style-h5-light' },
        { label: 'Font Size', variable: '--layout-font-size-sm' },
        { label: 'Font Weight', variable: '500' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0.01em' },
      ],
    },
  },
  'section-required-product': {
    component: (options) => {
      const position = options.position || 'middle'
      const showBackward = position === 'middle' || position === 'last'
      const showForward = position === 'middle' || position === 'first'
      const forwardDisabled = options.forwardDisabled === 'disabled'
      const descriptionMap: Record<string, string> = {
        'only': 'Required Item - 1 of 1',
        'first': 'Required Item - 1 of 3',
        'middle': 'Required Item - 2 of 3',
        'last': 'Required Item - 3 of 3',
      }
      return (
        <SectionRequiredProduct
          requiredItemDescription={descriptionMap[position] || 'Required Item - 2 of 3'}
          showBackwardButton={showBackward}
          showForwardButton={showForward}
          showChangeItemButton={true}
          forwardDisabled={showForward && forwardDisabled}
          onBackwardPress={() => {}}
          onForwardPress={() => {}}
          onChangeItemPress={() => {}}
        />
      )
    },
    styles: {
      layout: [
        { label: 'Width', variable: 'w-full' },
        { label: 'Max Width', variable: '--layout-width-max-lg' },
        { label: 'Gap', variable: '--layout-space-03' },
      ],
      'layout (center container)': [
        { label: 'Gap', variable: '--layout-space-04' },
      ],
    },
    options: [
      {
        type: 'buttonGroup',
        label: 'Position',
        choices: [
          { value: 'only', label: 'Only' },
          { value: 'first', label: 'First' },
          { value: 'middle', label: 'Middle' },
          { value: 'last', label: 'Last' },
        ],
        defaultValue: 'middle',
        columns: 2,
      },
      {
        type: 'toggle',
        label: 'Forward Disabled',
        choices: [
          { value: 'enabled', label: 'Enabled' },
          { value: 'disabled', label: 'Disabled' },
        ],
        defaultValue: 'enabled',
      },
    ],
  },
  'section-special-requests': {
    component: (options, _settings, setOptionValue) => (
      <SectionSpecialRequests
        value={options.specialRequests ?? ''}
        onChange={(text) => setOptionValue?.('specialRequests', text)}
      />
    ),
    styles: {
      layout: [
        { label: 'Height', variable: 'Hug contents' },
        { label: 'Width', variable: 'w-full' },
        { label: 'Max Width', variable: '--layout-width-max-lg' },
      ],
      'layout (disclaimer)': [
        { label: 'Padding', variable: '--layout-space-03' },
      ],
      'text (disclaimer)': [
        { label: 'Text Style', variable: 'text-style-detail (italic)' },
        { label: 'Font Size', variable: '--layout-font-size-xxs' },
        { label: 'Font Weight', variable: '500' },
        { label: 'Line Height', variable: '1.25' },
        { label: 'Letter Spacing', variable: '0em' },
      ],
      'color (disclaimer)': [
        { label: 'Text Color', variable: '--theme-text-tertiary' },
      ],
    },
  },
  'section-subtotal': {
    component: () => (
      <SectionSubtotal
        subtotal="$30.86"
        buttonText="Continue"
        onContinue={() => {}}
      />
    ),
    styles: {
      layout: [
        { label: 'Width', variable: 'w-full' },
        { label: 'Max Width', variable: '--layout-width-max-md' },
        { label: 'Gap', variable: '--layout-space-04' },
      ],
      'layout (text container)': [
        { label: 'Gap', variable: '--layout-space-02' },
        { label: 'Padding', variable: 'py-[var(--layout-space-02)]' },
      ],
      'text (label)': [
        { label: 'Text Style', variable: 'text-style-h3-light' },
        { label: 'Font Size', variable: '--layout-font-size-lg' },
        { label: 'Font Weight', variable: '630' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0.01em' },
      ],
      'text (value)': [
        { label: 'Text Style', variable: 'text-style-h3' },
        { label: 'Font Size', variable: '--layout-font-size-lg' },
        { label: 'Font Weight', variable: '700' },
        { label: 'Line Height', variable: '1.2' },
        { label: 'Letter Spacing', variable: '0.01em' },
      ],
      color: [
        { label: 'Text Color', variable: '--theme-text-primary' },
      ],
    },
  },
}

// Copyable mono text component for variable names
function CopyableMono({ text, color }: { text: string; color: string }) {
  const [copied, setCopied] = useState(false)

  const handleClick = async () => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  return (
    <button
      onClick={handleClick}
      className="font-mono text-xs hover:opacity-70 cursor-pointer transition-colors text-right"
      style={{
        color,
        wordBreak: 'break-all',
        hyphens: 'manual',
        lineHeight: '1.4'
      }}
      title="Click to copy"
    >
      {copied ? 'Copied!' : text.split('-').map((part, index, array) => (
        index === array.length - 1 ? part : <>{part}-<wbr /></>
      ))}
    </button>
  )
}

// Details panel tab definitions
interface DetailsPanelTab {
  id: string
  label: string
}

const detailsPanelTabs: DetailsPanelTab[] = [
  { id: 'information', label: 'Information' },
  { id: 'options', label: 'Options' },
  { id: 'nesting', label: 'Nesting' },
]

export default function ComponentsPreview({ settings, onSettingsChange, componentId, onNavigate, onForcedViewportModeChange, filteredComponents, onBack }: ComponentsPreviewProps) {
  const [detailsPanelVisible, setDetailsPanelVisible] = useState(true)
  const [activeDetailsPanelTab, setActiveDetailsPanelTab] = useState('information')
  const [hoveredStatusCategory, setHoveredStatusCategory] = useState<ReviewCategory | null>(null)
  const [optionValues, setOptionValues] = useState<Record<string, any>>({})
  const [backButtonHovered, setBackButtonHovered] = useState(false)
  const [backButtonPressed, setBackButtonPressed] = useState(false)
  const [prevButtonHovered, setPrevButtonHovered] = useState(false)
  const [prevButtonPressed, setPrevButtonPressed] = useState(false)
  const [nextButtonHovered, setNextButtonHovered] = useState(false)
  const [nextButtonPressed, setNextButtonPressed] = useState(false)
  const [nameCopied, setNameCopied] = useState(false)
  const [isRandomizing, setIsRandomizing] = useState(false)

  // Settings helper functions (without keyboard shortcuts - those stay in ShowcaseHeader)
  const updateSetting = <K extends keyof ShowcaseSettings>(
    key: K,
    value: ShowcaseSettings[K]
  ) => {
    onSettingsChange({
      ...settings,
      [key]: value
    })
  }

  const handleRandomize = () => {
    const themes: Theme[] = ['light', 'dark']
    const restaurants: Restaurant[] = ['woodfire', 'fastsushi', 'scoop', 'thaihouse']

    setIsRandomizing(true)
    setTimeout(() => setIsRandomizing(false), 200)

    onSettingsChange({
      ...settings,
      theme: themes[Math.floor(Math.random() * themes.length)] as Theme,
      restaurant: restaurants[Math.floor(Math.random() * restaurants.length)] as Restaurant,
    })
  }

  // Brand icons from public/showcase folder
  const brandButtons: { id: Restaurant; label: string; bgColor: string; icon: string; selectedIcon: string }[] = [
    { id: 'woodfire', label: 'Woodfire', bgColor: '#515C78', icon: '/showcase/Showcase-Icon-Woodfire.svg', selectedIcon: '/showcase/Showcase-Icon-Woodfire-Selected.svg' },
    { id: 'fastsushi', label: 'FastSushi', bgColor: '#EC4747', icon: '/showcase/Showcase-Icon-FastSushi.svg', selectedIcon: '/showcase/Showcase-Icon-FastSushi-Selected.svg' },
    { id: 'scoop', label: 'Scoop', bgColor: '#2B79D9', icon: '/showcase/Showcase-Icon-Scoop.svg', selectedIcon: '/showcase/Showcase-Icon-Scoop-Selected.svg' },
    { id: 'thaihouse', label: 'Thai House', bgColor: '#CB9A27', icon: '/showcase/Showcase-Icon-Thaihouse.svg', selectedIcon: '/showcase/Showcase-Icon-Thaihouse-Selected.svg' }
  ]

  // Determine which viewport mode to display (forced or user-selected)
  const displayedViewportMode = settings.viewportMode
  const forcedViewportMode = MOBILE_CHROME_COMPONENTS.includes(componentId) ? 'mobile'
    : DESKTOP_CHROME_COMPONENTS.includes(componentId) ? 'desktop'
    : null
  const isViewportLocked = !!forcedViewportMode

  // Dark theme colors for header controls
  const headerDarkBg = '#1F2937'        // gray-800
  const headerDarkBgSelected = '#111827' // gray-900
  const headerDarkBorder = '#374151'    // gray-700
  const headerDarkText = '#9CA3AF'      // gray-400
  const headerDarkTextSelected = '#FFFFFF'

  // Status data for review tracking
  const [statusData, setStatusData] = useState<ReviewStatusData>(() => loadStatusData())

  // Use filtered components for navigation if provided, otherwise fall back to all components
  // Freeze component order on mount - only recalculate when user leaves and comes back
  const [frozenComponents] = useState(() =>
    filteredComponents && filteredComponents.length > 0
      ? filteredComponents
      : getUnifiedOrderedComponents()
  )

  // Sync status to localStorage whenever it changes
  useEffect(() => {
    saveStatusData(statusData)
  }, [statusData])

  // Update forced viewport mode based on current component
  useEffect(() => {
    if (!onForcedViewportModeChange) return

    if (MOBILE_CHROME_COMPONENTS.includes(componentId)) {
      onForcedViewportModeChange('mobile')
    } else if (DESKTOP_CHROME_COMPONENTS.includes(componentId)) {
      onForcedViewportModeChange('desktop')
    } else {
      // Container or other component - allow user to control viewport
      onForcedViewportModeChange(null)
    }

    // Cleanup: clear forced mode when component unmounts
    return () => {
      onForcedViewportModeChange(null)
    }
  }, [componentId, onForcedViewportModeChange])

  // Handle status click - cycle through statuses
  const handleStatusClick = (category: ReviewCategory) => {
    setStatusData(prev => {
      const statusRecord = prev[componentId] as Record<string, string> | undefined
      const currentStatus = statusRecord?.[category] || 'default'
      const currentIndex = statusCycle.indexOf(currentStatus as ReviewStatus)
      const nextIndex = currentIndex === -1 ? 0 : (currentIndex + 1) % statusCycle.length
      const nextStatus = statusCycle[nextIndex]

      const existingComponentStatus = prev[componentId] || {
        webTokenCleanup: 'default' as ReviewStatus,
        nativeTokenCleanup: 'default' as ReviewStatus,
        accessibility: 'default' as ReviewStatus,
        animation: 'default' as ReviewStatus,
      }

      return {
        ...prev,
        [componentId]: {
          ...existingComponentStatus,
          [category]: nextStatus
        }
      }
    })
  }

  const getStatus = (category: ReviewCategory): ReviewStatus => {
    const statusRecord = statusData[componentId] as Record<string, string> | undefined
    return (statusRecord?.[category] as ReviewStatus) || 'default'
  }

  // Check if current component is fully reviewed (all categories complete) - updates instantly
  const statusRecord = statusData[componentId] as Record<string, string> | undefined
  const isCurrentComponentFullyReviewed = categories.every(
    category => statusRecord?.[category] === 'complete'
  )

  // Component dimension tracking
  const [componentDimensions, setComponentDimensions] = useState<{ width: number; height: number } | null>(null)
  const componentRef = useRef<HTMLDivElement>(null)

  // Set up ResizeObserver to track component dimensions
  const observerRef = useRef<ResizeObserver | null>(null)

  const measureComponent = useCallback(() => {
    if (componentRef.current) {
      // Find the actual component element (first child of the wrapper)
      const componentElement = componentRef.current.firstElementChild as HTMLElement
      if (componentElement) {
        const rect = componentElement.getBoundingClientRect()
        setComponentDimensions({
          width: Math.round(rect.width),
          height: Math.round(rect.height)
        })
      } else {
        setComponentDimensions({ width: 0, height: 0 })
      }
    }
  }, [])

  useEffect(() => {
    if (componentRef.current) {
      // Create observer
      observerRef.current = new ResizeObserver(() => {
        measureComponent()
      })

      // Observe the wrapper and its children
      observerRef.current.observe(componentRef.current)
      const componentElement = componentRef.current.firstElementChild
      if (componentElement) {
        observerRef.current.observe(componentElement)
      }

      // Initial measurement
      measureComponent()
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect()
      }
    }
  }, [componentId, measureComponent])

  // Re-measure when viewport mode changes
  useEffect(() => {
    measureComponent()
  }, [settings.viewportMode, measureComponent])

  // Use frozen component order to prevent navigation jumps when status changes
  const currentIndex = frozenComponents.findIndex(c => c.id === componentId)
  const currentComponent = frozenComponents[currentIndex]
  // Wrap around navigation - at start go to end, at end go to start
  const prevComponent = currentIndex > 0
    ? frozenComponents[currentIndex - 1]
    : frozenComponents[frozenComponents.length - 1]
  const nextComponent = currentIndex < frozenComponents.length - 1
    ? frozenComponents[currentIndex + 1]
    : frozenComponents[0]

  // Get current component configuration
  const config = componentConfigs[componentId]
  const hasOptions = config?.options && config.options.length > 0

  // Dark theme colors for bottom bar and details panel
  const darkBgLight = '#171717'   // neutral-900 (details panel background)
  const darkBorder = '#525252'    // neutral-500 (border for lighter sections)
  const darkText = '#a1a1a1'      // neutral-400
  const darkTextMono = '#D1D5DB'  // gray-300 (lighter, for mono text)
  const darkTextSelected = '#FFFFFF'

  // Reset option values when component changes
  useEffect(() => {
    if (config?.options) {
      const defaults: Record<string, any> = {}
      config.options.forEach((option) => {
        const key = option.label
          .split(/\s+/)
          .map((word, i) => i === 0 ? word.toLowerCase() : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join('')
        if (option.type === 'buttons') {
          defaults[key] = option.defaultValues
        } else {
          defaults[key] = option.defaultValue
        }
      })
      setOptionValues(defaults)
    } else {
      setOptionValues({})
    }
  }, [componentId])

  // Helper to generate stable keys for options - use camelCase version of label
  function getOptionKey(option: ComponentOption): string {
    // Convert label to camelCase key (e.g., "Field Label" -> "fieldLabel")
    return option.label
      .split(/\s+/)
      .map((word, i) => i === 0 ? word.toLowerCase() : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join('')
  }

  // Convert option values to component props format
  function getComponentOptions(): Record<string, unknown> {
    // Start with any raw option values (for direct setOptionValue calls like orderType)
    const result: Record<string, unknown> = { ...optionValues }

    if (!config?.options) return result

    config.options.forEach((option) => {
      const key = getOptionKey(option)
      if (option.type === 'buttons') {
        // Buttons use array of selected values
        result[key] = optionValues[key] ?? option.defaultValues
      } else {
        // Toggle and ButtonGroup use single value
        result[key] = optionValues[key] ?? option.defaultValue
      }
    })
    return result
  }

  // Helper to get component styles sections
  function getComponentStyles(): ComponentStyles {
    if (!config?.styles) return {}
    return config.styles
  }

  // Callback to allow components to update option values directly
  const setOptionValue = useCallback((key: string, value: unknown) => {
    setOptionValues(prev => ({ ...prev, [key]: value }))
  }, [])

  // Render component with options
  function renderComponent() {
    if (!config) return null
    const options = getComponentOptions()
    return config.component(options, settings, setOptionValue)
  }

  // Keyboard shortcut for toggling details panel and navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore if user is typing in an input field
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return
      }

      if (e.key === '\\') {
        e.preventDefault()
        setDetailsPanelVisible(prev => !prev)
      }

      // Escape key to go back to LandingComponentsSection
      if (e.key === 'Escape' && onBack) {
        e.preventDefault()
        onBack()
      }

      // Arrow key navigation (always works with wrap-around)
      if (e.key === 'ArrowLeft' && prevComponent) {
        e.preventDefault()
        onNavigate(prevComponent.id)
      }
      if (e.key === 'ArrowRight' && nextComponent) {
        e.preventDefault()
        onNavigate(nextComponent.id)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [prevComponent, nextComponent, onNavigate, onBack])

  return (
    <div className="flex flex-col h-full">
      {/* Top Navigation Bar */}
      <div
        className="flex items-center justify-between px-6 relative"
        style={{
          height: '60px',
          backgroundColor: '#000000',
          flexShrink: 0,
          borderBottom: '1px solid #262626'
        }}
      >
        {/* Left side - Back and Prev/Next buttons */}
        <div className="flex items-center gap-2">
          {/* Back Button */}
          {onBack && (
            <button
              onClick={onBack}
              onMouseEnter={() => setBackButtonHovered(true)}
              onMouseLeave={() => { setBackButtonHovered(false); setBackButtonPressed(false) }}
              onMouseDown={() => setBackButtonPressed(true)}
              onMouseUp={() => setBackButtonPressed(false)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '4px',
                height: '30px',
                backgroundColor: backButtonPressed ? '#404040' : backButtonHovered ? '#171717' : '#0a0a0a',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontFamily: "'Figtree', sans-serif",
                fontSize: '14px',
                fontWeight: 630,
                color: '#fafafa',
                padding: '0 8px',
              }}
              title="Back to components list (Escape)"
            >
              <ChevronLeft style={{ width: 14, height: 14 }} />
              Back
            </button>
          )}

          {/* Prev/Next Buttons - only show if more than one component */}
          {frozenComponents.length > 1 && (
            <div className="flex items-center gap-1">
              <button
                onClick={() => prevComponent && onNavigate(prevComponent.id)}
                onMouseEnter={() => setPrevButtonHovered(true)}
                onMouseLeave={() => { setPrevButtonHovered(false); setPrevButtonPressed(false) }}
                onMouseDown={() => setPrevButtonPressed(true)}
                onMouseUp={() => setPrevButtonPressed(false)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '30px',
                  height: '30px',
                  backgroundColor: prevButtonPressed ? '#404040' : prevButtonHovered ? '#171717' : '#0a0a0a',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  color: '#fafafa',
                  padding: 0,
                }}
                title={prevComponent ? `Previous: ${prevComponent.label}` : ''}
              >
                <StepBack style={{ width: 14, height: 14 }} />
              </button>
              <button
                onClick={() => nextComponent && onNavigate(nextComponent.id)}
                onMouseEnter={() => setNextButtonHovered(true)}
                onMouseLeave={() => { setNextButtonHovered(false); setNextButtonPressed(false) }}
                onMouseDown={() => setNextButtonPressed(true)}
                onMouseUp={() => setNextButtonPressed(false)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '30px',
                  height: '30px',
                  backgroundColor: nextButtonPressed ? '#404040' : nextButtonHovered ? '#171717' : '#0a0a0a',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  color: '#fafafa',
                  padding: 0,
                }}
                title={nextComponent ? `Next: ${nextComponent.label}` : ''}
              >
                <StepForward style={{ width: 14, height: 14 }} />
              </button>
            </div>
          )}
        </div>

        {/* Center - Component Name */}
        <div className="absolute left-1/2 transform -translate-x-1/2 flex items-center gap-2">
          <button
            onClick={async () => {
              if (currentComponent?.label) {
                try {
                  await navigator.clipboard.writeText(currentComponent.label)
                  setNameCopied(true)
                  setTimeout(() => setNameCopied(false), 1500)
                } catch (err) {
                  console.error('Failed to copy:', err)
                }
              }
            }}
            className="font-mono hover:opacity-70 cursor-pointer transition-opacity"
            style={{
              color: darkTextSelected,
              fontWeight: 'bold',
              fontSize: '16px',
              background: 'none',
              border: 'none',
              padding: 0
            }}
            title="Click to copy"
          >
            {nameCopied ? 'Copied!' : currentComponent?.label}
          </button>
          {isCurrentComponentFullyReviewed && !nameCopied && (
            <PackageCheck className="h-4 w-4 flex-shrink-0" style={{ color: '#00bc7d' }} />
          )}
        </div>

        {/* Right side - Showcase Controls */}
        <div className="flex items-center gap-2">
          {/* Randomize Button */}
          <button
            onClick={handleRandomize}
            className="h-7 w-7 p-0 rounded-md flex items-center justify-center transition-colors"
            style={{
              border: `1px solid ${headerDarkBorder}`,
              backgroundColor: isRandomizing ? headerDarkBgSelected : headerDarkBg,
              color: isRandomizing ? headerDarkTextSelected : headerDarkText
            }}
            title="Randomize theme"
          >
            <Dices
              className="h-3.5 w-3.5 transition-transform duration-300"
              style={{
                transform: isRandomizing ? 'rotate(180deg) scale(1.1)' : 'rotate(0deg) scale(1)'
              }}
            />
          </button>

          {/* Font Size Button */}
          <Popover>
            <PopoverTrigger asChild>
              <button
                className="h-7 rounded-md flex items-center justify-center transition-colors text-xs font-medium"
                style={{
                  border: `1px solid ${headerDarkBorder}`,
                  backgroundColor: headerDarkBg,
                  color: headerDarkText,
                  width: '50px'
                }}
                title="Font Scale"
              >
                {settings.fontScale}%
              </button>
            </PopoverTrigger>
            <PopoverContent
              className="w-80"
              style={{
                backgroundColor: headerDarkBgSelected,
                border: `1px solid ${headerDarkBorder}`
              }}
            >
              <div className="space-y-2">
                <div className="flex items-center justify-between h-[22px]">
                  <Label style={{ color: headerDarkTextSelected }}>Font Scale: {settings.fontScale}%</Label>
                  {settings.fontScale !== 100 && (
                    <button
                      onClick={() => updateSetting('fontScale', 100)}
                      className="p-1 rounded transition-colors hover:opacity-80"
                      style={{ color: headerDarkText }}
                      title="Reset to 100%"
                    >
                      <RotateCcw className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
                <Slider
                  value={settings.fontScale}
                  onValueChange={(value) => updateSetting('fontScale', value)}
                  min={50}
                  max={200}
                  step={10}
                  className="w-full"
                  trackColor={headerDarkBorder}
                  fillColor={headerDarkTextSelected}
                  thumbColor={headerDarkTextSelected}
                />
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* Main section: Component Preview + Details Panel */}
      <div className="flex flex-1 min-h-0">
        {/* Component Preview Section */}
        <div
          className="flex-1 relative"
          style={{
            backgroundColor: 'var(--theme-fill-page-background)',
            minWidth: detailsPanelVisible ? '60%' : '100%'
          }}
        >
          {/* Toggle Details Panel Button - fixed position relative to container */}
          <button
            onClick={() => setDetailsPanelVisible(!detailsPanelVisible)}
            className="absolute top-2 right-2 p-2 rounded-md transition-colors hover:opacity-70 z-10"
            style={{
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
              backdropFilter: 'blur(4px)',
              color: 'rgba(255, 255, 255, 0.8)'
            }}
            title={`${detailsPanelVisible ? 'Hide' : 'Show'} details panel (\\)`}
          >
            {detailsPanelVisible ? (
              <PanelRightClose className="h-5 w-5" />
            ) : (
              <PanelRightOpen className="h-5 w-5" />
            )}
          </button>

          {/* Dimension Display - fixed position relative to container */}
          {componentDimensions && (
            <div
              className="absolute bottom-2 right-2 px-2 py-1 rounded text-xs font-mono z-10"
              style={{
                backgroundColor: 'rgba(0, 0, 0, 0.3)',
                backdropFilter: 'blur(4px)',
                color: 'var(--theme-text-primary)'
              }}
            >
              {componentDimensions.width} × {componentDimensions.height}
            </div>
          )}

          {/* Scrollable Component Preview */}
          <div className="absolute inset-0 overflow-auto">
            <div className="flex items-center justify-center min-h-full min-w-full w-fit">
              <div
                ref={componentRef}
                className="flex items-center justify-center min-w-full w-fit"
                style={{
                  maxWidth: settings.viewportMode === 'mobile' ? '800px' : undefined
                }}
              >
                {config ? (
                  renderComponent()
                ) : (
                  <div
                    style={{
                      backgroundColor: 'var(--theme-fill-object-raised)',
                      border: '1px solid var(--theme-stroke-tertiary)',
                      borderRadius: 'var(--layout-radius-md)',
                      padding: 'var(--layout-space-07)',
                      color: 'var(--theme-fill-object-primary)',
                      fontSize: 'var(--layout-font-size-xl)'
                    }}
                  >
                    {currentComponent?.label || 'Component'}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Component Details Panel */}
        {detailsPanelVisible && (
          <div
            className="border-l flex-shrink-0 flex flex-col"
            style={{
              width: '40%',
              maxWidth: '400px',
              borderColor: darkBorder,
              backgroundColor: darkBgLight
            }}
          >
            {/* Tab Buttons */}
            <div
              className="flex items-center gap-1 px-3 py-2 border-b flex-shrink-0"
              style={{ borderColor: darkBorder }}
            >
              {detailsPanelTabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveDetailsPanelTab(tab.id)}
                  className="flex-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors"
                  style={{
                    backgroundColor: activeDetailsPanelTab === tab.id ? '#525252' : 'transparent',
                    color: activeDetailsPanelTab === tab.id ? darkTextSelected : darkText
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
              {/* Information Tab */}
              {activeDetailsPanelTab === 'information' && (
                <div className="flex-1 min-h-0 p-4 overflow-y-auto">
                  <div className="space-y-4">
                    {/* Review Status Icons Section */}
                    <div
                      className="flex flex-col items-center pb-4"
                      style={{ borderBottom: `1px solid ${darkBorder}` }}
                    >
                      <div style={{ color: darkText }} className="text-xs font-medium uppercase tracking-wide mb-2">
                        {hoveredStatusCategory ? categoryTooltips[hoveredStatusCategory] : 'Review Status'}
                      </div>
                      <div className="flex items-center justify-center">
                        {categories.map((category) => {
                          const IconComponent = categoryIconComponents[category]
                          const status = getStatus(category)
                          const color = statusColors[status]
                          return (
                            <button
                              key={category}
                              onClick={() => handleStatusClick(category)}
                              onMouseEnter={() => setHoveredStatusCategory(category)}
                              onMouseLeave={() => setHoveredStatusCategory(null)}
                              className="w-8 h-8 flex items-center justify-center rounded-md hover:opacity-80 transition-opacity cursor-pointer"
                            >
                              <IconComponent className="h-4 w-4" style={{ color }} />
                            </button>
                          )
                        })}
                      </div>
                      <textarea
                        value={statusData[componentId]?.comments || ''}
                        onChange={(e) => {
                          const value = e.target.value
                          setStatusData(prev => ({
                            ...prev,
                            [componentId]: {
                              ...prev[componentId] || {
                                webTokenCleanup: 'default' as ReviewStatus,
                                nativeTokenCleanup: 'default' as ReviewStatus,
                                accessibility: 'default' as ReviewStatus,
                                animation: 'default' as ReviewStatus,
                              },
                              comments: value,
                            }
                          }))
                        }}
                        placeholder="Add notes..."
                        rows={3}
                        className="w-full mt-3 text-xs rounded-md resize-y"
                        style={{
                          backgroundColor: 'rgba(255, 255, 255, 0.06)',
                          border: `1px solid ${darkBorder}`,
                          color: darkTextSelected,
                          padding: '8px',
                          outline: 'none',
                          fontFamily: "'Menlo', monospace",
                          lineHeight: '1.4',
                        }}
                      />
                    </div>
                    {/* Dynamic Style Sections */}
                      {Object.entries(getComponentStyles()).map(([sectionName, rows]) => (
                        rows && rows.length > 0 && (
                          <div key={sectionName}>
                            <div style={{ color: darkText }} className="text-xs font-medium uppercase tracking-wide mb-2 px-3">
                              {sectionName}
                            </div>
                            <table className="w-full text-sm">
                              <tbody>
                                {rows.map((row, index, arr) => (
                                  <tr
                                    key={`${sectionName}-${index}`}
                                    style={{
                                      borderBottom: index < arr.length - 1 ? `1px solid ${darkBorder}` : 'none'
                                    }}
                                  >
                                    <td
                                      className="py-2 px-3 text-xs"
                                      style={{ color: darkTextSelected }}
                                    >
                                      {row.label}
                                    </td>
                                    <td className="py-2 px-3 text-right">
                                      {row.variable ? (
                                        <CopyableMono text={row.variable} color={darkTextMono} />
                                      ) : (
                                        <span className="text-xs italic" style={{ color: darkText }}>—</span>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )
                      ))}
                  </div>
                </div>
              )}

              {/* Options Tab */}
              {activeDetailsPanelTab === 'options' && (
                <div className="flex-1 min-h-0 p-4 overflow-y-auto">
                  <div className="space-y-4">
                    {/* Global Options - Brand */}
                    <div>
                      <div
                        className="grid gap-1"
                        style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}
                      >
                        {brandButtons.map((brand) => {
                          const isSelected = settings.restaurant === brand.id
                          return (
                            <button
                              key={brand.id}
                              onClick={() => updateSetting('restaurant', brand.id)}
                              className="py-1.5 px-2 rounded text-sm transition-colors flex items-center justify-center gap-1.5"
                              style={{
                                backgroundColor: brand.bgColor,
                                border: isSelected ? '1px solid rgba(255, 255, 255, 0.3)' : '1px solid transparent',
                                color: '#ffffff',
                                fontWeight: isSelected ? 630 : 500,
                                opacity: isSelected ? 1 : 0.5,
                              }}
                            >
                              <img
                                src={brand.selectedIcon}
                                alt=""
                                className="h-6 w-6 pointer-events-none select-none"
                                draggable={false}
                              />
                              {brand.label}
                            </button>
                          )
                        })}
                      </div>
                    </div>

                    {/* Global Options - Theme (Light/Dark) Toggle */}
                    <div>
                      <SegmentedToggle
                        choices={[
                          { value: 'light' as Theme, label: <><Sun className="h-3.5 w-3.5" />Light</> },
                          { value: 'dark' as Theme, label: <><Moon className="h-3.5 w-3.5" />Dark</> },
                        ]}
                        value={settings.theme}
                        onChange={(value) => updateSetting('theme', value)}
                      />
                    </div>

                    {/* Global Options - Viewport Toggle or locked message */}
                    <div>
                      {isViewportLocked ? (
                        <div
                          className="rounded-md"
                          style={{
                            backgroundColor: '#262626',
                            padding: '2px',
                          }}
                        >
                          <div
                            className="rounded py-2 px-3 text-sm font-medium text-center"
                            style={{
                              backgroundColor: '#404040',
                              color: '#ffffff',
                            }}
                          >
                            {forcedViewportMode === 'mobile' ? (
                              <span className="flex items-center justify-center gap-1.5">
                                <Smartphone className="h-3.5 w-3.5" />
                                Mobile only component
                              </span>
                            ) : (
                              <span className="flex items-center justify-center gap-1.5">
                                <Monitor className="h-3.5 w-3.5" />
                                Desktop only component
                              </span>
                            )}
                          </div>
                        </div>
                      ) : (
                        <SegmentedToggle
                          choices={[
                            { value: 'auto' as const, label: <><MonitorSmartphone className="h-3.5 w-3.5" />Auto</> },
                            { value: 'desktop' as const, label: <><Monitor className="h-3.5 w-3.5" />Desktop</> },
                            { value: 'mobile' as const, label: <><Smartphone className="h-3.5 w-3.5" />Mobile</> },
                          ]}
                          value={displayedViewportMode}
                          onChange={(value) => updateSetting('viewportMode', value)}
                        />
                      )}
                    </div>

                    {/* Global Options - Icon Style. Writes the one showcase
                        setting that drives the core icon style store, so every
                        <Icon> in the preview re-cuts at once. */}
                    <div className="space-y-2">
                      <div style={{ color: darkText }} className="text-xs font-medium uppercase tracking-wide text-center">
                        Icon Style
                      </div>
                      <div
                        className="grid gap-1"
                        style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}
                      >
                        {ICON_STYLE_IDS.map((styleId: IconStyleId) => {
                          const isSelected = settings.iconStyle === styleId
                          return (
                            <button
                              key={styleId}
                              onClick={() => updateSetting('iconStyle', styleId)}
                              className="py-2 px-3 rounded text-sm transition-colors"
                              style={{
                                backgroundColor: isSelected ? '#404040' : '#262626',
                                border: isSelected ? '1px solid #737373' : '1px solid transparent',
                                color: isSelected ? '#ffffff' : '#a1a1a1',
                                fontWeight: isSelected ? 630 : 500,
                              }}
                            >
                              {ICON_STYLE_LABELS[styleId]}
                            </button>
                          )
                        })}
                      </div>
                    </div>

                    {/* Divider between global and component options */}
                    {hasOptions && (
                      <div style={{ borderTop: `1px solid ${darkBorder}` }} className="pt-4" />
                    )}

                    {/* Component-specific options */}
                    {hasOptions && config.options!.map((option) => {
                        const key = getOptionKey(option)
                        const currentValue = optionValues[key]

                        if (option.type === 'toggle') {
                          // Toggle: segmented control with sliding indicator
                          return (
                            <div key={key} className="space-y-2">
                              <div style={{ color: darkText }} className="text-xs font-medium uppercase tracking-wide text-center">
                                {option.label}
                              </div>
                              <SegmentedToggle
                                choices={option.choices.map(c => ({ value: c.value, label: c.label }))}
                                value={currentValue ?? option.defaultValue}
                                onChange={(value) => setOptionValues(prev => ({ ...prev, [key]: value }))}
                              />
                            </div>
                          )
                        } else if (option.type === 'buttonGroup') {
                          // ButtonGroup: single-select buttons in grid (radio-like)
                          const columns = option.columns || 3
                          return (
                            <div key={key} className="space-y-2">
                              <div style={{ color: darkText }} className="text-xs font-medium uppercase tracking-wide text-center">
                                {option.label}
                              </div>
                              <div
                                className="grid gap-1"
                                style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}
                              >
                                {option.choices.map((choice) => {
                                  const isSelected = (currentValue ?? option.defaultValue) === choice.value
                                  return (
                                    <button
                                      key={choice.value}
                                      onClick={() => setOptionValues(prev => ({ ...prev, [key]: choice.value }))}
                                      className="py-2 px-3 rounded text-sm transition-colors"
                                      style={{
                                        backgroundColor: isSelected ? '#404040' : '#262626',
                                        border: isSelected ? '1px solid #737373' : '1px solid transparent',
                                        color: isSelected ? '#ffffff' : '#a1a1a1',
                                        fontWeight: isSelected ? 630 : 500,
                                      }}
                                    >
                                      {choice.label}
                                    </button>
                                  )
                                })}
                              </div>
                            </div>
                          )
                        } else if (option.type === 'number') {
                          // Number: numeric up/down control with editable input
                          const numValue = currentValue ?? option.defaultValue
                          const min = option.min ?? 1
                          const max = option.max ?? 25
                          const isAtMin = numValue <= min
                          const isAtMax = numValue >= max
                          return (
                            <div key={key} className="space-y-2">
                              <div style={{ color: darkText }} className="text-xs font-medium uppercase tracking-wide text-center">
                                {option.label}
                              </div>
                              <div className="flex justify-center">
                                {/* Compact container matching 1/3 button width */}
                                <div
                                  className="inline-flex items-center rounded"
                                  style={{
                                    backgroundColor: '#262626',
                                    padding: '2px',
                                    width: 'calc(33.333% - 2.67px)', // Match 1/3 button width accounting for gap
                                    minWidth: '90px',
                                  }}
                                >
                                  {/* Decrease button */}
                                  <button
                                    onClick={() => {
                                      if (!isAtMin) {
                                        setOptionValues(prev => ({ ...prev, [key]: numValue - 1 }))
                                      }
                                    }}
                                    disabled={isAtMin}
                                    className="flex items-center justify-center rounded transition-colors flex-shrink-0"
                                    style={{
                                      width: '32px',
                                      height: '32px',
                                      backgroundColor: isAtMin ? 'transparent' : '#404040',
                                      color: isAtMin ? '#525252' : '#ffffff',
                                      cursor: isAtMin ? 'not-allowed' : 'pointer',
                                    }}
                                  >
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <path d="M3 8H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                    </svg>
                                  </button>

                                  {/* Editable number input - fills remaining space with no padding */}
                                  <input
                                    type="text"
                                    inputMode="numeric"
                                    value={numValue}
                                    onChange={(e) => {
                                      // Allow typing any digits, validation happens on Enter/blur
                                      const val = e.target.value
                                      if (val === '' || /^\d+$/.test(val)) {
                                        setOptionValues(prev => ({ ...prev, [key]: val === '' ? '' : parseInt(val, 10) }))
                                      }
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        e.currentTarget.blur()
                                      }
                                    }}
                                    onBlur={(e) => {
                                      // Clamp to min/max on blur
                                      const val = parseInt(e.target.value, 10)
                                      if (isNaN(val) || val < min) {
                                        setOptionValues(prev => ({ ...prev, [key]: min }))
                                      } else if (val > max) {
                                        setOptionValues(prev => ({ ...prev, [key]: max }))
                                      } else {
                                        setOptionValues(prev => ({ ...prev, [key]: val }))
                                      }
                                    }}
                                    className="text-center font-semibold outline-none flex-1 min-w-0"
                                    style={{
                                      height: '32px',
                                      backgroundColor: 'transparent',
                                      color: '#ffffff',
                                      fontSize: '14px',
                                      border: 'none',
                                    }}
                                  />

                                  {/* Increase button */}
                                  <button
                                    onClick={() => {
                                      if (!isAtMax) {
                                        setOptionValues(prev => ({ ...prev, [key]: numValue + 1 }))
                                      }
                                    }}
                                    disabled={isAtMax}
                                    className="flex items-center justify-center rounded transition-colors flex-shrink-0"
                                    style={{
                                      width: '32px',
                                      height: '32px',
                                      backgroundColor: isAtMax ? 'transparent' : '#404040',
                                      color: isAtMax ? '#525252' : '#ffffff',
                                      cursor: isAtMax ? 'not-allowed' : 'pointer',
                                    }}
                                  >
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <path d="M8 3V13M3 8H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                    </svg>
                                  </button>
                                </div>
                              </div>
                            </div>
                          )
                        } else {
                          // Buttons: multi-select buttons in grid (checkbox-like)
                          const selectedValues: string[] = currentValue ?? option.defaultValues
                          const columns = option.columns || 3
                          return (
                            <div key={key} className="space-y-2">
                              <div style={{ color: darkText }} className="text-xs font-medium uppercase tracking-wide text-center">
                                {option.label}
                              </div>
                              <div
                                className="grid gap-1"
                                style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}
                              >
                                {option.choices.map((choice) => {
                                  const isSelected = selectedValues.includes(choice.value)
                                  return (
                                    <button
                                      key={choice.value}
                                      onClick={() => {
                                        setOptionValues(prev => {
                                          const current: string[] = prev[key] ?? option.defaultValues
                                          if (current.includes(choice.value)) {
                                            return { ...prev, [key]: current.filter(v => v !== choice.value) }
                                          } else {
                                            return { ...prev, [key]: [...current, choice.value] }
                                          }
                                        })
                                      }}
                                      className="py-2 px-3 rounded text-sm transition-colors"
                                      style={{
                                        backgroundColor: isSelected ? '#404040' : '#262626',
                                        border: isSelected ? '1px solid #737373' : '1px solid transparent',
                                        color: isSelected ? '#ffffff' : '#a1a1a1',
                                        fontWeight: isSelected ? 630 : 500,
                                      }}
                                    >
                                      {choice.label}
                                    </button>
                                  )
                                })}
                              </div>
                            </div>
                          )
                        }
                      })}
                  </div>
                </div>
              )}

              {/* Nesting Tab */}
              {activeDetailsPanelTab === 'nesting' && (
                <div className="flex-1 min-h-0 p-4 overflow-y-auto">
                  <div className="space-y-4">
                    {(() => {
                      const nestingData = getComponentNestingData(componentId)
                      const hasUsedIn = nestingData.usedIn && Object.keys(nestingData.usedIn).length > 0
                      const hasContains = nestingData.contains && nestingData.contains.length > 0
                      const hasAnyData = hasUsedIn || hasContains

                      if (!hasAnyData) {
                        return (
                          <div
                            className="flex items-center justify-center py-8"
                            style={{ color: darkText }}
                          >
                            <span className="text-sm italic">No nesting data</span>
                          </div>
                        )
                      }

                      return (
                        <>
                          {/* "Used In" categories */}
                          {hasUsedIn && nonNestedCategories.map((category) => {
                            const links = nestingData.usedIn?.[category as Exclude<NestingCategory, 'Nested'>]
                            if (!links || links.length === 0) return null

                            const IconComponent = nestingCategoryIcons[category]
                            return (
                              <div key={category}>
                                <div
                                  style={{ color: darkText }}
                                  className="text-xs font-medium uppercase tracking-wide mb-2 px-1 flex items-center gap-2"
                                >
                                  <IconComponent style={{ width: 14, height: 14 }} />
                                  {category}
                                </div>
                                <div className="space-y-1">
                                  {links.map((link: NestingLink, index: number) => (
                                    <button
                                      key={`${link.componentId}-${index}`}
                                      onClick={() => onNavigate(link.componentId)}
                                      className="w-full text-left px-3 py-2 rounded-md transition-colors hover:bg-white/10"
                                      style={{
                                        color: darkTextSelected,
                                        fontSize: '14px',
                                        fontWeight: 500,
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                      }}
                                    >
                                      {link.label}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )
                          })}

                          {/* Divider before Nested section */}
                          {hasUsedIn && hasContains && (
                            <div style={{ borderTop: `1px solid ${darkBorder}` }} className="my-4" />
                          )}

                          {/* "Nested" category (contains) */}
                          {hasContains && (
                            <div>
                              <div
                                style={{ color: darkText }}
                                className="text-xs font-medium uppercase tracking-wide mb-2 px-1 flex items-center gap-2"
                              >
                                <ListTree style={{ width: 14, height: 14 }} />
                                Nested
                              </div>
                              <div className="space-y-1">
                                {nestingData.contains!.map((link, index) => (
                                  <button
                                    key={`${link.componentId}-${index}`}
                                    onClick={() => onNavigate(link.componentId)}
                                    className="w-full text-left px-3 py-2 rounded-md transition-colors hover:bg-white/10"
                                    style={{
                                      color: darkTextSelected,
                                      fontSize: '14px',
                                      fontWeight: 500,
                                      background: 'none',
                                      border: 'none',
                                      cursor: 'pointer',
                                    }}
                                  >
                                    {link.label}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </>
                      )
                    })()}
                  </div>
                </div>
              )}
            </div>

          </div>
        )}
      </div>
    </div>
  )
}
